import {
  assertClientFileUploadPolicy,
  canonicalJson,
  calculationSnapshotHashPayload,
  FINTRACKS_FILES_FOLDER_ID,
  type ClientFinancialEntry,
  type GuaranteeExposure,
  clientFinancialEntrySchema,
  clientPipelineSchema,
  MAX_PIPELINE_HISTORY,
  clientPipelineStageSchema,
  type ClientFollowUp,
  type ClientContribution,
  type ClientGoal,
  clientGoalSchema,
  type CalculationSnapshot,
  type Client,
  clientSchema,
  type FamilyMember,
  isFinanciallyDependentToday,
  type CreateFamilyMemberInput,
  type UpdateFamilyMemberInput,
  type ClientFileLink,
  clientFileLinkSchema,
  createClientFileLinkSchema,
  type CreateClientFileLinkInput,
  type UploadClientFileInput,
  type CreateClient,
  createClientSchema,
  updateClientSchema,
  type UpdateClient,
  type CreateFinancialEntryInput,
  type CreateFollowUpInput,
  createFollowUpSchema,
  updateFollowUpSchema,
  createFamilyMemberSchema,
  updateFamilyMemberSchema,
  familyMemberSchema,
  clientFollowUpSchema,
  calculationSnapshotSchema,
  createReportSnapshotSchema,
  reportSnapshotSchema,
  legacyReportSnapshotSchema,
  operationJournalSchema,
  MAX_OPERATION_PENDING,
  MAX_OPERATION_COMPLETED_KEYS,
  type ContributionOperation,
  type CreateContributionInput,
  type SettleContributionInput,
  createContributionSchema,
  clientContributionSchema,
  settleContributionSchema,
  createGoalSchema,
  createScenarioSchema,
  goalStatusSchema,
  createFinancialEntrySchema,
  createProtectionNeedSchema,
  protectionNeedSchema,
  protectionMonthlyHouseholdExpense,
  moneyFromNumber,
  moneySchema,
  roundMoneyText,
  type CreateGoalInput,
  type ClientProtectionNeed,
  type CreateProtectionNeedInput,
  type CreateReportSnapshotInput,
  type RetirementCalculationRequest,
  type ReportSnapshot,
  type LegacyReportSnapshot,
  type ReportSummary,
  type ReportSection,
  type ScenarioAssumptions,
  type ScenarioAssumptionsRow,
  type UpdateFollowUpInput,
  type ClientPipeline,
  type ClientPipelineStage,
  type AuditEntry,
  type AuditLog,
  type OperationReconcileResult,
  type UpdateFinancialEntryInput,
  type UpdateGoalInput,
  type CreateFinancingComparisonInput,
  type CreateFinancingSimulationInput,
  type CreatePrivatePensionSimulationInput,
  type FinancingComparison,
  type FinancingSimulation,
  type PrivatePensionSimulation,
  type FinancingScheduleRow,
  assumptionsFromRow,
  createFinancingSimulationSchema,
  createFinancingComparisonSchema,
  createPrivatePensionSimulationSchema,
  privatePensionSimulationSchema,
  privatePensionResultMatches,
  assertPrivatePensionSimulationDerived,
  financingSimulationSchema,
  financingComparisonSchema,
  planScenarioSchema,
  r5PlanningDataSchema,
  planningDataSchema,
  calculationHistorySchema,
  auditLogSchema,
  auditEntrySchema,
  reportsDataSchema,
  fileLinksDataSchema,
  followUpDataSchema,
  operationReconcileResultSchema,
  apiErrorSchema,
  scenarioAssumptionsSchema,
  editorialNotesDataSchema,
  capturedFinanceDataSchema,
  type CapturedFinanceData,
  guaranteeExposureSchema,
  planningExclusionSchema,
  savePlanningExclusionSchema,
  type PlanningExclusionSelection,
  type SavePlanningExclusionInput,
  resolveExcludedTargetIds,
  canonicalPlanningExclusions,
  protectionRevisionPayload,
  guaranteeExposureBindingMatches,
  protectionGuaranteesScenarioMatchesReport,
  resolveEffectiveGuaranteeExposures,
  mapPlannedCashflowRulesToEngine,
  retirementCalculationRequestSchema,
} from '@fintracks/contracts';
import {
  annualizeFinancingComparison,
  compareFinancingSchedules,
  consortiumScheduleRowsFromInput,
  emergencySuccessionReserve,
  futureValueLump,
  goalPeriodicContribution,
  inventoryCost,
  monthlyEffectiveRate,
  periodicInternalRateOfReturnPct,
  presentValueLump,
  presentValueAnnuityDue,
  presentValueSchedule,
  privatePensionProjection,
  protectionNeedFromDerived,
  realAnnualRate,
  sacSchedule,
  serializeCalendarResult,
  simulateCalendar,
  MonthlyCashflowRule,
  AnnualCashflowRule,
  type SerializedCalendarResult,
} from '@fintracks/calculation-engine';

export interface SessionUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: { id: string; name: string };
}

const SESSION_USER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SESSION_USER_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PROVISIONING_KEY = /^[A-Za-z0-9._:-]{1,200}$/;
const IDEMPOTENCY_KEY = /^[a-z0-9._:-]{8,200}$/;
const PROVISIONING_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
function canonicalIdempotencyKey(value: string): string {
  return value.trim().toLowerCase();
}
function hasUnsafeProvisioningControl(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
}
const FINTRACKS_MANAGEMENT_ROLE_ID = 'bf45c43b-01ce-4122-a2f2-8608c11b39ff';
const FINTRACKS_CONSULTANT_ROLE_ID = 'a5d4129c-81ec-409c-bacc-ad60661edad0';
const FINTRACKS_ROLE_IDS = new Set([FINTRACKS_MANAGEMENT_ROLE_ID, FINTRACKS_CONSULTANT_ROLE_ID]);
const POCKETBASE_WIRE_PATH = '/api/pocketbase-wire';
const POCKETBASE_API_PATH = '/api';

function pocketBaseOrigin(serverOrigin?: string): string | undefined {
  const configured = serverOrigin?.trim();
  if (!configured) {
    return typeof window !== 'undefined' && window.location?.origin ? window.location.origin : undefined;
  }
  let parsed: URL;
  try { parsed = new URL(configured); } catch { throw new Error('serverOrigin do PocketBase deve ser uma origem absoluta.'); }
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || parsed.pathname !== '/' || parsed.search || parsed.hash) {
    throw new Error('serverOrigin do PocketBase deve conter somente uma origem same-origin, sem credenciais, query ou hash.');
  }
  if (typeof window !== 'undefined' && window.location?.origin && parsed.origin !== window.location.origin) {
    throw new Error('O adapter PocketBase aceita somente a origem da aplicação.');
  }
  return parsed.origin;
}

function pocketBaseEndpoint(value: string | undefined, expectedPath: string, origin?: string, absoluteOutput = false): string {
  const raw = (value ?? expectedPath).trim();
  if (!raw || raw.startsWith('//')) throw new Error(`O endpoint PocketBase deve usar exatamente ${expectedPath}.`);
  const absolute = /^[a-z][a-z\d+.-]*:/i.test(raw);
  if (absolute && !origin) throw new Error('Uso server-side do PocketBase exige serverOrigin explícita para uma URL absoluta same-origin.');
  let parsed: URL;
  try { parsed = new URL(raw, origin ?? 'https://fintracks-pocketbase.invalid'); } catch { throw new Error(`O endpoint PocketBase deve usar exatamente ${expectedPath}.`); }
  if (parsed.username || parsed.password || parsed.search || parsed.hash || parsed.pathname !== expectedPath) {
    throw new Error(`O endpoint PocketBase deve usar exatamente ${expectedPath}, sem query, hash ou credenciais.`);
  }
  if (origin && parsed.origin !== origin) throw new Error('O adapter PocketBase aceita somente endpoints same-origin.');
  return origin && (absolute || absoluteOutput) ? `${origin}${expectedPath}` : expectedPath;
}

function assertPocketBaseNamespace(value: string): string {
  const namespace = value.trim();
  if (!SESSION_USER_ID.test(namespace) || namespace !== FINTRACKS_FILES_FOLDER_ID) {
    throw new Error('O namespace lógico de arquivos PocketBase deve ser o UUID canônico de Fintracks.');
  }
  return namespace;
}

export class RepositoryError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(message: string, status: number, code = 'REQUEST_FAILED', requestId?: string) {
    super(message);
    this.name = 'RepositoryError';
    this.status = status;
    this.code = code;
    if (requestId !== undefined) this.requestId = requestId;
  }
}

export type ClientProvisioningState = 'pending' | 'directus_confirmed' | 'provisioned' | 'failed' | 'outcome_unknown' | 'dead_letter';
export interface ClientProvisioningStatus {
  clientId: string;
  state: ClientProvisioningState;
  attempts: number;
  requestId: string;
  redriveCount: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface PlanScenario {
  id: string;
  planId: string;
  name: string;
  isPrimary: boolean;
  visibleInComparison: boolean;
  version: number;
  assumptionsVersion: number;
  assumptions: ScenarioAssumptions;
}

export interface CreateScenarioInput {
  name: string;
  sourceScenarioId?: string;
  makePrimary?: boolean;
}

export type RepositoryProvider = 'directus' | 'pocketbase';

export interface FintracksRepository {
  readonly mode: 'demo' | 'directus' | 'pocketbase';
  login(email: string, password: string): Promise<SessionUser>;
  logout(): Promise<void>;
  currentUser(): Promise<SessionUser | null>;
  listClients(): Promise<Client[]>;
  listArchivedClients(): Promise<Client[]>;
  getClient(id: string): Promise<Client | null>;
  /** Provider legado pode omitir a extensão; os adapters Directus/PocketBase a implementam. */
  getCapturedFinanceData?(clientId: string): Promise<CapturedFinanceData | null>;
  createClient(input: CreateClient): Promise<Client>;
  getClientProvisioning(clientId: string): Promise<ClientProvisioningStatus>;
  reconcileClientProvisioning(clientId: string): Promise<ClientProvisioningStatus>;
  updateClient(id: string, patch: UpdateClient, expectedVersion: number): Promise<Client>;
  listFamilyMembers(clientId: string): Promise<FamilyMember[]>;
  createFamilyMember(input: CreateFamilyMemberInput): Promise<FamilyMember>;
  updateFamilyMember(clientId: string, memberId: string, patch: UpdateFamilyMemberInput, expectedVersion: number): Promise<FamilyMember>;
	  listClientFiles(clientId: string): Promise<ClientFileLink[]>;
	  listArchivedClientFiles(clientId: string): Promise<ClientFileLink[]>;
	  /** @deprecated Link-only para migração/legado; não envia bytes nem cria receipt. Use uploadClientFile para novos arquivos. */
	  createClientFile(input: CreateClientFileLinkInput): Promise<ClientFileLink>;
  uploadClientFile(input: UploadClientFileInput): Promise<ClientFileLink>;
  downloadClientFile(clientId: string, fileId: string): Promise<Blob>;
  archiveClientFile(clientId: string, fileId: string, expectedVersion: number): Promise<ClientFileLink>;
  restoreClientFile(clientId: string, fileId: string, expectedVersion: number): Promise<ClientFileLink>;
  reconcileOperations(clientId: string): Promise<OperationReconcileResult>;
  listScenarios(clientId: string): Promise<PlanScenario[]>;
  getScenario(clientId: string, scenarioId: string): Promise<PlanScenario | null>;
  createScenario(clientId: string, input: CreateScenarioInput): Promise<PlanScenario>;
  saveScenario(clientId: string, scenario: PlanScenario): Promise<PlanScenario>;
  listExclusionSelections(clientId: string): Promise<PlanningExclusionSelection[]>;
  saveExclusionSelection(clientId: string, input: SavePlanningExclusionInput, expectedVersion: number): Promise<PlanningExclusionSelection>;
  listGoals(clientId: string): Promise<ClientGoal[]>;
  listArchivedGoals(clientId: string): Promise<ClientGoal[]>;
  createGoal(clientId: string, input: CreateGoalInput): Promise<ClientGoal>;
  updateGoal(clientId: string, goalId: string, patch: UpdateGoalInput, expectedVersion: number): Promise<ClientGoal>;
  listFollowUp(clientId?: string): Promise<ClientFollowUp[]>;
  createFollowUp(input: CreateFollowUpInput): Promise<ClientFollowUp>;
  updateFollowUp(clientId: string, itemId: string, patch: UpdateFollowUpInput, expectedVersion: number): Promise<ClientFollowUp>;
  listContributions(clientId: string): Promise<ClientContribution[]>;
  createContribution(input: CreateContributionInput, idempotencyKey: string): Promise<ClientContribution>;
  reconcileContributionByKey(clientId: string, idempotencyKey: string, kind?: 'plan' | 'settle'): Promise<ClientContribution>;
  settleContribution(clientId: string, contributionId: string, input: SettleContributionInput, expectedVersion: number, idempotencyKey: string): Promise<ClientContribution>;
  getClientPipeline(clientId: string): Promise<ClientPipeline>;
  updateClientPipeline(clientId: string, stage: ClientPipelineStage, expectedVersion: number): Promise<ClientPipeline>;
  listFinancialEntries(clientId: string): Promise<ClientFinancialEntry[]>;
  listArchivedFinancialEntries(clientId: string): Promise<ClientFinancialEntry[]>;
  createFinancialEntry(input: CreateFinancialEntryInput): Promise<ClientFinancialEntry>;
  updateFinancialEntry(clientId: string, entryId: string, patch: UpdateFinancialEntryInput, expectedVersion: number): Promise<ClientFinancialEntry>;
  listGuaranteeExposures(clientId: string, scenarioId?: string | null): Promise<GuaranteeExposure[]>;
  getProtectionNeed(clientId: string): Promise<ClientProtectionNeed | null>;
  saveProtectionNeed(input: CreateProtectionNeedInput, expectedVersion: number): Promise<ClientProtectionNeed>;
  listPrivatePensionSimulations(clientId: string): Promise<PrivatePensionSimulation[]>;
  createPrivatePensionSimulation(input: CreatePrivatePensionSimulationInput): Promise<PrivatePensionSimulation>;
  listFinancingSimulations(clientId: string): Promise<FinancingSimulation[]>;
  createFinancingSimulation(input: CreateFinancingSimulationInput): Promise<FinancingSimulation>;
  listFinancingComparisons(clientId: string): Promise<FinancingComparison[]>;
  createFinancingComparison(input: CreateFinancingComparisonInput): Promise<FinancingComparison>;
  listReportSnapshots(clientId?: string): Promise<ReportSnapshot[]>;
  listLegacyReportSnapshots(clientId?: string): Promise<LegacyReportSnapshot[]>;
  createReportSnapshot(input: CreateReportSnapshotInput): Promise<ReportSnapshot>;
  calculateRetirement(request: RetirementCalculationRequest): Promise<CalculationSnapshot>;
  listCalculationSnapshots(clientId?: string): Promise<CalculationSnapshot[]>;
}

export interface DirectusRepositoryOptions {
  baseUrl: string;
  edgeBaseUrl?: string;
  fileGatewayBaseUrl?: string;
  contributionGatewayBaseUrl?: string;
  provider?: RepositoryProvider;
  serverOrigin?: string;
  directusOnly?: boolean;
  filesFolderId?: string;
  allowDirectusFileFallback?: boolean;
  directusFileFallbackMode?: 'synthetic';
  casWrites?: boolean;
  fetchImpl?: typeof fetch;
}

interface Envelope<T> { data: T; meta?: { total_count?: number | string; noOp?: boolean } }
interface RawContact {
  id?: string;
  client_id: string;
  kind: 'email' | 'phone' | 'mobile' | 'other';
  value: string;
  is_primary: boolean;
}
interface RawFamilyMember {
  id: string;
  client_id: string;
  full_name: string;
  relationship: string;
  birth_date: string | null;
  financially_dependent: boolean;
  succession_dependent: boolean;
  financial_dependency_end: string | null;
  protection_needed: boolean;
  version: number | string;
  date_created?: string;
  date_updated?: string;
}
interface RawClient {
  id: string;
  status: Client['status'];
  full_name: string;
  short_name: string | null;
  birth_date: string | null;
  stated_age: number | null;
  profession: string | null;
  date_created?: string;
  date_updated?: string;
  version: number | string;
  data_model_version?: number | string | null;
  data_state?: 'canonical' | 'candidate-only';
  planning_data?: unknown;
  editorial_notes?: unknown;
  captured_finance?: unknown | object | null;
  calculation_history?: unknown;
  reports_data?: unknown;
  follow_up_data?: unknown;
  file_links?: unknown;
  audit_log?: unknown;
  operation_journal?: unknown;
  contacts?: RawContact[];
}
interface RawDirectusFile {
  id: string;
  folder?: string | null;
  description?: string | null;
  filename_download?: string | null;
  type?: string | null;
  filesize?: number | string | null;
}
interface R5PlanningData {
  version: number;
  pensionSimulations: PrivatePensionSimulation[];
  financingSimulations: FinancingSimulation[];
  financingComparisons: FinancingComparison[];
}
interface PlanningData {
  version: number;
  scenarios: PlanScenario[];
  goals: ClientGoal[];
  financials: ClientFinancialEntry[];
  guaranteeExposures: GuaranteeExposure[];
  protection: ClientProtectionNeed | null;
  exclusions: PlanningExclusionSelection[];
  r5: R5PlanningData;
}
interface CalculationIdempotency { key: string; requestHash: string; snapshotId: string }
interface CalculationHistory { version: number; snapshots: CalculationSnapshot[]; idempotency: CalculationIdempotency[] }
interface ReportsData { version: number; reports: ReportSnapshot[]; legacyReports: LegacyReportSnapshot[] }
interface FileLinksData { version: number; files: ClientFileLink[] }
interface FollowUpData { version: number; items: ClientFollowUp[]; pipeline: ClientPipeline; contributions: ClientContribution[]; contributionOperations: ContributionOperation[] }
const DATA_MODEL_VERSION = 'embedded-v1';
const DOCUMENT_VERSION = 1;
const SYNTHETIC_ACTOR_ID = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa';
const CLIENT_FIELDS = [
  'id', 'status', 'full_name', 'short_name', 'birth_date', 'stated_age', 'profession',
  'date_created', 'date_updated', 'version', 'data_model_version', 'planning_data',
  'editorial_notes', 'captured_finance', 'calculation_history', 'reports_data', 'audit_log', 'operation_journal', 'contacts.id', 'contacts.client_id', 'contacts.kind', 'contacts.value',
  'contacts.is_primary', 'follow_up_data', 'file_links',
].join(',');
const CLIENT_LIST_FIELDS = [
		'id', 'status', 'full_name', 'short_name', 'birth_date', 'stated_age', 'profession',
		'date_created', 'date_updated', 'version', 'contacts.id', 'contacts.client_id', 'contacts.kind', 'contacts.value', 'contacts.is_primary',
].join(',');
const FAMILY_FIELDS = 'id,client_id,full_name,relationship,birth_date,financially_dependent,succession_dependent,financial_dependency_end,protection_needed,version,date_created,date_updated';

function parseJson<T>(value: unknown, fallback: T): T {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') {
    try { return JSON.parse(value) as T; } catch { throw new RepositoryError('JSON persistido inválido', 502, 'INVALID_ENVELOPE'); }
  }
  return value as T;
}

function normalizePensionSimulation(value: PrivatePensionSimulation | Record<string, unknown>): PrivatePensionSimulation {
  const parsed = privatePensionSimulationSchema.safeParse(value);
  if (!parsed.success) {
    try {
      if (!privatePensionResultMatches(value as Parameters<typeof privatePensionResultMatches>[0])) {
        throw new RepositoryError('Resultado de previdência persistido não corresponde ao motor canônico.', 502, 'INVALID_R5_DERIVATION');
      }
    } catch (error) {
      if (error instanceof RepositoryError) throw error;
    }
    throw new RepositoryError('Simulação de previdência persistida inválida', 502, 'INVALID_R5_READ');
  }
  return parsed.data;
}

function normalizeFinancingSimulation(value: FinancingSimulation | Record<string, unknown>): FinancingSimulation {
  const parsed = financingSimulationSchema.safeParse(value);
  if (!parsed.success) throw new RepositoryError('Simulação financeira persistida inválida', 502, 'INVALID_R5_READ');
  return parsed.data;
}

function normalizeFinancingComparison(value: FinancingComparison | Record<string, unknown>): FinancingComparison {
  const parsed = financingComparisonSchema.safeParse(value);
  if (!parsed.success) throw new RepositoryError('Comparação financeira persistida inválida', 502, 'INVALID_R5_READ');
  return parsed.data;
}

function sameDerivedValue(actual: unknown, expected: unknown): boolean {
  return canonicalJson(actual) === canonicalJson(expected);
}

function sameClientDocumentValue(field: typeof CLIENT_DOCUMENT_FIELDS[number], actual: unknown, expected: unknown, clientId: string): boolean {
  if (field === 'follow_up_data') {
    try {
      const actualData = followUpData(actual, clientId);
      const expectedData = followUpData(expected, clientId);
      const expectedRecord = parseJson<Record<string, unknown>>(expected, {});
      const comparable = (data: FollowUpData): Record<string, unknown> => ({
        version: data.version,
        items: data.items,
        pipeline: data.pipeline,
        ...(Object.prototype.hasOwnProperty.call(expectedRecord, 'contributions') ? { contributions: data.contributions } : {}),
        ...(Object.prototype.hasOwnProperty.call(expectedRecord, 'contributionOperations') ? { contributionOperations: data.contributionOperations } : {}),
      });
      // A document-gateway response is intentionally redacted. Compare only
      // protected financial fields that the command actually supplied.
      return sameDerivedValue(comparable(actualData), comparable(expectedData));
    } catch {
      return false;
    }
  }
  return sameDerivedValue(actual, expected);
}

const CLIENT_DOCUMENT_FIELDS = ['data_model_version', 'planning_data', 'calculation_history', 'reports_data', 'follow_up_data', 'file_links', 'audit_log', 'operation_journal'] as const;

function assertClientMutationContent(response: Envelope<RawClient>, clientId: string, expectedVersion: number, expectedBody: Record<string, unknown>): void {
  assertClientMutationIdentity(response, clientId, expectedVersion);
  for (const field of CLIENT_DOCUMENT_FIELDS) {
    if (field === 'operation_journal' && response.data[field] === undefined) continue;
    if (Object.prototype.hasOwnProperty.call(expectedBody, field) && !sameClientDocumentValue(field, response.data[field], expectedBody[field], clientId)) {
      throw new RepositoryError(`Resposta persistida diverge no campo ${field}.`, 502, 'CLIENT_RESPONSE_CONTENT_INVALID');
    }
  }
}

function assertClientMutationFields(response: Envelope<RawClient>, clientId: string, expectedVersion: number, expectedPatch: UpdateClient): void {
  assertClientMutationIdentity(response, clientId, expectedVersion);
  const rawData = Array.isArray(response.data) ? response.data[0] : response.data;
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) throw new RepositoryError('Cliente persistido inválido.', 422, 'VALIDATION_ERROR');
  const persisted = mapClient(rawData as RawClient);
  for (const field of ['fullName', 'shortName', 'birthDate', 'statedAge', 'profession', 'email', 'phone', 'status'] as const) {
    if (expectedPatch[field] !== undefined && !sameDerivedValue(persisted[field], expectedPatch[field])) {
      throw new RepositoryError(`Resposta persistida diverge no campo cadastral ${field}.`, 502, 'CLIENT_RESPONSE_CONTENT_INVALID');
    }
  }
}

function assertR5DerivedResults(value: R5PlanningData): void {
  for (const simulation of value.pensionSimulations) {
    try {
      assertPrivatePensionSimulationDerived(simulation);
      const projection = privatePensionProjection({
        product: simulation.product,
        taxRegime: simulation.taxRegime,
        socialSecurity: simulation.socialSecurity,
        annualTaxableIncome: Number(simulation.annualTaxableIncome),
        annualDeductibleExpenses: Number(simulation.annualDeductibleExpenses),
        currentReserve: Number(simulation.currentReserve),
        monthlyContribution: Number(simulation.monthlyContribution),
        annualReturnPct: simulation.annualReturnPct,
        inflationAnnualPct: simulation.inflationAnnualPct,
        accumulationYears: simulation.accumulationYears,
        retirementIncomeYears: simulation.retirementIncomeYears,
      });
      const expected = {
        suggestedPgblAnnual: projection.suggestedPgblAnnual.toFixed(2),
        taxBenefit: projection.taxBenefit.toFixed(2),
        grossReserve: projection.grossReserve.toFixed(2),
        taxableRescue: projection.taxableRescue.toFixed(2),
        estimatedTax: projection.estimatedTax.toFixed(2),
        netReserve: projection.netReserve.toFixed(2),
        sustainableMonthlyIncome: projection.sustainableMonthlyIncome.toFixed(2),
        taxRatePct: projection.taxRatePct,
      };
      if (!sameDerivedValue(simulation.result, expected)) throw new Error('PENSION_DERIVATION_INVALID');
    } catch {
      throw new RepositoryError('Resultado de previdência persistido não corresponde ao motor canônico.', 502, 'INVALID_R5_DERIVATION');
    }
  }

  for (const simulation of value.financingSimulations) {
    try {
      if (simulation.schedule.length !== simulation.termMonths || simulation.schedule.length < 1) throw new Error('FINANCING_SCHEDULE_INVALID');
      const expectedSchedule = simulation.kind === 'sac'
        ? sacSchedule(Number(simulation.principal), simulation.termMonths, simulation.annualInterestPct, simulation.annualIndexationPct)
          .map((row) => ({ installment: row.number, payment: row.payment.toFixed(2), principal: row.amortization.toFixed(2), interest: row.interest.toFixed(2), balance: row.closingBalance.toFixed(2) }))
        : consortiumScheduleRowsFromInput({
          credit: Number(simulation.principal), termMonths: simulation.termMonths, anticipationFeePct: simulation.anticipationFeePct,
          administrationFeePct: simulation.administrationFeePct, reserveFundPct: simulation.reserveFundPct, monthlyInsurancePct: simulation.monthlyInsurancePct,
          installmentReducerPct: simulation.installmentReducerPct, contemplationInstallment: simulation.contemplationInstallment,
          totalBidPct: simulation.totalBidPct, embeddedBidPct: simulation.embeddedBidPct, annualAdjustmentPct: simulation.annualAdjustmentPct,
        }).map((row, index) => ({ installment: index + 1, payment: row.payment.toFixed(2), principal: row.principal.toFixed(2), interest: row.interest.toFixed(2), balance: row.balance.toFixed(2) }));
      const expectedTotal = sumMoneyStrings(expectedSchedule.map((row) => row.payment));
      if (!sameDerivedValue(simulation.schedule, expectedSchedule) || simulation.firstPayment !== expectedSchedule[0]?.payment || simulation.lastPayment !== expectedSchedule.at(-1)?.payment || simulation.totalPaid !== expectedTotal) {
        throw new Error('FINANCING_DERIVATION_INVALID');
      }
    } catch {
      throw new RepositoryError('Cronograma financeiro persistido não corresponde ao motor canônico.', 502, 'INVALID_R5_DERIVATION');
    }
  }

  const financingById = new Map(value.financingSimulations.map((simulation) => [simulation.id, simulation]));
  for (const comparison of value.financingComparisons) {
    const suggested = financingById.get(comparison.suggestedSimulationId);
    const alternative = financingById.get(comparison.alternativeSimulationId);
    if (!suggested || !alternative || suggested.clientId !== comparison.clientId || alternative.clientId !== comparison.clientId) {
      throw new RepositoryError('Comparação financeira referencia simulação inexistente ou estrangeira.', 502, 'INVALID_R5_REFERENCE');
    }
    try {
      const suggestedPayments = suggested.schedule.map((row) => Number(row.payment));
      const alternativePayments = alternative.schedule.map((row) => Number(row.payment));
      const rows = compareFinancingSchedules(suggestedPayments, alternativePayments, comparison.annualOpportunityRatePct);
      const annualRows = annualizeFinancingComparison(rows);
      const tirAnnualPct = periodicInternalRateOfReturnPct([0, ...rows.map((row) => row.difference.toNumber())]);
      const expected = {
        suggestedPresentValue: presentValueSchedule(suggestedPayments, comparison.annualOpportunityRatePct).toFixed(2),
        alternativePresentValue: presentValueSchedule(alternativePayments, comparison.annualOpportunityRatePct).toFixed(2),
        tirAnnualPct,
        rows: rows.map((row) => ({ installment: row.installment, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
        annualRows: annualRows.map((row) => ({ year: row.year, suggestedPayment: row.suggestedPayment.toFixed(2), alternativePayment: row.alternativePayment.toFixed(2), difference: row.difference.toFixed(2), accumulatedDifference: row.accumulatedDifference.toFixed(2) })),
      };
      const actual = {
        suggestedPresentValue: comparison.suggestedPresentValue,
        alternativePresentValue: comparison.alternativePresentValue,
        tirAnnualPct: comparison.tirAnnualPct,
        rows: comparison.rows,
        annualRows: comparison.annualRows,
      };
      if (!sameDerivedValue(actual, expected)) throw new Error('COMPARISON_DERIVATION_INVALID');
    } catch {
      throw new RepositoryError('Comparação financeira persistida não corresponde às simulações canônicas.', 502, 'INVALID_R5_DERIVATION');
    }
  }
}

function assertEmbeddedOwnership(items: Array<{ clientId: string }>, clientId: string | undefined, label: string): void {
  if (clientId && items.some((item) => item.clientId !== clientId)) throw new RepositoryError(`${label} pertence a outro cliente`, 502, 'OWNERSHIP_INVALID');
}

function r5Data(value: unknown, clientId?: string): R5PlanningData {
  const parsed = parseJson<Partial<R5PlanningData>>(value, {});
  if (clientId) {
    const rows = [
      ...(Array.isArray(parsed.pensionSimulations) ? parsed.pensionSimulations : []),
      ...(Array.isArray(parsed.financingSimulations) ? parsed.financingSimulations : []),
      ...(Array.isArray(parsed.financingComparisons) ? parsed.financingComparisons : []),
    ];
    if (rows.some((row) => String((row as Record<string, unknown>)?.clientId ?? '') !== clientId)) {
      throw new RepositoryError('Registro R5 pertence a outro cliente.', 502, 'OWNERSHIP_INVALID');
    }
  }
  const result = assertInput(r5PlanningDataSchema, {
    version: parsed.version,
    pensionSimulations: Array.isArray(parsed.pensionSimulations) ? parsed.pensionSimulations.map(normalizePensionSimulation) : parsed.pensionSimulations,
    financingSimulations: Array.isArray(parsed.financingSimulations) ? parsed.financingSimulations.map(normalizeFinancingSimulation) : parsed.financingSimulations,
    financingComparisons: Array.isArray(parsed.financingComparisons) ? parsed.financingComparisons.map(normalizeFinancingComparison) : parsed.financingComparisons,
  }, 'Envelope R5 persistido inválido.');
  assertEmbeddedOwnership(result.pensionSimulations, clientId, 'Simulação de previdência');
  assertEmbeddedOwnership(result.financingSimulations, clientId, 'Simulação financeira');
  assertEmbeddedOwnership(result.financingComparisons, clientId, 'Comparação financeira');
  assertR5DerivedResults(result);
  return result;
}

function assertProtectionDerived(value: ClientProtectionNeed): void {
  try {
    const expected = calculateProtection({
      clientId: value.clientId,
      monthlyLivingExpenses: Number(value.monthlyLivingExpenses), horizonYears: value.horizonYears,
      dependents: value.dependents, estateBaseValue: Number(value.estateBaseValue),
      legalFeesPct: value.legalFeesPct, courtCostsPct: value.courtCostsPct, inheritanceTaxPct: value.inheritanceTaxPct,
      emergencyMonths: value.emergencyMonths, currentGuarantees: Number(value.currentGuarantees),
      wholeLifeInsuredCapital: Number(value.wholeLifeInsuredCapital), deductiblePrivatePension: Number(value.deductiblePrivatePension),
      realAnnualRatePct: value.realAnnualRatePct,
    } as CreateProtectionNeedInput, { ...value, version: value.version - 1 });
    for (const field of ['totalExpenses', 'emergencyReserve', 'successionNeed', 'recommendedCapital', 'coverageGap'] as const) {
      if (value[field] !== expected[field]) throw new Error(`PROTECTION_DERIVATION_INVALID:${field}`);
    }
  } catch {
    throw new RepositoryError('Derivados de proteção persistidos não correspondem ao motor canônico.', 502, 'INVALID_PROTECTION_DERIVATION');
  }
}

function planningData(value: unknown, clientId?: string): PlanningData {
  const parsed = parseJson<Partial<PlanningData>>(value, {});
  const normalized = {
    version: parsed.version,
    scenarios: Array.isArray(parsed.scenarios) ? parsed.scenarios.map(normalizeScenario) : parsed.scenarios,
    goals: Array.isArray(parsed.goals) ? parsed.goals.map(normalizeGoal) : parsed.goals,
    financials: Array.isArray(parsed.financials) ? parsed.financials.map(normalizeFinancialEntry) : parsed.financials,
    guaranteeExposures: Array.isArray(parsed.guaranteeExposures) ? parsed.guaranteeExposures.map((item) => assertInput(guaranteeExposureSchema, item, 'Exposição de garantia persistida inválida.')) : parsed.guaranteeExposures,
    protection: parsed.protection === null ? null : parsed.protection === undefined ? undefined : normalizeProtection(parsed.protection),
    exclusions: Array.isArray(parsed.exclusions) ? parsed.exclusions.map((item) => assertInput(planningExclusionSchema, item, 'Desconsideração persistida inválida.')) : parsed.exclusions,
    r5: r5Data(parsed.r5, clientId),
  };
  if (Array.isArray(normalized.scenarios) && Array.isArray(normalized.goals)) {
    const scenarioIds = new Set(normalized.scenarios.map((scenario) => typeof scenario === 'object' && scenario !== null && 'id' in scenario ? String((scenario as { id?: unknown }).id) : ''));
    if (normalized.goals.some((goal) => typeof goal === 'object' && goal !== null && 'scenarioId' in goal && (goal as { scenarioId?: unknown }).scenarioId !== null && !scenarioIds.has(String((goal as { scenarioId?: unknown }).scenarioId)))) throw new RepositoryError('Meta referencia um cenário inexistente.', 502, 'SCENARIO_OWNERSHIP_INVALID');
  }
  const result = assertInput(planningDataSchema, normalized, 'Envelope de planejamento persistido inválido.');
  assertEmbeddedOwnership(result.goals, clientId, 'Meta');
  const scenarioIds = new Set(result.scenarios.map((scenario) => scenario.id));
  if (result.goals.some((goal) => goal.scenarioId !== null && !scenarioIds.has(goal.scenarioId))) throw new RepositoryError('Meta referencia um cenário inexistente.', 502, 'SCENARIO_OWNERSHIP_INVALID');
  assertEmbeddedOwnership(result.financials, clientId, 'Lançamento financeiro');
  assertEmbeddedOwnership(result.guaranteeExposures, clientId, 'Exposição de garantia');
  assertEmbeddedOwnership(result.exclusions, clientId, 'Desconsideração');
  const expenseIds = new Set(result.financials.filter((entry) => entry.kind === 'expense').map((entry) => entry.id));
  if (result.exclusions.some((selection) => selection.targetType === 'expense' && selection.targetIds.some((id) => !expenseIds.has(id)))) {
    throw new RepositoryError('Desconsideração referencia despesa de outro cliente ou inexistente.', 502, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
  }
  const guaranteeIds = new Set(result.guaranteeExposures.map((exposure) => exposure.id));
  if (result.exclusions.some((selection) => selection.targetType === 'guarantee' && selection.targetIds.some((id) => !guaranteeIds.has(id)))) {
    throw new RepositoryError('Desconsideração referencia exposição de garantia de outro cliente ou inexistente.', 502, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
  }
  const scenarioIdsForExclusions = new Set(result.scenarios.map((scenario) => scenario.id));
  if (result.exclusions.some((item) => item.scenarioId !== null && !scenarioIdsForExclusions.has(item.scenarioId))) throw new RepositoryError('Desconsideração referencia cenário inexistente.', 502, 'SCENARIO_OWNERSHIP_INVALID');
  if (clientId && result.protection && result.protection.clientId !== clientId) throw new RepositoryError('Diagnóstico de proteção pertence a outro cliente', 502, 'OWNERSHIP_INVALID');
  if (result.protection) assertProtectionDerived(result.protection);
  return result;
}

function calculationHistory(value: unknown, clientId?: string): CalculationHistory {
  const parsed = parseJson<Partial<CalculationHistory> | CalculationSnapshot[]>(value, {});
  if (Array.isArray(parsed)) {
    const snapshots = parsed.map((snapshot) => assertInput(calculationSnapshotSchema, snapshot, 'Snapshot de cálculo persistido inválido.'));
    assertEmbeddedOwnership(snapshots, clientId, 'Snapshot de cálculo');
    if (snapshots.some((snapshot) => snapshot.idempotencyKey)) throw new RepositoryError('Histórico legado não contém hashes de idempotência', 502, 'INVALID_CALCULATION_HISTORY');
    return assertInput(calculationHistorySchema, { version: DOCUMENT_VERSION, snapshots, idempotency: [] }, 'Histórico de cálculo persistido inválido.');
  }
  const snapshots = Array.isArray(parsed.snapshots) ? parsed.snapshots.map((snapshot) => assertInput(calculationSnapshotSchema, snapshot, 'Snapshot de cálculo persistido inválido.')) : parsed.snapshots;
  assertEmbeddedOwnership(snapshots ?? [], clientId, 'Snapshot de cálculo');
  return assertInput(calculationHistorySchema, { version: parsed.version, snapshots, idempotency: parsed.idempotency }, 'Histórico de cálculo persistido inválido.');
}

function reportsData(value: unknown, clientId?: string, scenarioIds?: ReadonlySet<string>): ReportsData {
  const parsed = parseJson<Record<string, unknown>>(value, {});
  const rawReports = parsed.reports === undefined ? [] : parsed.reports;
  if (!Array.isArray(rawReports)) throw new RepositoryError('Envelope de relatórios persistido contém reports inválido.', 502, 'INVALID_REPORTS_DATA');
  const reports: ReportSnapshot[] = [];
  const rawLegacyReports = parsed.legacyReports === undefined ? [] : parsed.legacyReports;
  if (!Array.isArray(rawLegacyReports)) throw new RepositoryError('Envelope de relatórios persistido contém legacyReports inválido.', 502, 'INVALID_REPORTS_DATA');
  const legacyReports: LegacyReportSnapshot[] = rawLegacyReports.map((report) => assertInput(legacyReportSnapshotSchema, report, 'Snapshot legado de relatório persistido inválido.'));
  for (const report of rawReports) {
    const canonical = reportSnapshotSchema.safeParse(report);
    const details = canonical.success && canonical.data.details && typeof canonical.data.details === 'object' && !Array.isArray(canonical.data.details) ? canonical.data.details as Record<string, unknown> : null;
    if (!canonical.success) {
      const legacy = legacyReportSnapshotSchema.safeParse(report);
      const markedCanonical = report && typeof report === 'object' && !Array.isArray(report) && (report as Record<string, unknown>).canonical === true;
      if (!legacy.success || markedCanonical) throw new RepositoryError('Snapshot de relatório persistido inválido.', 502, 'INVALID_REPORTS_DATA');
      legacyReports.push(legacy.data);
    } else if (Object.prototype.hasOwnProperty.call(details ?? {}, 'family')) reports.push(canonical.data);
    else legacyReports.push(assertInput(legacyReportSnapshotSchema, report, 'Snapshot legado de relatório persistido inválido.'));
  }
  const result = assertInput(reportsDataSchema, { version: parsed.version, reports, legacyReports }, 'Envelope de relatórios persistido inválido.');
  assertEmbeddedOwnership(result.reports ?? [], clientId, 'Snapshot de relatório');
  assertEmbeddedOwnership(result.legacyReports ?? [], clientId, 'Snapshot legado de relatório');
  if (scenarioIds && result.reports.some((report) => !scenarioIds.has(report.scenarioId))) throw new RepositoryError('Relatório referencia cenário inexistente ou de outro cliente.', 502, 'SCENARIO_OWNERSHIP_INVALID');
  return result;
}

function persistedNumeric(value: unknown, field: string): number {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
  if (!Number.isFinite(parsed)) throw new RepositoryError(`Campo numérico persistido inválido: ${field}`, 502, 'INVALID_NUMERIC_READ');
  return parsed;
}

function persistedBoolean(value: unknown, field: string): boolean {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  throw new RepositoryError(`Campo booleano persistido inválido: ${field}`, 502, 'INVALID_BOOLEAN_READ');
}

function normalizeEmbeddedAssumptions(value: Record<string, unknown>): ScenarioAssumptions {
  const optionalNumber = (...keys: string[]) => {
    const raw = keys.map((key) => value[key]).find((item) => item !== undefined && item !== null);
    return raw === undefined ? undefined : persistedNumeric(raw, keys[0] ?? 'optional');
  };
  const postRetirementNominalAnnualRatePct = optionalNumber('postRetirementNominalAnnualRatePct', 'post_retirement_nominal_rate');
  const postRetirementInflationAnnualRatePct = optionalNumber('postRetirementInflationAnnualRatePct', 'post_retirement_inflation_rate');
  return assertInput(scenarioAssumptionsSchema, {
    baseYear: persistedNumeric(value.baseYear ?? value.base_year, 'baseYear'),
    baseMonth: persistedNumeric(value.baseMonth ?? value.base_month, 'baseMonth'),
    currentAge: persistedNumeric(value.currentAge ?? value.current_age, 'currentAge'),
    retirementAge: persistedNumeric(value.retirementAge ?? value.retirement_age, 'retirementAge'),
    lifeExpectancyAge: persistedNumeric(value.lifeExpectancyAge ?? value.life_expectancy_age, 'lifeExpectancyAge'),
    currentReserve: persistedNumeric(value.currentReserve ?? value.current_reserve, 'currentReserve'),
    monthlyContribution: persistedNumeric(value.monthlyContribution ?? value.monthly_contribution, 'monthlyContribution'),
    desiredMonthlyIncome: persistedNumeric(value.desiredMonthlyIncome ?? value.desired_monthly_income, 'desiredMonthlyIncome'),
    nominalAnnualRatePct: persistedNumeric(value.nominalAnnualRatePct ?? value.nominal_annual_rate_pct, 'nominalAnnualRatePct'),
    inflationAnnualRatePct: persistedNumeric(value.inflationAnnualRatePct ?? value.inflation_annual_rate_pct, 'inflationAnnualRatePct'),
    differentiatePostRetirementRates: persistedBoolean(value.differentiatePostRetirementRates ?? value.differentiate_post_retirement_rates ?? false, 'differentiatePostRetirementRates'),
    ...(postRetirementNominalAnnualRatePct === undefined ? {} : { postRetirementNominalAnnualRatePct }),
    ...(postRetirementInflationAnnualRatePct === undefined ? {} : { postRetirementInflationAnnualRatePct }),
    ...(value.cashflowRules === undefined ? {} : { cashflowRules: value.cashflowRules }),
  }, 'Premissas de cenário persistidas inválidas.');
}

function normalizeFamilyMember(value: RawFamilyMember | Record<string, unknown>): FamilyMember {
  const raw = value as Record<string, unknown>;
  const birthDate = raw.birthDate ?? raw.birth_date;
  const createdAt = raw.createdAt ?? raw.date_created;
  const rawUpdatedAt = raw.updatedAt ?? raw.date_updated;
  const updatedAt = rawUpdatedAt === null && Number(raw.version) === 1 ? createdAt : rawUpdatedAt;
  const normalized = {
    id: raw.id, clientId: raw.clientId ?? raw.client_id, fullName: raw.fullName ?? raw.full_name,
    relationship: raw.relationship, birthDate: birthDate === null || birthDate === undefined ? null : birthDate,
    financiallyDependent: persistedBoolean(raw.financiallyDependent ?? raw.financially_dependent, 'financiallyDependent'), successionDependent: persistedBoolean(raw.successionDependent ?? raw.succession_dependent, 'successionDependent'),
    financialDependencyEnd: raw.financialDependencyEnd === null || raw.financialDependencyEnd === undefined
      ? (raw.financial_dependency_end === null || raw.financial_dependency_end === undefined ? null : raw.financial_dependency_end)
      : raw.financialDependencyEnd,
    protectionNeeded: persistedBoolean(raw.protectionNeeded ?? raw.protection_needed, 'protectionNeeded'), version: persistedNumeric(raw.version, 'version'),
    createdAt, updatedAt,
  };
  return assertInput(familyMemberSchema, normalized, 'Familiar persistido inválido.');
}

function assertFamilyFields(member: FamilyMember, expected: CreateFamilyMemberInput | UpdateFamilyMemberInput, label: string): void {
  const checks: Array<[keyof FamilyMember, unknown]> = [
    ['fullName', 'fullName' in expected ? expected.fullName?.trim() : undefined],
    ['relationship', 'relationship' in expected ? expected.relationship?.trim() : undefined],
    ['birthDate', 'birthDate' in expected ? expected.birthDate ?? null : undefined],
    ['financiallyDependent', 'financiallyDependent' in expected ? expected.financiallyDependent ?? false : undefined],
    ['successionDependent', 'successionDependent' in expected ? expected.successionDependent ?? false : undefined],
    ['financialDependencyEnd', 'financialDependencyEnd' in expected ? expected.financialDependencyEnd ?? null : undefined],
    ['protectionNeeded', 'protectionNeeded' in expected ? expected.protectionNeeded ?? false : undefined],
  ];
  for (const [field, value] of checks) {
    if (value !== undefined && !sameDerivedValue(member[field], value)) throw new RepositoryError(`Resposta de ${label} diverge no campo ${field}.`, 502, 'FAMILY_RESPONSE_MISMATCH');
  }
}

function fileLinksData(value: unknown, clientId?: string): FileLinksData {
  const parsed = parseJson<Partial<FileLinksData>>(value, {});
  const files = Array.isArray(parsed.files) ? parsed.files.map((item) => assertInput(clientFileLinkSchema, item, 'Referência de arquivo persistida inválida.')) : parsed.files;
  if (clientId && Array.isArray(files) && files.some((file) => file.clientId !== clientId)) throw new RepositoryError('Arquivo pertence a outro cliente', 502, 'FILE_OWNERSHIP_INVALID');
  return assertInput(fileLinksDataSchema, { version: parsed.version, files }, 'Envelope de arquivos persistido inválido.');
}

function followUpData(value: unknown, clientId?: string): FollowUpData {
  const parsed = parseJson<Partial<FollowUpData>>(value, {});
  const result = assertInput(followUpDataSchema, parsed, 'Envelope de acompanhamento persistido inválido.');
  if (clientId && (result.pipeline.clientId !== clientId || result.items.some((item) => item.clientId !== clientId) || result.contributions?.some((item) => item.clientId !== clientId))) throw new RepositoryError('Acompanhamento pertence a outro cliente', 502, 'FOLLOW_UP_OWNERSHIP_INVALID');
  if (clientId && result.contributionOperations?.some((item) => item.clientId !== clientId)) throw new RepositoryError('Operação de aporte pertence a outro cliente', 502, 'CONTRIBUTION_OPERATION_OWNERSHIP_INVALID');
  const normalized = { ...result, contributions: result.contributions ?? [], contributionOperations: result.contributionOperations ?? [] };
  assertContributionLedgerIntegrity(normalized, clientId);
  return normalized;
}

function auditLog(value: unknown): AuditLog {
  const parsed = parseJson<Partial<AuditLog> | AuditEntry[] | null>(value, null);
  if (parsed === null || typeof parsed !== 'object') throw new RepositoryError('Auditoria persistida ausente ou inválida.', 502, 'AUDIT_LOG_INVALID');
  const candidate = Array.isArray(parsed) ? { version: DOCUMENT_VERSION, entries: parsed } : {
    version: Number(parsed.version),
    entries: parsed.entries,
  };
  const legacyActor = '00000000-0000-4000-8000-000000000000';
  const entries = Array.isArray(candidate.entries) ? candidate.entries : [];
  const candidateForSchema = {
    ...candidate,
    entries: entries.map((entry) => entry && typeof entry === 'object' && !Array.isArray(entry) && (entry as Record<string, unknown>).actorId === undefined
      ? { ...(entry as Record<string, unknown>), actorId: legacyActor }
      : entry),
  };
  const validated = auditLogSchema.safeParse(candidateForSchema);
  if (!validated.success) throw new RepositoryError('Auditoria persistida inválida.', 502, 'AUDIT_LOG_INVALID');
  return {
    ...validated.data,
    entries: validated.data.entries.map((entry, index) => entries[index] && typeof entries[index] === 'object' && !Array.isArray(entries[index]) && (entries[index] as Record<string, unknown>).actorId === undefined
      ? { ...entry, actorId: undefined }
      : entry),
  } as unknown as AuditLog;
}
function writableAuditLog(value: unknown): AuditLog {
  const log = auditLog(value);
  if (log.entries.some((entry) => entry.actorId === undefined)) {
    throw new RepositoryError('A auditoria legada sem autoria verificável não pode receber novas mutações.', 422, 'AUDIT_ACTOR_REQUIRED');
  }
  return log;
}

function normalizeScenario(value: PlanScenario | Record<string, unknown>): PlanScenario {
  const raw = value as Record<string, unknown>;
  const assumptions = raw.assumptions as ScenarioAssumptions | ScenarioAssumptionsRow | undefined;
  if (!assumptions) throw new Error('Cenário sem premissas');
  const embedded = 'retirementAge' in assumptions || 'baseYear' in assumptions || 'currentReserve' in assumptions
    ? normalizeEmbeddedAssumptions(assumptions as Record<string, unknown>)
    : assumptionsFromRow(assumptions as ScenarioAssumptionsRow);
  return assertInput(planScenarioSchema, {
    id: raw.id,
    planId: raw.planId ?? raw.plan_id,
    name: raw.name,
    isPrimary: persistedBoolean(raw.isPrimary ?? raw.is_primary, 'isPrimary'),
    visibleInComparison: persistedBoolean(raw.visibleInComparison ?? raw.visible_in_comparison, 'visibleInComparison'),
    version: persistedNumeric(raw.version, 'version'),
    assumptionsVersion: persistedNumeric(raw.assumptionsVersion ?? (embedded ? 1 : (assumptions as ScenarioAssumptionsRow).version), 'assumptionsVersion'),
    assumptions: embedded,
  }, 'Cenário persistido inválido.');
}

function normalizeGoal(value: ClientGoal | Record<string, unknown>): ClientGoal {
  const raw = value as Record<string, unknown>;
  const scenarioId = raw.scenarioId !== undefined ? raw.scenarioId : raw.scenario_id;
  return assertInput(clientGoalSchema, {
    id: raw.id,
    clientId: raw.clientId ?? raw.client_id,
    scenarioId: scenarioId === null || scenarioId === undefined ? null : scenarioId,
    title: raw.title,
    category: raw.category,
    priority: raw.priority,
    targetDate: raw.targetDate ?? raw.target_date,
    targetValue: moneyString((raw.targetValue ?? raw.target_value) as number | string),
    reservedValue: moneyString((raw.reservedValue ?? raw.reserved_value) as number | string),
    periodicity: raw.periodicity,
    status: raw.status,
    version: persistedNumeric(raw.version, 'version'),
    createdAt: raw.createdAt ?? raw.created_at,
    updatedAt: raw.updatedAt ?? raw.updated_at,
  }, 'Meta persistida inválida.');
}

function normalizeFinancialEntry(value: ClientFinancialEntry | Record<string, unknown>): ClientFinancialEntry {
  const raw = value as Record<string, unknown>;
  return assertInput(clientFinancialEntrySchema, {
    id: raw.id, clientId: raw.clientId ?? raw.client_id, kind: raw.kind, category: raw.category,
    title: raw.title, institution: raw.institution === null || raw.institution === undefined ? null : raw.institution,
    currentValue: moneyString((raw.currentValue ?? raw.current_value) as number | string),
    monthlyValue: moneyString((raw.monthlyValue ?? raw.monthly_value) as number | string),
    retirementMonthlyValue: moneyString((raw.retirementMonthlyValue ?? raw.retirement_monthly_value) as number | string),
    liquidity: raw.liquidity, periodicity: raw.periodicity, active: persistedBoolean(raw.active ?? raw.is_active, 'active'), version: persistedNumeric(raw.version, 'version'),
    createdAt: raw.createdAt ?? raw.created_at, updatedAt: raw.updatedAt ?? raw.updated_at,
  }, 'Registro patrimonial persistido inválido.');
}

function normalizeProtection(value: ClientProtectionNeed | Record<string, unknown>): ClientProtectionNeed {
  const raw = value as Record<string, unknown>;
  const pick = (...keys: string[]) => keys.map((key) => raw[key]).find((item) => item !== undefined);
  const money = (...keys: string[]) => moneyString(pick(...keys) as number | string);
  return assertInput(protectionNeedSchema, {
    id: raw.id, clientId: raw.clientId ?? raw.client_id,
    monthlyLivingExpenses: money('monthlyLivingExpenses', 'monthly_living_expenses'), horizonYears: persistedNumeric(pick('horizonYears', 'horizon_years'), 'horizonYears'),
    dependents: persistedNumeric(raw.dependents, 'dependents'), estateBaseValue: money('estateBaseValue', 'estate_base_value'),
    legalFeesPct: persistedNumeric(pick('legalFeesPct', 'legal_fees_pct'), 'legalFeesPct'), courtCostsPct: persistedNumeric(pick('courtCostsPct', 'court_costs_pct'), 'courtCostsPct'),
    inheritanceTaxPct: persistedNumeric(pick('inheritanceTaxPct', 'inheritance_tax_pct'), 'inheritanceTaxPct'), emergencyMonths: persistedNumeric(pick('emergencyMonths', 'emergency_months'), 'emergencyMonths'),
    currentGuarantees: money('currentGuarantees', 'current_guarantees'), guaranteesMode: pick('guaranteesMode', 'guarantees_mode') ?? 'manual', guaranteesScenarioId: pick('guaranteesScenarioId', 'guarantees_scenario_id') ?? null, wholeLifeInsuredCapital: money('wholeLifeInsuredCapital', 'whole_life_insured_capital'),
    deductiblePrivatePension: money('deductiblePrivatePension', 'deductible_private_pension'), realAnnualRatePct: persistedNumeric(pick('realAnnualRatePct', 'real_annual_rate_pct'), 'realAnnualRatePct'),
    totalExpenses: money('totalExpenses', 'total_expenses'), emergencyReserve: money('emergencyReserve', 'emergency_reserve'), successionNeed: money('successionNeed', 'succession_need'),
    recommendedCapital: money('recommendedCapital', 'recommended_capital'), coverageGap: money('coverageGap', 'coverage_gap'),
    ...(pick('sourceRevisionFingerprint', 'source_revision_fingerprint') !== undefined
      ? { sourceRevisionFingerprint: pick('sourceRevisionFingerprint', 'source_revision_fingerprint') }
      : {}),
    version: persistedNumeric(raw.version, 'version'), updatedAt: raw.updatedAt ?? raw.updated_at,
  }, 'Diagnóstico de proteção persistido inválido.');
}

async function sha256(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalJson(value)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
async function deterministicUuid(value: unknown): Promise<string> {
  const digest = await sha256(value);
  const variant = ((parseInt(digest.slice(16, 18), 16) & 0x3f) | 0x80).toString(16).padStart(2, '0');
  return `${digest.slice(0, 8)}-${digest.slice(8, 12)}-4${digest.slice(13, 16)}-${variant}${digest.slice(18, 20)}-${digest.slice(20, 32)}`;
}
async function verifyCalculationSnapshotHash(snapshot: CalculationSnapshot): Promise<void> {
  const expected = await sha256(calculationSnapshotHashPayload(snapshot));
  if (snapshot.sha256 !== expected) throw new RepositoryError('Snapshot de cálculo persistido não passou na verificação criptográfica.', 502, 'CALCULATION_HASH_INVALID');
}
async function verifyReportSnapshotHash(snapshot: ReportSnapshot): Promise<void> {
  const base = Object.fromEntries(Object.entries(snapshot).filter(([key]) => key !== 'sha256'));
  const expected = await sha256(base);
  if (snapshot.sha256 !== expected) throw new RepositoryError('Snapshot de relatório persistido não passou na verificação criptográfica.', 502, 'REPORT_HASH_INVALID');
}

function serializeRetirementOutput(input: ScenarioAssumptions): SerializedCalendarResult {
  const rules = input.cashflowRules ? mapPlannedCashflowRulesToEngine(input.cashflowRules, { currentYear: input.baseYear, currentMonth: input.baseMonth, currentAge: input.currentAge, lifeExpectancyAge: input.lifeExpectancyAge }, { monthly: MonthlyCashflowRule, annual: AnnualCashflowRule }) : undefined;
  return serializeCalendarResult(simulateCalendar({
    currentYear: input.baseYear, currentMonth: input.baseMonth, currentAge: input.currentAge, retirementAge: input.retirementAge,
    lifeExpectancyAge: input.lifeExpectancyAge, currentReserve: input.currentReserve, nominalAnnualPct: input.nominalAnnualRatePct,
    inflationAnnualPct: input.inflationAnnualRatePct, plannedMonthlyContributionBeforeRetirement: input.monthlyContribution,
    desiredMonthlyWithdrawalAfterRetirement: input.desiredMonthlyIncome, differentiatePostRetirementRates: input.differentiatePostRetirementRates,
    postRetirementNominalAnnualPct: input.postRetirementNominalAnnualRatePct ?? null,
    postRetirementInflationAnnualPct: input.postRetirementInflationAnnualRatePct ?? null,
    ...(rules ?? {}),
  }));
}

async function verifyCalculationSnapshotDerivation(snapshot: CalculationSnapshot): Promise<void> {
  if (canonicalJson(serializeRetirementOutput(snapshot.input)) !== canonicalJson(snapshot.output)) {
    throw new RepositoryError('Snapshot de cálculo persistido não corresponde ao motor canônico.', 502, 'CALCULATION_DERIVATION_INVALID');
  }
}
async function verifyCalculationSnapshots(snapshots: readonly CalculationSnapshot[]): Promise<void> {
  for (const snapshot of snapshots) {
    await verifyCalculationSnapshotHash(snapshot);
    await verifyCalculationSnapshotDerivation(snapshot);
  }
}
async function verifyCalculationHistoryIdempotency(history: CalculationHistory, clientId: string): Promise<void> {
  const seen = new Set<string>();
  const indexedSnapshotIds = new Set<string>();
  for (const entry of history.idempotency) {
    const key = canonicalIdempotencyKey(entry.key);
    if (seen.has(key)) throw new RepositoryError('Histórico de cálculo contém chaves de idempotência duplicadas.', 502, 'INVALID_CALCULATION_HISTORY');
    seen.add(key);
    const snapshot = history.snapshots.find((item) => item.id === entry.snapshotId);
    if (!snapshot || snapshot.clientId !== clientId || canonicalIdempotencyKey(snapshot.idempotencyKey ?? '') !== key) throw new RepositoryError('Índice de idempotência não corresponde ao snapshot de cálculo.', 502, 'INVALID_CALCULATION_HISTORY');
    if (indexedSnapshotIds.has(snapshot.id)) throw new RepositoryError('Índice de idempotência aponta mais de uma vez para o mesmo snapshot.', 502, 'INVALID_CALCULATION_HISTORY');
    indexedSnapshotIds.add(snapshot.id);
    const requestHash = await sha256({ clientId: snapshot.clientId, scenarioId: snapshot.scenarioId, input: snapshot.input });
    if (requestHash !== entry.requestHash) throw new RepositoryError('Hash de idempotência não corresponde ao snapshot de cálculo.', 502, 'INVALID_CALCULATION_HISTORY');
  }
  const snapshotKeys = new Set<string>();
  for (const snapshot of history.snapshots) {
    if (snapshot.idempotencyKey === undefined) continue;
    const key = canonicalIdempotencyKey(snapshot.idempotencyKey);
    if (snapshotKeys.has(key)) throw new RepositoryError('Snapshots de cálculo contêm chaves de idempotência duplicadas.', 502, 'INVALID_CALCULATION_HISTORY');
    snapshotKeys.add(key);
    const matchingEntries = history.idempotency.filter((entry) => canonicalIdempotencyKey(entry.key) === key && entry.snapshotId === snapshot.id);
    if (matchingEntries.length !== 1) throw new RepositoryError('Snapshot de cálculo com chave de idempotência não possui vínculo único no índice.', 502, 'INVALID_CALCULATION_HISTORY');
  }
}
async function verifyReportSnapshots(reports: readonly ReportSnapshot[]): Promise<void> {
  for (const report of reports) await verifyReportSnapshotHash(report);
}
async function verifyLegacyReportSemantics(report: ReportSnapshot, current: RawClient, clientId: string, familyMembers: readonly FamilyMember[]): Promise<void> {
  if (!report.sourceCalculationId || !report.sourceRevisionFingerprint || !report.details) throw new RepositoryError('Snapshot de relatório sem cálculo-fonte, fingerprint ou detalhes completos.', 502, 'REPORT_SOURCE_REQUIRED');
  const planning = planningData(current.planning_data, clientId);
  await assertReportGuaranteeBindings(planning, report.scenarioId);
  const followUp = followUpData(current.follow_up_data, clientId);
  await verifyContributionOperationHashes(followUp);
  const scenario = planning.scenarios.find((item) => item.id === report.scenarioId);
  if (!scenario) throw new RepositoryError('Snapshot legado referencia cenário inexistente.', 502, 'REPORT_SOURCE_STALE');
  const history = calculationHistory(current.calculation_history, clientId);
  await verifyCalculationSnapshots(history.snapshots);
  await verifyCalculationHistoryIdempotency(history, clientId);
  const source = history.snapshots.find((item) => item.id === report.sourceCalculationId);
  if (!source || source.clientId !== clientId || source.scenarioId !== report.scenarioId) {
    throw new RepositoryError('Snapshot legado referencia cálculo-fonte ausente ou de outro cliente.', 502, 'CALCULATION_OWNERSHIP_INVALID');
  }
  if (canonicalJson(source.input) !== canonicalJson(scenario.assumptions)) {
    throw new RepositoryError('Snapshot legado usa cálculo-fonte obsoleto em relação ao cenário.', 409, 'REPORT_SOURCE_STALE');
  }
  const familyRevision = familyMembers.map((member) => ({ id: member.id, version: member.version }));
  const currentPayload = await reportSourceRevisionPayloadForClient(planning, followUp, report.scenarioId, followUp.contributions, familyRevision);
  const expectedFingerprint = await sha256(currentPayload);
  const legacyPayload = { ...currentPayload } as Record<string, unknown>;
  delete legacyPayload.exclusions;
  const legacyFingerprint = planning.exclusions.length === 0 ? await sha256(legacyPayload) : null;
  if (report.sourceRevisionFingerprint !== expectedFingerprint && report.sourceRevisionFingerprint !== legacyFingerprint) {
    throw new RepositoryError('Fingerprint do snapshot legado não corresponde às fontes atuais.', 409, 'REPORT_SOURCE_FINGERPRINT_MISMATCH');
  }
  const expectedDetails = reportDetailsForClient(planning, followUp, report.scenarioId, followUp.contributions, familyMembers);
  if (!report.details || canonicalJson(report.details) !== canonicalJson(expectedDetails)) {
    throw new RepositoryError('Detalhes do snapshot legado não correspondem às fontes atuais.', 409, 'REPORT_SOURCE_DETAILS_STALE');
  }
  const expectedSummary = reportSummaryForClient(planning, followUp, report.scenarioId, source.output, followUp.contributions);
  for (const [key, value] of Object.entries(report.summary)) {
    if (value !== undefined && value !== expectedSummary[key as keyof typeof expectedSummary]) {
      throw new RepositoryError('Resumo do snapshot legado não corresponde às fontes atuais.', 409, 'REPORT_SUMMARY_MISMATCH');
    }
  }
}
async function verifyLegacyReportSnapshots(reports: readonly ReportSnapshot[], current: RawClient, clientId: string, familyMembers: readonly FamilyMember[]): Promise<void> {
  for (const report of reports) await verifyLegacyReportSemantics(report, current, clientId, familyMembers);
}
const moneyString = (value: number | string): string => {
  if (typeof value === 'number') return moneyFromNumber(value);
  if (!/^-?(0|[1-9]\d*)\.\d{2}$/.test(value) || value === '-0.00') throw new RepositoryError('Valor financeiro persistido não está em centavos exatos', 502, 'INVALID_MONEY_READ');
  return value;
};
const calculatedMoney = (value: number | { toFixed(decimalPlaces: number): string }): string => {
  const text = typeof value === 'number'
    ? (() => {
      try {
        return roundMoneyText(value);
      } catch {
        throw new RepositoryError('Resultado financeiro fora do limite seguro', 502, 'CALCULATED_MONEY_OVERFLOW');
      }
    })()
    : value.toFixed(2);
  if (!/^-?\d+\.\d{2}$/.test(text)) throw new RepositoryError('Resultado financeiro inválido', 502, 'CALCULATED_MONEY_INVALID');
  const safe = moneySchema.safeParse(text);
  if (!safe.success) throw new RepositoryError('Resultado financeiro fora do limite seguro', 502, 'CALCULATED_MONEY_OVERFLOW');
  return safe.data;
};

function assertContributionCreateResponse(item: ClientContribution, input: CreateContributionInput): ClientContribution {
  const expectedAmount = moneyString(input.plannedAmount);
  if (item.clientId !== input.clientId || item.goalId !== input.goalId || item.resource !== input.resource.trim() || item.dueDate !== input.dueDate ||
    item.plannedAmount !== expectedAmount || item.note !== input.note || item.status !== 'pending' || item.actualAmount !== null ||
    item.realizedAt !== null || (item.settlementNote !== undefined && item.settlementNote !== null) || item.version !== 1) {
    throw new RepositoryError('Resposta de planejamento não corresponde ao comando enviado.', 502, 'CONTRIBUTION_RESPONSE_MISMATCH');
  }
  return item;
}

function assertContributionCanSettle(item: ClientContribution, input: SettleContributionInput, expectedVersion: number): void {
  if (item.version !== expectedVersion || item.status !== 'pending') throw new RepositoryError('Aporte alterado ou encerrado; recarregue os dados', 409, 'VERSION_CONFLICT');
  if (input.status === 'realized') {
    const today = new Date().toISOString().slice(0, 10);
    const createdDate = item.createdAt.slice(0, 10);
    if (input.realizedAt! < createdDate || input.realizedAt! > today) throw new RepositoryError('A data de realização deve estar entre a criação e hoje', 409, 'CONTRIBUTION_DATE_INVALID');
  }
}

function assertContributionPlanReceipt(item: ClientContribution, clientId: string, contributionId: string): ClientContribution {
  const receipt = assertInput(clientContributionSchema, item, 'Recibo de planejamento de aporte inválido.');
  if (receipt.clientId !== clientId || receipt.id !== contributionId || receipt.status !== 'pending' || receipt.version !== 1 || receipt.actualAmount !== null || receipt.realizedAt !== null || receipt.settlementNote !== null) {
    throw new RepositoryError('O recibo inicial do planejamento está inconsistente.', 502, 'CONTRIBUTION_RECONCILE_RESPONSE_MISMATCH');
  }
  return receipt;
}

function assertContributionSettleResponse(item: ClientContribution, original: ClientContribution, clientId: string, contributionId: string, input: SettleContributionInput, expectedVersion: number): ClientContribution {
  const expectedAmount = input.status === 'realized' ? moneyString(input.actualAmount!) : null;
  const expectedDate = input.status === 'realized' ? input.realizedAt : null;
  const immutableFieldsMatch = item.clientId === clientId && item.id === contributionId && item.goalId === original.goalId && item.resource === original.resource && item.dueDate === original.dueDate
    && item.plannedAmount === original.plannedAmount && item.note === original.note;
  const responseFieldsMatch = item.status === input.status && item.actualAmount === expectedAmount && item.realizedAt === expectedDate && (item.settlementNote ?? null) === input.note;
  const freshSettlement = original.version === expectedVersion && original.status === 'pending' && item.version === expectedVersion + 1;
  const terminalReplay = original.version === expectedVersion + 1 && original.status === input.status && item.version === original.version;
  if (original.clientId !== clientId || original.id !== contributionId || !immutableFieldsMatch || !responseFieldsMatch || !(freshSettlement || terminalReplay)) {
    throw new RepositoryError('Resposta de realização não corresponde ao comando enviado.', 502, 'CONTRIBUTION_RESPONSE_MISMATCH');
  }
  return item;
}

function assertContributionSettleReceipt(receipt: ClientContribution, current: ClientContribution, clientId: string, contributionId: string): void {
  const parsed = assertInput(clientContributionSchema, receipt, 'Recibo de settlement de aporte inválido.');
  if (parsed.clientId !== clientId || parsed.id !== contributionId || parsed.status === 'pending' || canonicalJson(parsed) !== canonicalJson(current)) {
    throw new RepositoryError('O recibo de settlement não corresponde ao estado persistido.', 502, 'CONTRIBUTION_LEDGER_INVALID');
  }
}

function assertContributionLedgerIntegrity(document: FollowUpData, clientId?: string): void {
  const contributionById = new Map<string, ClientContribution>();
  for (const contribution of document.contributions) {
    if (contributionById.has(contribution.id)) throw new RepositoryError('Ledger contém aportes duplicados.', 502, 'CONTRIBUTION_LEDGER_INVALID');
    contributionById.set(contribution.id, contribution);
  }
  const operationKeys = new Set<string>();
  const createContributionIds = new Set<string>();
  const settleContributionIds = new Set<string>();
  const createReceipts = new Map<string, ClientContribution>();
  for (const operation of document.contributionOperations) {
    const canonicalKey = operation.key.toLowerCase();
    const actorScopedKey = `${operation.actorId}:${canonicalKey}`;
    if (operationKeys.has(actorScopedKey)) throw new RepositoryError('Ledger contém chaves de idempotência duplicadas para o mesmo ator.', 502, 'CONTRIBUTION_LEDGER_INVALID');
    operationKeys.add(actorScopedKey);
    if (clientId !== undefined && operation.clientId !== clientId) throw new RepositoryError('Operação de aporte pertence a outro cliente.', 502, 'CONTRIBUTION_OPERATION_OWNERSHIP_INVALID');
    const contribution = contributionById.get(operation.contributionId);
    if (!contribution || contribution.clientId !== operation.clientId) throw new RepositoryError('Operação de aporte aponta para registro inexistente ou de outro cliente.', 502, 'CONTRIBUTION_LEDGER_INVALID');
    if (operation.kind === 'create') {
      if (createContributionIds.has(operation.contributionId)) throw new RepositoryError('Ledger contém mais de um planejamento para o mesmo aporte.', 502, 'CONTRIBUTION_LEDGER_INVALID');
      createContributionIds.add(operation.contributionId);
      const receipt = assertContributionPlanReceipt(operation.receipt, operation.clientId, operation.contributionId);
      createReceipts.set(operation.contributionId, receipt);
      if (receipt.clientId !== contribution.clientId || receipt.id !== contribution.id || receipt.goalId !== contribution.goalId || receipt.resource !== contribution.resource || receipt.dueDate !== contribution.dueDate || receipt.plannedAmount !== contribution.plannedAmount || receipt.note !== contribution.note || receipt.createdAt !== contribution.createdAt) {
        throw new RepositoryError('Recibo inicial não corresponde aos campos imutáveis do aporte.', 502, 'CONTRIBUTION_LEDGER_INVALID');
      }
    } else {
      if (settleContributionIds.has(operation.contributionId)) throw new RepositoryError('Ledger contém mais de um settlement para o mesmo aporte.', 502, 'CONTRIBUTION_LEDGER_INVALID');
      settleContributionIds.add(operation.contributionId);
      assertContributionSettleReceipt(operation.receipt, contribution, operation.clientId, operation.contributionId);
    }
  }
  if (createContributionIds.size !== document.contributions.length) throw new RepositoryError('Ledger contém aporte sem recibo de planejamento.', 502, 'CONTRIBUTION_LEDGER_INVALID');
  if (document.contributions.some((contribution) => contribution.status !== 'pending' && !settleContributionIds.has(contribution.id))) throw new RepositoryError('Ledger contém aporte terminal sem recibo de settlement.', 502, 'CONTRIBUTION_LEDGER_INVALID');
  for (const operation of document.contributionOperations.filter((item) => item.kind === 'settle')) {
    const createReceipt = createReceipts.get(operation.contributionId);
    if (!createReceipt || operation.receipt.version !== createReceipt.version + 1 || operation.receipt.clientId !== createReceipt.clientId || operation.receipt.goalId !== createReceipt.goalId || operation.receipt.resource !== createReceipt.resource || operation.receipt.dueDate !== createReceipt.dueDate || operation.receipt.plannedAmount !== createReceipt.plannedAmount || operation.receipt.note !== createReceipt.note || operation.receipt.createdAt !== createReceipt.createdAt || Date.parse(operation.receipt.updatedAt) < Date.parse(createReceipt.createdAt)) {
      throw new RepositoryError('A cadeia de recibos do aporte é inconsistente.', 502, 'CONTRIBUTION_LEDGER_INVALID');
    }
  }
}

async function verifyContributionOperationHashes(document: FollowUpData): Promise<void> {
  for (const operation of document.contributionOperations) {
    const receipt = operation.receipt;
    const input = operation.kind === 'create'
      ? { clientId: receipt.clientId, goalId: receipt.goalId, resource: receipt.resource, dueDate: receipt.dueDate, plannedAmount: Number(receipt.plannedAmount), note: receipt.note }
      : receipt.status === 'realized'
        ? { status: 'realized' as const, actualAmount: Number(receipt.actualAmount), realizedAt: receipt.realizedAt!, note: receipt.settlementNote! }
        : { status: 'not_realized' as const, note: receipt.settlementNote! };
    const expected = operation.kind === 'create'
      ? await sha256({ kind: 'create', clientId: operation.clientId, input })
      : await sha256({ kind: 'settle', clientId: operation.clientId, contributionId: operation.contributionId, expectedVersion: receipt.version - 1, input });
    if (expected !== operation.requestHash) throw new RepositoryError('Hash da operação de aporte não corresponde ao recibo persistido.', 502, 'CONTRIBUTION_LEDGER_INVALID');
  }
}

async function verifiedFollowUpData(value: unknown, clientId: string): Promise<FollowUpData> {
  const document = followUpData(value, clientId);
  await verifyContributionOperationHashes(document);
  return document;
}

function assertActiveDirectusMutationClient(current: RawClient): void {
  if (current.status === 'archived') throw new RepositoryError('Clientes arquivados não podem receber novas mutações no modo Directus-only.', 409, 'CLIENT_NOT_ACTIVE');
}

function contributionActorId(sessionUserId?: string | null): string {
  if (!sessionUserId) return SYNTHETIC_ACTOR_ID;
  const value = sessionUserId.trim().toLowerCase();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)) throw new RepositoryError('A sessão possui um ator inválido para operações financeiras.', 401, 'SESSION_EXPIRED');
  return value;
}

function sumMoneyStrings(values: readonly string[]): string {
  const cents = values.reduce((total, value) => {
    const match = /^(-?)(0|[1-9]\d*)\.(\d{2})$/.exec(value);
    if (!match) throw new RepositoryError('Valor financeiro de relatório inválido', 422, 'REPORT_GOALS_MISMATCH');
    if (match[1] === '-' && match[2] === '0' && match[3] === '00') throw new RepositoryError('Valor financeiro de relatório inválido', 422, 'REPORT_GOALS_MISMATCH');
    const amount = BigInt(match[2]!) * 100n + BigInt(match[3]!);
    return total + (match[1] === '-' ? -amount : amount);
  }, 0n);
  const sign = cents < 0n ? '-' : '';
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

function negateMoneyString(value: string): string {
  return value === '0.00' ? '0.00' : `-${value}`;
}

function reportGoalFundingAmount(goal: ClientGoal, assumptions: ScenarioAssumptions): string {
  const target = new Date(`${goal.targetDate}T00:00:00Z`);
  const months = Number.isNaN(target.getTime()) ? 0 : Math.max(0, (target.getUTCFullYear() - assumptions.baseYear) * 12 + target.getUTCMonth() + 1 - assumptions.baseMonth);
  const annualRealRate = realAnnualRate(assumptions.nominalAnnualRatePct, assumptions.inflationAnnualRatePct);
  const monthlyRate = monthlyEffectiveRate(annualRealRate);
  const targetValue = Number(goal.targetValue);
  const reservedValue = Number(goal.reservedValue);
  const accumulatedAtGoal = futureValueLump(reservedValue, monthlyRate, months);
  const pendingAtGoal = targetValue - accumulatedAtGoal;
  const interval = goal.periodicity === 'annual' ? 12 : 1;
  if (goal.periodicity === 'one-time') return moneyString(Math.max(0, Number(pendingAtGoal.toFixed(2))));
  if (months === 0) return moneyString(Math.max(0, Number(pendingAtGoal.toFixed(2))));
  const contributionMonths = Math.floor(months / interval) * interval;
  const postAccumulationMonths = months - contributionMonths;
  if (contributionMonths === 0) return moneyString(pendingAtGoal > 0 ? presentValueLump(pendingAtGoal, monthlyRate, months) : 0);
  return moneyString(Number(goalPeriodicContribution(targetValue, reservedValue, annualRealRate, contributionMonths, postAccumulationMonths, interval).periodicContributionDue.toFixed(2)));
}

function divideMoneyString(value: string, divisor: bigint): string {
  const match = /^(-?)(\d+)\.(\d{2})$/.exec(value);
  if (!match || divisor <= 0n) throw new RepositoryError('Valor monetário inválido na derivação do relatório.', 422, 'REPORT_SUMMARY_INVALID');
  const sign = match[1] === '-' ? -1n : 1n;
  const cents = sign * (BigInt(match[2]!) * 100n + BigInt(match[3]!));
  const absolute = cents < 0n ? -cents : cents;
  let quotient = absolute / divisor;
  if (absolute % divisor * 2n >= divisor) quotient += 1n;
  const signed = sign < 0n ? -quotient : quotient;
  const outputSign = signed < 0n ? '-' : '';
  const positive = signed < 0n ? -signed : signed;
  return `${outputSign}${positive / 100n}.${String(positive % 100n).padStart(2, '0')}`;
}

function reportMonthlyEquivalent(entries: ReadonlyArray<{ monthlyValue: string; periodicity: 'monthly' | 'annual' | 'one-time'; retirementMonthlyValue?: string }>, useRetirementValue = false): string {
  const amount = (entry: typeof entries[number]) => useRetirementValue && entry.retirementMonthlyValue !== undefined ? entry.retirementMonthlyValue : entry.monthlyValue;
  const monthly = sumMoneyStrings(entries.filter((entry) => entry.periodicity === 'monthly').map(amount));
  const annual = sumMoneyStrings(entries.filter((entry) => entry.periodicity === 'annual').map(amount));
  return sumMoneyStrings([monthly, divideMoneyString(annual, 12n)]);
}

export function reportSummaryForClient(
  planning: PlanningData,
  followUp: FollowUpData,
  scenarioId: string,
  retirement: Pick<CalculationSnapshot['output'], 'retirementReserve' | 'consume'>,
  contributions = followUp.contributions,
): ReportSummary {
  const scenario = planning.scenarios.find((item) => item.id === scenarioId);
  if (!scenario) throw new RepositoryError('O cenário do relatório não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
  const exclusions = planning.exclusions ?? [];
  const activeGoals = planning.goals.filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === scenarioId));
  const goalFunding = activeGoals.map((goal) => ({ goal, amount: reportGoalFundingAmount(goal, scenario.assumptions) }));
  const monthlyGoalFunding = sumMoneyStrings(goalFunding.filter(({ goal }) => goal.periodicity === 'monthly').map(({ amount }) => amount));
  const annualGoalFunding = sumMoneyStrings(goalFunding.filter(({ goal }) => goal.periodicity === 'annual').map(({ amount }) => amount));
  const oneTimeGoalFunding = sumMoneyStrings(goalFunding.filter(({ goal }) => goal.periodicity === 'one-time').map(({ amount }) => amount));
  const fundingBasis = new Set(activeGoals.map((goal) => goal.periodicity)).size > 1 ? 'mixed-periodicity' : 'single-periodicity';
  const financials = planning.financials.filter((entry) => entry.active);
  const assets = financials.filter((entry) => entry.kind === 'asset');
  const liabilities = financials.filter((entry) => entry.kind === 'liability');
  const incomes = financials.filter((entry) => entry.kind === 'income');
  const excludedExpenseIds = new Set(resolveExcludedTargetIds(exclusions, scenarioId, 'expense'));
  const expenses = financials.filter((entry) => entry.kind === 'expense' && !excludedExpenseIds.has(entry.id));
  const liquidAssets = assets.filter((entry) => entry.liquidity === 'immediate' || entry.liquidity === 'short_term');
  const pending = contributions.filter((item) => item.status === 'pending');
  return {
    retirementReserve: retirement.retirementReserve,
    additionalMonthly: retirement.consume.additionalMonthly,
    activeGoals: activeGoals.length,
    totalGoalValue: sumMoneyStrings(activeGoals.map((goal) => goal.targetValue)),
    ...(fundingBasis === 'single-periodicity' ? { totalGoalFunding: sumMoneyStrings([monthlyGoalFunding, annualGoalFunding, oneTimeGoalFunding]) } : {}),
    monthlyGoalFunding,
    annualGoalFunding,
    oneTimeGoalFunding,
    fundingBasis,
    netWorth: sumMoneyStrings([sumMoneyStrings(assets.map((entry) => entry.currentValue)), negateMoneyString(sumMoneyStrings(liabilities.map((entry) => entry.currentValue)))]),
    liquidity: sumMoneyStrings(liquidAssets.map((entry) => entry.currentValue)),
    monthlyIncome: reportMonthlyEquivalent(incomes, true),
    monthlyExpense: reportMonthlyEquivalent(expenses, true),
    plannedContributions: sumMoneyStrings(pending.map((item) => item.plannedAmount)),
    pendingContributions: pending.length,
    protectionGap: planning.protection?.coverageGap ?? '0.00',
    recommendedProtectionCapital: planning.protection?.recommendedCapital ?? '0.00',
    pensionCount: planning.r5.pensionSimulations.length,
    financingCount: planning.r5.financingSimulations.length,
  };
}

function validateReportGoalSummary(input: CreateReportSnapshotInput, planning: PlanningData): void {
  if (!input.sourceCalculationId && !input.goalFunding) return;
  const activeGoals = planning.goals.filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === input.scenarioId));
  const totalGoalValue = sumMoneyStrings(activeGoals.map((goal) => goal.targetValue));
  const expectedByPeriodicity = {
    monthly: sumMoneyStrings(activeGoals.filter((goal) => goal.periodicity === 'monthly').map((goal) => reportGoalFundingAmount(goal, planning.scenarios.find((scenario) => scenario.id === input.scenarioId)!.assumptions))),
    annual: sumMoneyStrings(activeGoals.filter((goal) => goal.periodicity === 'annual').map((goal) => reportGoalFundingAmount(goal, planning.scenarios.find((scenario) => scenario.id === input.scenarioId)!.assumptions))),
    oneTime: sumMoneyStrings(activeGoals.filter((goal) => goal.periodicity === 'one-time').map((goal) => reportGoalFundingAmount(goal, planning.scenarios.find((scenario) => scenario.id === input.scenarioId)!.assumptions))),
  };
  const fundingBasis = new Set(activeGoals.map((goal) => goal.periodicity)).size > 1 ? 'mixed-periodicity' : 'single-periodicity';
  const totalGoalFunding = fundingBasis === 'single-periodicity' ? sumMoneyStrings([expectedByPeriodicity.monthly, expectedByPeriodicity.annual, expectedByPeriodicity.oneTime]) : undefined;
  if (input.summary.activeGoals !== activeGoals.length || input.summary.totalGoalValue !== totalGoalValue || input.summary.totalGoalFunding !== totalGoalFunding ||
      (input.summary.monthlyGoalFunding !== undefined && input.summary.monthlyGoalFunding !== expectedByPeriodicity.monthly) ||
      (input.summary.annualGoalFunding !== undefined && input.summary.annualGoalFunding !== expectedByPeriodicity.annual) ||
      (input.summary.oneTimeGoalFunding !== undefined && input.summary.oneTimeGoalFunding !== expectedByPeriodicity.oneTime) ||
      (input.summary.fundingBasis !== undefined && input.summary.fundingBasis !== fundingBasis) ||
      (fundingBasis === 'mixed-periodicity' && input.summary.fundingBasis !== fundingBasis)) {
    throw new RepositoryError('Resumo de metas não corresponde ao cenário persistido.', 422, 'REPORT_GOALS_MISMATCH');
  }
  const funding = input.goalFunding ?? [];
  const seen = new Set<string>();
  for (const item of funding) {
    if (seen.has(item.goalId)) throw new RepositoryError('Funding de relatório contém meta duplicada.', 422, 'REPORT_GOALS_MISMATCH');
    seen.add(item.goalId);
    const goal = activeGoals.find((candidate) => candidate.id === item.goalId);
    if (!goal || goal.title !== item.title || goal.category !== item.category || goal.periodicity !== item.periodicity || item.amount !== reportGoalFundingAmount(goal, planning.scenarios.find((scenario) => scenario.id === input.scenarioId)!.assumptions)) {
      throw new RepositoryError('Funding de relatório não corresponde às metas do cenário.', 422, 'REPORT_GOALS_MISMATCH');
    }
  }
  if (seen.size !== activeGoals.length) throw new RepositoryError('Funding de relatório não cobre todas as metas ativas.', 422, 'REPORT_GOALS_MISMATCH');
}

function sortedReportRevisionSources(items: ReadonlyArray<{ id: string; version: number }>) {
  return items.map(({ id, version }) => ({ id, version })).sort((left, right) => left.id.localeCompare(right.id));
}

export async function reportSourceRevisionPayloadForClient(planning: PlanningData, followUp: FollowUpData, scenarioId: string, contributions = followUp.contributions, familyRevision?: ReadonlyArray<{ id: string; version: number }>) {
  const scenario = planning.scenarios.find((item) => item.id === scenarioId);
  if (!scenario) throw new RepositoryError('O cenário do relatório não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
  if (!protectionGuaranteesScenarioMatchesReport(planning.protection, scenarioId)) throw new RepositoryError('O diagnóstico canônico de proteção pertence a outro cenário.', 409, 'PROTECTION_SCENARIO_MISMATCH');
  const exclusions = planning.exclusions ?? [];
  const excludedExpenseIds = new Set(resolveExcludedTargetIds(exclusions, scenarioId, 'expense'));
  const activeGoals = planning.goals.filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === scenarioId));
  const availableGuaranteeExposures = resolveEffectiveGuaranteeExposures(planning.guaranteeExposures ?? [], scenarioId);
  const effectiveGuaranteeExposures = resolveEffectiveGuaranteeExposures(planning.guaranteeExposures ?? [], scenarioId, exclusions);
  if (planning.protection?.guaranteesMode === 'canonical') {
    const kinds = new Set(availableGuaranteeExposures.map((exposure) => exposure.kind));
    if (availableGuaranteeExposures.length !== GUARANTEE_EXPOSURE_KINDS.length || kinds.size !== GUARANTEE_EXPOSURE_KINDS.length) throw new RepositoryError('O relatório está bloqueado por cardinalidade incompleta de garantias canônicas.', 409, 'GUARANTEE_BINDING_PENDING');
    if (availableGuaranteeExposures.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) throw new RepositoryError('O relatório está bloqueado por exposição de garantia sem binding verificado.', 409, 'GUARANTEE_BINDING_PENDING');
    await assertGuaranteeBindingReceipts(availableGuaranteeExposures);
  }
  const payload = {
    scenario: { id: scenario.id, version: scenario.version, assumptionsVersion: scenario.assumptionsVersion },
    goals: sortedReportRevisionSources(activeGoals),
    financials: sortedReportRevisionSources(planning.financials.filter((entry) => entry.active && (entry.kind !== 'expense' || !excludedExpenseIds.has(entry.id)))),
    guaranteeExposures: sortedReportRevisionSources(effectiveGuaranteeExposures),
    exclusions: canonicalPlanningExclusions(exclusions),
    protection: planning.protection ? { id: planning.protection.id, version: planning.protection.version } : null,
    pensions: sortedReportRevisionSources(planning.r5.pensionSimulations),
    financings: sortedReportRevisionSources(planning.r5.financingSimulations),
    financingComparisons: sortedReportRevisionSources(planning.r5.financingComparisons),
    contributions: sortedReportRevisionSources(contributions),
    followUp: sortedReportRevisionSources(followUp.items),
    pipelineVersion: followUp.pipeline.version,
  };
  return familyRevision === undefined ? payload : { ...payload, family: sortedReportRevisionSources(familyRevision) };
}

export function reportDetailsForClient(planning: PlanningData, followUp: FollowUpData, scenarioId: string, contributions = followUp.contributions, familyMembers?: ReadonlyArray<FamilyMember>) {
  if (!protectionGuaranteesScenarioMatchesReport(planning.protection, scenarioId)) throw new RepositoryError('O diagnóstico canônico de proteção pertence a outro cenário.', 409, 'PROTECTION_SCENARIO_MISMATCH');
  const activeGoals = planning.goals.filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === scenarioId));
  const exclusions = planning.exclusions ?? [];
  const excludedExpenseIds = new Set(resolveExcludedTargetIds(exclusions, scenarioId, 'expense'));
  return {
    goals: activeGoals.map((goal) => ({ id: goal.id, title: goal.title, targetValue: goal.targetValue, reservedValue: goal.reservedValue, periodicity: goal.periodicity, status: goal.status })),
    financials: planning.financials.filter((entry) => entry.active && (entry.kind !== 'expense' || !excludedExpenseIds.has(entry.id))).map((entry) => ({ id: entry.id, title: entry.title, kind: entry.kind, institution: entry.institution, currentValue: entry.currentValue, monthlyValue: entry.monthlyValue, retirementMonthlyValue: entry.retirementMonthlyValue, liquidity: entry.liquidity, periodicity: entry.periodicity })),
    guarantees: resolveEffectiveGuaranteeExposures(planning.guaranteeExposures ?? [], scenarioId, exclusions).map(({ id, kind, label, value }) => ({ id, kind, label, value })),
    protection: planning.protection ? { dependents: planning.protection.dependents, totalExpenses: planning.protection.totalExpenses, emergencyReserve: planning.protection.emergencyReserve, successionNeed: planning.protection.successionNeed, recommendedCapital: planning.protection.recommendedCapital, coverageGap: planning.protection.coverageGap } : null,
    pensions: planning.r5.pensionSimulations.map((item) => ({ id: item.id, name: item.name, product: item.product, currentReserve: item.currentReserve, monthlyContribution: item.monthlyContribution, grossReserve: item.result.grossReserve, netReserve: item.result.netReserve, sustainableMonthlyIncome: item.result.sustainableMonthlyIncome })),
    financings: planning.r5.financingSimulations.map((item) => ({ id: item.id, name: item.name, kind: item.kind, principal: item.principal, firstPayment: item.firstPayment, lastPayment: item.lastPayment, totalPaid: item.totalPaid, termMonths: item.termMonths, schedule: item.schedule })),
    financingComparisons: [...planning.r5.financingComparisons].sort((left, right) => left.id.localeCompare(right.id)),
    contributions: contributions.map((item) => ({ id: item.id, resource: item.resource, dueDate: item.dueDate, plannedAmount: item.plannedAmount, actualAmount: item.actualAmount, status: item.status, realizedAt: item.realizedAt })),
    followUp: { items: [...followUp.items].sort((left, right) => left.id.localeCompare(right.id)), pipeline: followUp.pipeline },
    ...(familyMembers ? { family: familyMembers.map((member) => ({ id: member.id, fullName: member.fullName, relationship: member.relationship, birthDate: member.birthDate, financiallyDependent: member.financiallyDependent, successionDependent: member.successionDependent, financialDependencyEnd: member.financialDependencyEnd, protectionNeeded: member.protectionNeeded, version: member.version })) } : {}),
  };
}

function assertInput<T>(schema: { safeParse: (value: unknown) => { success: boolean; data?: T } }, value: unknown, message: string): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success) throw new RepositoryError(message, 422, 'VALIDATION_ERROR');
  return parsed.data as T;
}

function assertResponse<T>(schema: { safeParse: (value: unknown) => { success: boolean; data?: T } }, value: unknown, message: string, code = 'UPSTREAM_RESPONSE_INVALID'): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success) throw new RepositoryError(message, 502, code);
  return parsed.data as T;
}
const MAX_JSON_MUTATION_BYTES = 900_000;
const MAX_PAGED_OFFSET = 100_000;
const MAX_COMPACT_ARRAY_ITEMS = 500;
const MAX_AUDIT_ENTRIES = 1_000;
const DIRECTUS_FILE_REF = /^directus:\/\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

function assertCompactMutationLimits(serialized: string): void {
  let value: unknown;
  try { value = JSON.parse(serialized) as unknown; } catch { throw new Error('Payload JSON inválido'); }
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Payload JSON deve ser um envelope objeto');
  const body = value as Record<string, unknown>;
  const check = (parent: unknown, key: string, limit: number) => {
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) return;
    const candidate = (parent as Record<string, unknown>)[key];
    if (Array.isArray(candidate) && candidate.length > limit) throw new Error(`Envelope ${key} excede o limite operacional de ${limit} registros`);
  };
  for (const key of ['scenarios', 'goals', 'financials']) check(body.planning_data, key, MAX_COMPACT_ARRAY_ITEMS);
  check((body.planning_data as Record<string, unknown> | undefined)?.r5, 'pensionSimulations', MAX_COMPACT_ARRAY_ITEMS);
  check((body.planning_data as Record<string, unknown> | undefined)?.r5, 'financingSimulations', MAX_COMPACT_ARRAY_ITEMS);
  check((body.planning_data as Record<string, unknown> | undefined)?.r5, 'financingComparisons', MAX_COMPACT_ARRAY_ITEMS);
  check(body.calculation_history, 'snapshots', MAX_COMPACT_ARRAY_ITEMS);
  check(body.calculation_history, 'idempotency', MAX_COMPACT_ARRAY_ITEMS);
  check(body.reports_data, 'reports', MAX_COMPACT_ARRAY_ITEMS);
  check(body.follow_up_data, 'items', MAX_AUDIT_ENTRIES);
  check(body.follow_up_data, 'contributions', MAX_AUDIT_ENTRIES);
  check((body.follow_up_data as Record<string, unknown> | undefined)?.pipeline, 'history', MAX_PIPELINE_HISTORY);
  check(body.file_links, 'files', MAX_COMPACT_ARRAY_ITEMS);
  check(body.audit_log, 'entries', MAX_AUDIT_ENTRIES);
  check(body.operation_journal, 'pending', MAX_OPERATION_PENDING);
  check(body.operation_journal, 'completedKeys', MAX_OPERATION_COMPLETED_KEYS);
  if (body.operation_journal !== undefined && !operationJournalSchema.safeParse(parseJson(body.operation_journal, {})).success) {
    throw new Error('Journal de operações inválido');
  }
}

function calculateProtection(input: CreateProtectionNeedInput, current: ClientProtectionNeed | null): Omit<ClientProtectionNeed, 'sourceRevisionFingerprint'> {
  const monthlyRate = monthlyEffectiveRate(input.realAnnualRatePct / 100);
  const protectedMonthlyExpense = protectionMonthlyHouseholdExpense(input.monthlyLivingExpenses, input.dependents);
  const totalExpenses = presentValueAnnuityDue(protectedMonthlyExpense, monthlyRate, input.horizonYears * 12);
  const emergencyReserve = emergencySuccessionReserve(input.emergencyMonths, input.monthlyLivingExpenses).toNumber();
  const successionNeed = inventoryCost(input.estateBaseValue, input.legalFeesPct, input.courtCostsPct, input.inheritanceTaxPct).toNumber();
  const recommendedCapital = totalExpenses + emergencyReserve + successionNeed;
  const coverageGap = Math.max(0, protectionNeedFromDerived(totalExpenses + emergencyReserve, successionNeed, input.currentGuarantees, {
    wholeLifeInsuredCapital: input.wholeLifeInsuredCapital,
    deductiblePrivatePension: input.deductiblePrivatePension,
  }).toNumber());
  const now = new Date().toISOString();
  return {
    id: current?.id ?? crypto.randomUUID(), clientId: input.clientId,
    monthlyLivingExpenses: moneyString(input.monthlyLivingExpenses), horizonYears: input.horizonYears,
    dependents: input.dependents, estateBaseValue: moneyString(input.estateBaseValue),
    legalFeesPct: input.legalFeesPct, courtCostsPct: input.courtCostsPct, inheritanceTaxPct: input.inheritanceTaxPct,
    emergencyMonths: input.emergencyMonths, currentGuarantees: moneyString(input.currentGuarantees), guaranteesMode: input.guaranteesMode, guaranteesScenarioId: input.guaranteesScenarioId,
    wholeLifeInsuredCapital: moneyString(input.wholeLifeInsuredCapital), deductiblePrivatePension: moneyString(input.deductiblePrivatePension),
    realAnnualRatePct: input.realAnnualRatePct, totalExpenses: calculatedMoney(totalExpenses), emergencyReserve: calculatedMoney(emergencyReserve),
    successionNeed: calculatedMoney(successionNeed), recommendedCapital: calculatedMoney(recommendedCapital), coverageGap: calculatedMoney(coverageGap),
    version: (current?.version ?? 0) + 1, updatedAt: now,
  };
}

function protectionDependentsFromFamily(members: readonly FamilyMember[]): number {
  return members.filter((member) => isFinanciallyDependentToday(member)).length;
}

const GUARANTEE_EXPOSURE_KINDS: readonly GuaranteeExposure['kind'][] = ['immobilized_property', 'monthly_income', 'financial_resources', 'life_insurance'];

function effectiveGuaranteeExposures(exposures: readonly GuaranteeExposure[], scenarioId: string | null, exclusions: readonly PlanningExclusionSelection[] = []): GuaranteeExposure[] {
  return resolveEffectiveGuaranteeExposures(exposures, scenarioId, exclusions);
}

async function assertGuaranteeBindingReceipts(exposures: readonly GuaranteeExposure[]): Promise<void> {
  const verified = await Promise.all(exposures.map((exposure) => guaranteeExposureBindingMatches(exposure)));
  if (verified.some((matches) => !matches)) throw new RepositoryError('A exposição de garantia não possui receipt criptograficamente vinculado ao seu conteúdo.', 409, 'GUARANTEE_BINDING_INVALID');
}

async function assertReportGuaranteeBindings(planning: PlanningData, scenarioId: string): Promise<void> {
  if (planning.protection?.guaranteesMode !== 'canonical') return;
  if (!protectionGuaranteesScenarioMatchesReport(planning.protection, scenarioId)) throw new RepositoryError('O diagnóstico canônico de proteção pertence a outro cenário.', 409, 'PROTECTION_SCENARIO_MISMATCH');
  const available = effectiveGuaranteeExposures(planning.guaranteeExposures, scenarioId);
  const kinds = new Set(available.map((exposure) => exposure.kind));
  if (available.length !== GUARANTEE_EXPOSURE_KINDS.length || kinds.size !== GUARANTEE_EXPOSURE_KINDS.length) throw new RepositoryError('O relatório está bloqueado por cardinalidade incompleta de garantias canônicas.', 409, 'GUARANTEE_BINDING_PENDING');
  if (available.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) throw new RepositoryError('O relatório está bloqueado por exposição de garantia sem binding verificável.', 409, 'GUARANTEE_BINDING_PENDING');
  await assertGuaranteeBindingReceipts(available);
}

function capturedFinanceOwnershipMismatch(value: unknown, clientId: string): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const envelope = value as Record<string, unknown>;
  const owner = clientId.toLowerCase();
  const envelopeClientId = envelope.clientId;
  if (typeof envelopeClientId === 'string' && SESSION_USER_ID.test(envelopeClientId) && envelopeClientId.toLowerCase() !== owner) return true;
  if (!Array.isArray(envelope.facts)) return false;
  return envelope.facts.some((fact) => {
    if (!fact || typeof fact !== 'object' || Array.isArray(fact)) return false;
    const factClientId = (fact as Record<string, unknown>).clientId;
    return typeof factClientId === 'string' && SESSION_USER_ID.test(factClientId) && factClientId.toLowerCase() !== owner;
  });
}

function parseCapturedFinanceData(value: unknown, clientId: string): CapturedFinanceData | null {
  if (value === undefined || value === null) return null;
  let candidate: unknown = value;
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value) as unknown;
    } catch {
      throw new RepositoryError('Envelope captured_finance persistido inválido.', 502, 'CAPTURED_FINANCE_INVALID');
    }
  }
  if (candidate === null) return null;
  if (capturedFinanceOwnershipMismatch(candidate, clientId)) {
    throw new RepositoryError('Envelope captured_finance não pertence ao cliente consultado.', 502, 'CAPTURED_FINANCE_OWNERSHIP_INVALID');
  }
  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) return null;
  const parsed = capturedFinanceDataSchema.safeParse(candidate);
  if (!parsed.success) throw new RepositoryError('Envelope captured_finance persistido inválido.', 502, 'CAPTURED_FINANCE_INVALID');
  if (parsed.data.clientId.toLowerCase() !== clientId.toLowerCase()) {
    throw new RepositoryError('Envelope captured_finance não pertence ao cliente consultado.', 502, 'CAPTURED_FINANCE_OWNERSHIP_INVALID');
  }
  return parsed.data;
}

function preserveCapturedFinanceReadback(row: RawClient, previous: RawClient): RawClient {
  if (row.captured_finance !== undefined || previous.captured_finance === undefined) return row;
  return { ...row, captured_finance: previous.captured_finance };
}

const mapClient = (row: RawClient): Client => {
  // O gateway Edge valida client_id antes de projetar a resposta e o remove
  // do contrato público. Se o campo vier presente, a divergência continua
  // sendo fatal; ausente significa apenas a projeção já autorizada do Edge.
  if (row.contacts?.some((contact) => contact.client_id !== undefined && contact.client_id !== row.id)) throw new RepositoryError('Contato persistido não pertence ao cliente.', 502, 'CONTACT_OWNERSHIP_INVALID');
  const primaryKinds = new Set<string>();
  for (const contact of row.contacts ?? []) {
    if (contact.is_primary && primaryKinds.has(contact.kind)) throw new RepositoryError('Há mais de um contato primário do mesmo tipo.', 502, 'CONTACT_PRIMARY_DUPLICATE');
    if (contact.is_primary) primaryKinds.add(contact.kind);
  }
  const email = row.contacts?.find((contact) => contact.kind === 'email' && contact.is_primary)?.value ?? null;
  const phone = row.contacts?.find((contact) => (contact.kind === 'phone' || contact.kind === 'mobile') && contact.is_primary)?.value ?? null;
  const filled = [row.full_name, row.short_name, row.birth_date ?? row.stated_age, row.profession, email, phone]
    .filter((value) => value !== null && value !== '').length;
  const editorialNotes = row.editorial_notes === undefined ? undefined : assertInput(editorialNotesDataSchema, parseJson(row.editorial_notes, null), 'Notas editoriais persistidas inválidas.');
  const capturedFinance = parseCapturedFinanceData(row.captured_finance, row.id);
  return assertInput(clientSchema, {
    id: row.id,
    status: row.status,
    fullName: row.full_name,
    shortName: row.short_name,
    birthDate: row.birth_date,
    statedAge: row.stated_age,
    profession: row.profession,
    email,
    phone,
    completeness: Math.round(filled / 6 * 100),
    updatedAt: row.date_updated ?? row.date_created,
    version: Number(row.version),
    ...(row.data_state ? { dataState: row.data_state } : {}),
    ...(editorialNotes === undefined ? {} : { editorialNotes }),
    ...(capturedFinance === null ? {} : { capturedFinance }),
  }, 'Cliente persistido inválido.');
};

function assertClientMutationIdentity(value: unknown, clientId: string, expectedVersion: number): void {
  const envelope = value && typeof value === 'object' && !Array.isArray(value) ? value as { data?: unknown } : {};
  const data = Array.isArray(envelope.data) ? envelope.data[0] : envelope.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new RepositoryError('A mutação não retornou um cliente persistido.', 502, 'CLIENT_RESPONSE_INVALID');
  const row = data as Record<string, unknown>;
  if (row.id !== clientId || Number(row.version) !== expectedVersion) throw new RepositoryError('A mutação confirmou cliente ou versão divergentes.', 502, 'CLIENT_RESPONSE_INVALID');
}

function ageFromBirthDate(input: CreateClient, today = new Date()): number {
  if (!input.birthDate) return input.statedAge ?? 35;
  const birth = new Date(`${input.birthDate}T00:00:00.000Z`);
  let age = today.getUTCFullYear() - birth.getUTCFullYear();
  const beforeBirthday = today.getUTCMonth() < birth.getUTCMonth() || (today.getUTCMonth() === birth.getUTCMonth() && today.getUTCDate() < birth.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
}

function baseScenario(input: CreateClient, scenarioId: string, planId: string, operationAt: string): PlanScenario {
  const now = new Date(operationAt);
  const currentAge = Math.max(0, Math.min(ageFromBirthDate(input, now), 119));
  const retirementAge = Math.min(120, Math.max(60, currentAge + 1));
  return {
    id: scenarioId,
    planId,
    name: 'Cenário base',
    isPrimary: true,
    visibleInComparison: true,
    version: 1,
    assumptionsVersion: 1,
    assumptions: {
      baseYear: now.getUTCFullYear(),
      baseMonth: now.getUTCMonth() + 1,
      currentAge,
      retirementAge,
      lifeExpectancyAge: Math.min(130, Math.max(90, retirementAge + 1)),
      currentReserve: 0,
      monthlyContribution: 0,
      desiredMonthlyIncome: 0,
      nominalAnnualRatePct: 8,
      inflationAnnualRatePct: 4,
      differentiatePostRetirementRates: false,
    },
  };
}

const CLIENT_PROVISIONING_STATES = new Set<ClientProvisioningState>(['pending', 'directus_confirmed', 'provisioned', 'failed', 'outcome_unknown', 'dead_letter']);
function normalizeClientProvisioning(value: unknown, clientId: string): ClientProvisioningStatus {
  const row = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const state = row.state;
  const attempts = Number(row.attempts);
  const canonicalClientId = clientId.toLowerCase();
  const errorState = state === 'failed' || state === 'outcome_unknown' || state === 'dead_letter';
  const createdAt = typeof row.createdAt === 'string' ? row.createdAt : '';
  const updatedAt = typeof row.updatedAt === 'string' ? row.updatedAt : '';
  const completedAt = row.completedAt;
  const redriveCount = Number(row.redriveCount ?? 0);
  const lastErrorCode = row.lastErrorCode == null ? null : row.lastErrorCode;
  const lastErrorMessage = row.lastErrorMessage == null ? null : row.lastErrorMessage;
  const isCanonicalDate = (candidate: unknown): candidate is string => {
    if (typeof candidate !== 'string' || !PROVISIONING_ISO.test(candidate)) return false;
    const parsed = new Date(candidate);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString() === candidate;
  };
  if (!SESSION_USER_ID.test(canonicalClientId) || row.clientId !== canonicalClientId || typeof state !== 'string' || !CLIENT_PROVISIONING_STATES.has(state as ClientProvisioningState)
    || !Number.isSafeInteger(attempts) || attempts < 0 || attempts > 8 || !Number.isSafeInteger(redriveCount) || redriveCount < 0 || redriveCount > 1 || typeof row.requestId !== 'string'
    || !PROVISIONING_KEY.test(row.requestId) || (lastErrorCode !== null && (typeof lastErrorCode !== 'string' || !/^[A-Z0-9_:.-]{1,80}$/.test(lastErrorCode)))
    || (lastErrorMessage !== null && typeof lastErrorMessage !== 'string')
    || (lastErrorMessage !== undefined && lastErrorMessage !== null && (lastErrorMessage.length > 240 || hasUnsafeProvisioningControl(lastErrorMessage)))
    || !isCanonicalDate(createdAt) || !isCanonicalDate(updatedAt) || Date.parse(updatedAt) < Date.parse(createdAt)
    || (completedAt !== null && !isCanonicalDate(completedAt))
    || (errorState && (typeof lastErrorCode !== 'string' || typeof lastErrorMessage !== 'string'))
    || (!errorState && (lastErrorCode !== null || lastErrorMessage !== null))
    || (state === 'provisioned' && completedAt === null)
    || (state !== 'provisioned' && completedAt !== null)
    || (completedAt !== null && (Date.parse(completedAt) < Date.parse(createdAt) || Date.parse(completedAt) > Date.parse(updatedAt)))) {
    throw new RepositoryError('Status de provisioning inválido.', 502, 'PROVISIONING_RESPONSE_INVALID');
  }
  return { clientId: canonicalClientId, state: state as ClientProvisioningState, attempts, requestId: row.requestId, redriveCount, lastErrorCode, lastErrorMessage, createdAt, updatedAt, completedAt: completedAt as string | null };
}

function pocketBaseProvisioningStatus(clientId: string): ClientProvisioningStatus {
  const now = new Date().toISOString();
  return {
    clientId: clientId.toLowerCase(), state: 'provisioned', attempts: 0,
    requestId: `pocketbase-${clientId.toLowerCase()}`, redriveCount: 0,
    lastErrorCode: null, lastErrorMessage: null, createdAt: now, updatedAt: now, completedAt: now,
  };
}

export class DirectusRepository implements FintracksRepository {
  readonly mode: 'directus' | 'pocketbase';
  private readonly provider: RepositoryProvider;
  private readonly fetchImpl: typeof fetch;
  private readonly baseUrl: string;
  private readonly edgeBaseUrl: string;
  private readonly contributionGatewayBaseUrl: string;
  private readonly fileGatewayBaseUrl: string;
  private readonly filesFolderId: string | undefined;
  private readonly allowDirectusFileFallback: boolean;
  private readonly casWrites: boolean;
  private readonly syntheticSession: boolean;
  private readonly directusOnly: boolean;
  private sessionUserId: string | undefined;
  private sessionRoleId: string | undefined;

  constructor(options: DirectusRepositoryOptions) {
    this.provider = options.provider ?? 'directus';
    this.mode = this.provider;
    const pocketbase = this.provider === 'pocketbase';
    const syntheticFallback = options.allowDirectusFileFallback === true && options.directusFileFallbackMode === 'synthetic';
    const directusOnly = options.directusOnly === true && (pocketbase || !options.edgeBaseUrl);
    const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.NODE_ENV;
    if (pocketbase && (syntheticFallback || options.allowDirectusFileFallback === true)) throw new Error('O adapter PocketBase não aceita fallback de arquivos Directus.');
    if (pocketbase && options.baseUrl === '/directus') throw new Error('O adapter PocketBase não aceita o endpoint legado /directus.');
    if (!options.edgeBaseUrl && !syntheticFallback && !directusOnly) throw new Error('Gateway Edge obrigatório, ou escolha o modo Directus-only explícito.');
    if (options.edgeBaseUrl && options.casWrites === false) throw new Error('Gateway Edge exige CAS habilitado; casWrites:false é incompatível com o proxy transacional.');
    if (directusOnly && options.casWrites === false && nodeEnv !== 'test') throw new Error('Modo Directus-only exige CAS habilitado fora de fixtures sintéticas.');
    if (syntheticFallback && nodeEnv !== 'test') throw new Error('Fallback nativo de arquivos é permitido somente em fixtures sintéticas de teste.');
    if (directusOnly && !options.fileGatewayBaseUrl && nodeEnv !== 'test') throw new Error('Modo Directus-only exige o gateway privado de arquivos /api.');
    if (directusOnly && !options.contributionGatewayBaseUrl && nodeEnv !== 'test') throw new Error('Modo Directus-only exige o gateway privado financeiro /api.');
    if (typeof window !== 'undefined' && !options.edgeBaseUrl && !directusOnly) throw new Error('No navegador, use o modo Directus-only explícito ou o gateway Edge.');
    if (typeof window !== 'undefined' && syntheticFallback) throw new Error('Fallback nativo de arquivos é permitido somente fora do navegador.');
    if (!pocketbase && typeof window !== 'undefined' && window.location?.origin) {
      const origin = window.location.origin;
      const directusUrl = new URL(options.baseUrl, origin);
      const directusPathValid = directusUrl.pathname === '/directus' || directusUrl.pathname.startsWith('/directus/');
      if (directusUrl.origin !== origin || !directusPathValid) throw new Error('No navegador, o Directus deve usar o gateway same-origin /directus.');
      for (const gatewayBaseUrl of [options.edgeBaseUrl, options.fileGatewayBaseUrl, options.contributionGatewayBaseUrl].filter((value): value is string => Boolean(value))) {
        const gatewayUrl = new URL(gatewayBaseUrl, origin);
        const gatewayPathValid = gatewayUrl.pathname === '/api' || gatewayUrl.pathname.startsWith('/api/');
        if (gatewayUrl.origin !== origin || !gatewayPathValid) throw new Error('No navegador, os gateways devem usar o proxy same-origin /api.');
      }
    }
    if (!pocketbase && (options.edgeBaseUrl || options.fileGatewayBaseUrl || options.contributionGatewayBaseUrl) && typeof window === 'undefined' && nodeEnv !== 'test') {
      const referenceOrigin = 'https://fintracks-gateway.invalid';
      const directusUrl = new URL(options.baseUrl, referenceOrigin);
      const directusPathValid = directusUrl.pathname === '/directus' || directusUrl.pathname.startsWith('/directus/');
      const gatewayUrls = [options.edgeBaseUrl, options.fileGatewayBaseUrl, options.contributionGatewayBaseUrl]
        .filter((value): value is string => Boolean(value))
        .map((value) => new URL(value, referenceOrigin));
      const gatewayPathsValid = gatewayUrls.every((url) => url.pathname === '/api' || url.pathname.startsWith('/api/'));
      if (!gatewayUrls.every((url) => url.origin === directusUrl.origin) || !directusPathValid || !gatewayPathsValid) {
        throw new Error('Fora do navegador, os gateways Edge/arquivos e Directus devem apontar para /api e /directus same-origin; use um adapter server-side explícito para outro transporte.');
      }
    }
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.edgeBaseUrl = (options.edgeBaseUrl ?? '').replace(/\/$/, '');
    this.contributionGatewayBaseUrl = (options.contributionGatewayBaseUrl ?? options.edgeBaseUrl ?? '').replace(/\/$/, '');
    this.fileGatewayBaseUrl = (options.fileGatewayBaseUrl ?? options.edgeBaseUrl ?? '').replace(/\/$/, '');
    this.allowDirectusFileFallback = syntheticFallback;
    this.syntheticSession = syntheticFallback;
    this.directusOnly = directusOnly;
    if (options.filesFolderId !== undefined && options.filesFolderId !== FINTRACKS_FILES_FOLDER_ID) throw new Error('A pasta de arquivos deve ser a pasta privada Fintracks canônica.');
    this.filesFolderId = options.filesFolderId;
    this.casWrites = options.casWrites !== false;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  private assertPrivateFileTransport(): void {
    if (this.directusOnly && !this.fileGatewayBaseUrl && !this.allowDirectusFileFallback) {
      throw new RepositoryError('Modo Directus-only exige o gateway privado de arquivos /api.', 503, 'FILE_GATEWAY_REQUIRED');
    }
  }

  private async mutationIdempotencyKey(method: string, path: string, body: BodyInit | null | undefined): Promise<string> {
    const parts: string[] = [method.toUpperCase(), path];
    if (typeof body === 'string') parts.push(body);
    else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      for (const [key, value] of body.entries()) {
        if (typeof Blob !== 'undefined' && value instanceof Blob) {
          const bytes = await value.arrayBuffer();
          const fileDigest = await crypto.subtle.digest('SHA-256', bytes);
          const hex = Array.from(new Uint8Array(fileDigest), (byte) => byte.toString(16).padStart(2, '0')).join('');
          const name = typeof (value as Blob & { name?: unknown }).name === 'string' ? (value as Blob & { name: string }).name : '';
          parts.push(key, `blob:${name}:${value.type}:${value.size}:${hex}`);
        } else parts.push(key, String(value));
      }
    } else if (body !== undefined && body !== null) parts.push(String(body));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join('\u0000')));
    return `sdk-${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  }

  private async request<T>(base: string, path: string, init: RequestInit = {}, onResponseRequestId?: (requestId: string | undefined) => void): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    const multipart = typeof FormData !== 'undefined' && init.body instanceof FormData;
    if (init.body && !multipart) headers.set('Content-Type', 'application/json');
    if (multipart) headers.delete('Content-Type');
    if (init.body && !multipart && typeof init.body === 'string') {
      if (new TextEncoder().encode(init.body).byteLength > MAX_JSON_MUTATION_BYTES) throw new Error('Payload JSON excede o limite operacional de 900 KB');
      assertCompactMutationLimits(init.body);
    }
    const method = String(init.method ?? 'GET').toUpperCase();
    const mutation = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (mutation) {
      const suppliedKey = headers.get('Idempotency-Key');
      const key = suppliedKey === null ? await this.mutationIdempotencyKey(method, path, init.body) : canonicalIdempotencyKey(suppliedKey);
      if (!IDEMPOTENCY_KEY.test(key)) throw new RepositoryError('A chave de idempotência é inválida.', 422, 'IDEMPOTENCY_KEY_INVALID');
      headers.set('Idempotency-Key', key);
    }
    let response: Response;
    const contributionMutation = mutation && path.startsWith('/contributions/');
    const ambiguousResponseError = (requestId?: string) => new RepositoryError(
      'Não foi possível confirmar o resultado do aporte; mantenha a operação bloqueada e reconcilie pela mesma chave.',
      502,
      'CONTRIBUTION_OUTCOME_UNKNOWN',
      requestId,
    );
    try {
      response = await this.fetchImpl(`${base}${path}`, { ...init, headers, credentials: 'include' });
    } catch {
      throw new RepositoryError(
        mutation ? 'Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.' : 'O serviço de dados não respondeu.',
        503,
        mutation ? 'OUTCOME_UNKNOWN' : 'REQUEST_FAILED',
      );
    }
    const responseRequestId = response.headers.get('X-Request-Id') ?? undefined;
    onResponseRequestId?.(responseRequestId);
    if (!response.ok) {
      if (response.status === 401) {
        const hadSession = this.sessionUserId !== undefined;
        this.clearSessionCache();
        if (!path.startsWith('/auth/login') && !path.startsWith('/users/me') && hadSession && typeof window !== 'undefined') window.dispatchEvent(new Event('fintracks:session-expired'));
      }
      const raw = await response.text();
      if (method === 'POST' && path.startsWith('/operations/reconcile/') && response.status === 409) {
        try {
          const reconciliationEnvelope = JSON.parse(raw) as unknown;
          if (reconciliationEnvelope && typeof reconciliationEnvelope === 'object' && !Array.isArray(reconciliationEnvelope) && 'data' in reconciliationEnvelope) return reconciliationEnvelope as T;
        } catch { /* fall through to the normal error envelope validation */ }
      }
      let message = `HTTP ${response.status}`;
      let code = 'REQUEST_FAILED';
      let requestId: string | undefined = response.headers.get('X-Request-Id') ?? undefined;
      let parsed: ReturnType<typeof apiErrorSchema.safeParse> | null = null;
      try { parsed = apiErrorSchema.safeParse(JSON.parse(raw)); } catch { parsed = null; }
      if (parsed?.success) {
        message = parsed.data.error.message;
        code = parsed.data.error.code;
        requestId = parsed.data.error.requestId ?? requestId;
      } else if (contributionMutation) throw ambiguousResponseError(requestId);
      else if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', response.status, 'OUTCOME_UNKNOWN', requestId);
      else message = 'Resposta de erro inválida do serviço de dados';
      throw new RepositoryError(message, response.status, code, requestId);
    }
    if (response.status === 204) {
      if (path === '/auth/logout') return {} as T;
      if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', 502, 'OUTCOME_UNKNOWN', responseRequestId);
      return {} as T;
    }
    let raw: string;
    try { raw = await response.text(); }
    catch {
      if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', 502, 'OUTCOME_UNKNOWN', responseRequestId);
      throw new RepositoryError('O serviço de dados não conseguiu entregar o corpo da resposta.', 502, 'UPSTREAM_RESPONSE_INVALID', responseRequestId);
    }
    if (!raw.trim()) {
      if (path === '/auth/logout') return {} as T;
      if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', 502, 'OUTCOME_UNKNOWN', responseRequestId);
      throw new RepositoryError('O serviço de dados retornou uma resposta vazia.', 502, 'UPSTREAM_RESPONSE_INVALID');
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', 502, 'OUTCOME_UNKNOWN', responseRequestId);
        throw new RepositoryError('O serviço de dados retornou um envelope inválido.', 502, 'UPSTREAM_RESPONSE_INVALID');
      }
      return parsed as T;
    } catch {
      if (mutation) throw new RepositoryError('Não foi possível confirmar o resultado da operação; reconcilie antes de repetir.', 502, 'OUTCOME_UNKNOWN', responseRequestId);
      throw new RepositoryError('O serviço de dados retornou JSON inválido.', 502, 'UPSTREAM_RESPONSE_INVALID');
    }
  }

  private async blobRequest(base: string, path: string): Promise<Blob> {
    const response = await this.fetchImpl(`${base}${path}`, { headers: { Accept: '*/*' }, credentials: 'include' });
    if (!response.ok) {
      if (response.status === 401) {
        const hadSession = this.sessionUserId !== undefined;
        this.clearSessionCache();
        if (hadSession && typeof window !== 'undefined') window.dispatchEvent(new Event('fintracks:session-expired'));
      }
      const raw = await response.text();
      let message = `HTTP ${response.status}`;
      let code = 'REQUEST_FAILED';
      let requestId: string | undefined = response.headers.get('X-Request-Id') ?? undefined;
      try {
        const parsed = apiErrorSchema.safeParse(JSON.parse(raw));
        if (parsed.success) {
          message = parsed.data.error.message;
          code = parsed.data.error.code;
          requestId = parsed.data.error.requestId ?? requestId;
        } else message = 'Resposta de erro inválida do armazenamento';
      } catch {
        message = 'Resposta de erro inválida do armazenamento';
      }
      throw new RepositoryError(message, response.status, code, requestId);
    }
    return response.blob();
  }

  private directus<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (this.provider === 'pocketbase' && (path.startsWith('/auth/') || path.startsWith('/users/me') || path.startsWith('/directus') || !path.startsWith('/items/'))) {
      throw new RepositoryError('O adapter PocketBase não aceita transporte Directus ou rota não allowlisted.', 500, 'POCKETBASE_PATH_INVALID');
    }
    if (this.casWrites && init.method?.toUpperCase() === 'PATCH' && typeof init.body === 'string') {
      const match = /^\/items\/(fintracks_clients|fintracks_family_members)\/([^?]+)(?:\?.*)?$/.exec(path);
      if (match) {
        let payload: Record<string, unknown>;
        try { payload = JSON.parse(init.body) as Record<string, unknown>; } catch { payload = {}; }
        const nextVersion = Number(payload.version);
        if (Number.isInteger(nextVersion) && nextVersion > 1) {
          const collection = match[1]!;
          const id = encodeURIComponent(match[2]!);
          const responseFields = this.edgeBaseUrl
            ? 'id,version,status,full_name,short_name,birth_date,stated_age,profession,data_model_version'
            : collection === 'fintracks_family_members' ? FAMILY_FIELDS : CLIENT_FIELDS;
          const sourceParams = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?') + 1) : '');
          const familyClientId = sourceParams.get('filter[client_id][_eq]');
          const ownershipQuery = collection === 'fintracks_clients'
            ? this.directusClientOwnershipQuery()
            : familyClientId ? `&filter[client_id][_eq]=${encodeURIComponent(familyClientId)}` : '';
          const casPath = `/items/${collection}?filter[id][_eq]=${id}&filter[version][_eq]=${nextVersion - 1}${ownershipQuery}&limit=1&fields=${encodeURIComponent(responseFields)}`;
          return this.request<Envelope<RawClient[] | RawClient>>(this.baseUrl, casPath, { ...init, method: 'PATCH' }).then((result) => {
            const rows = Array.isArray(result.data) ? result.data : result.data && typeof result.data === 'object' ? [result.data] : [];
            if (rows.length !== 1) throw new RepositoryError('A gravação perdeu a corrida de versão.', 409, 'VERSION_CONFLICT');
            return { data: rows[0] } as unknown as T;
          });
        }
      }
    }
    return this.request<T>(this.baseUrl, path, init);
  }

  private clientPath(id?: string): string {
    const item = id ? `/${encodeURIComponent(id)}` : '';
    if (this.provider === 'pocketbase') return `/items/fintracks_clients${item}`;
    return id ? `/items/fintracks_clients${item}?fields=${encodeURIComponent(CLIENT_FIELDS)}${this.directusClientOwnershipQuery()}` : '/items/fintracks_clients';
  }

  private clientMutationPath(id: string, expectedVersion: number): string {
    if (this.provider === 'pocketbase') return `/items/fintracks_clients/${encodeURIComponent(id)}`;
    return `/items/fintracks_clients?filter[id][_eq]=${encodeURIComponent(id)}&filter[version][_eq]=${expectedVersion}&limit=1&fields=${encodeURIComponent(CLIENT_FIELDS)}${this.directusClientOwnershipQuery()}`;
  }

  private directusClientOwnershipQuery(): string {
    if (this.provider === 'pocketbase') return '';
    if (!this.directusOnly || this.sessionRoleId !== FINTRACKS_CONSULTANT_ROLE_ID || !this.sessionUserId) return '';
    return `&filter[user_created][_eq]=${encodeURIComponent(this.sessionUserId)}`;
  }

  private documentGatewayBaseUrl(): string {
    return this.edgeBaseUrl || (this.directusOnly ? this.fileGatewayBaseUrl : '');
  }

  private makeAuditEntry(action: AuditEntry['action'], entityId: string, fields?: string[], actorId = this.sessionUserId, stable?: { id: string; at: string }): AuditEntry {
    const actor = actorId?.trim() || (this.syntheticSession ? SYNTHETIC_ACTOR_ID : undefined);
    if (!actor) throw new RepositoryError('A mutação exige uma sessão autenticada para autoria de auditoria.', 401, 'SESSION_EXPIRED');
    const entry = {
      id: stable?.id ?? crypto.randomUUID(), action, at: stable?.at ?? new Date().toISOString(), entityId, actorId: actor,
      ...(fields && fields.length > 0 ? { fields } : {}),
    };
    const validated = assertInput(auditEntrySchema, entry, 'Evento de auditoria criado inválido.');
    return { ...validated, actorId: actor } as AuditEntry;
  }

  private async rawClient(id: string): Promise<RawClient> {
    await this.ensureDirectusBrowserSession();
    const documentGatewayBaseUrl = this.documentGatewayBaseUrl();
    const response = documentGatewayBaseUrl
      ? await this.request<Envelope<RawClient>>(documentGatewayBaseUrl, `/client-document/${encodeURIComponent(id)}`)
      : await this.directus<Envelope<RawClient>>(this.clientPath(id));
    if (String(response.data?.id ?? '') !== id) throw new RepositoryError('Resposta de cliente não corresponde ao identificador consultado.', 502, 'CLIENT_RESPONSE_INVALID');
    if (response.data.operation_journal !== undefined && !operationJournalSchema.safeParse(parseJson(response.data.operation_journal, {})).success) throw new RepositoryError('Journal de operações persistido inválido.', 502, 'OPERATION_JOURNAL_INVALID');
    if (response.data.data_state !== 'candidate-only') auditLog(response.data.audit_log);
    return response.data;
  }

  private async assertFamilyExclusionTargets(planning: PlanningData, clientId: string, familyMembers?: readonly FamilyMember[]): Promise<void> {
    const selections = planning.exclusions.filter((selection) => selection.targetType === 'family_member' && selection.targetIds.length > 0);
    if (selections.length === 0) return;
    const members = familyMembers ?? await this.listFamilyMembers(clientId);
    const familyIds = new Set(members.map((member) => member.id));
    if (selections.some((selection) => selection.targetIds.some((id) => !familyIds.has(id)))) {
      throw new RepositoryError('Desconsideração referencia familiar de outro cliente ou inexistente.', 502, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
    }
  }

  private assertGuaranteeExclusionTargets(planning: PlanningData, clientId: string): void {
    const guaranteeIds = new Set(planning.guaranteeExposures.filter((exposure) => exposure.clientId === clientId).map((exposure) => exposure.id));
    const selections = planning.exclusions.filter((selection) => selection.targetType === 'guarantee' && selection.targetIds.length > 0);
    if (selections.some((selection) => selection.targetIds.some((id) => !guaranteeIds.has(id)))) throw new RepositoryError('Desconsideração referencia exposição de garantia de outro cliente ou inexistente.', 502, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
  }

  private async patchClientDocument(clientId: string, expectedVersion: number, body: Record<string, unknown>): Promise<RawClient | undefined> {
    const documentGatewayBaseUrl = this.documentGatewayBaseUrl();
    if (documentGatewayBaseUrl) {
      try {
        await this.request<Envelope<RawClient>>(documentGatewayBaseUrl, `/client-document/${encodeURIComponent(clientId)}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } catch (error) {
        // A resposta pode ser perdida depois do commit. O mesmo payload e
        // versão devem ser confirmados por leitura antes de qualquer retry;
        // nunca reenviar automaticamente um append desconhecido.
        if (!(error instanceof RepositoryError) || error.code !== 'OUTCOME_UNKNOWN') throw error;
      }
      const persisted = await this.rawClient(clientId);
      assertClientMutationContent({ data: persisted }, clientId, expectedVersion, body);
      return persisted;
    }
    try {
      const response = await this.directus<Envelope<RawClient>>(this.clientMutationPath(clientId, expectedVersion - 1), {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (Array.isArray(response.data) && response.data.length === 0) {
        throw new RepositoryError('A versão do cliente mudou antes da gravação.', 409, 'VERSION_CONFLICT');
      }
      assertClientMutationContent(response, clientId, expectedVersion, body);
      if (!this.directusOnly) return response.data;
      const persisted = await this.rawClient(clientId);
      assertClientMutationContent({ data: persisted }, clientId, expectedVersion, body);
      return persisted;
    } catch (error) {
      if (!(error instanceof RepositoryError) || error.code !== 'OUTCOME_UNKNOWN') throw error;
      const persisted = await this.rawClient(clientId);
      try {
        assertClientMutationContent({ data: persisted }, clientId, expectedVersion, body);
        return persisted;
      } catch {
        throw error;
      }
    }
  }

  private async listClientPages<T>(fields: string, extraQuery = ''): Promise<T[]> {
    if (this.directusOnly && typeof window !== 'undefined') {
      await this.ensureDirectusBrowserSession();
    } else if (!this.sessionUserId) {
      const user = await this.currentUser();
      if (!user) throw new RepositoryError('A listagem exige uma sessão autenticada.', 401, 'SESSION_EXPIRED');
	      if (!user.role?.id || !FINTRACKS_ROLE_IDS.has(user.role.id)) throw new RepositoryError('A listagem exige uma role Fintracks autorizada.', 403, 'ROLE_FORBIDDEN');
    }
    // Em runtime publicado, o diretório de clientes passa exclusivamente pelo
    // read-model do Worker, que aplica Authz/ownership antes de consultar o
    // Directus. O caminho Directus abaixo permanece para os envelopes privados
    // e para o adapter sintético sem gateway.
    if (this.edgeBaseUrl && fields === CLIENT_LIST_FIELDS) {
      const archived = new URLSearchParams(extraQuery.replace(/^&/, '')).get('filter[status][_eq]') === 'archived';
      const page = await this.request<Envelope<T[]>>(this.edgeBaseUrl, `/read-model/clients${archived ? '?status=archived' : ''}`);
      if (!page || typeof page !== 'object' || !Array.isArray(page.data)) throw new RepositoryError('O read-model de clientes retornou uma coleção inválida.', 502, 'COLLECTION_RESPONSE_INVALID');
      return page.data;
    }
    const pageSize = 200;
    const rows: T[] = [];
    const ownershipQuery = this.directusClientOwnershipQuery();
    for (let offset = 0; offset <= MAX_PAGED_OFFSET; offset += pageSize) {
      const page = await this.directus<Envelope<T[]>>(`/items/fintracks_clients?fields=${encodeURIComponent(fields)}&limit=${pageSize}&offset=${offset}&meta=total_count${extraQuery}${ownershipQuery}`);
      if (!page || typeof page !== 'object' || !Array.isArray(page.data)) throw new RepositoryError('O serviço de dados retornou uma coleção inválida.', 502, 'COLLECTION_RESPONSE_INVALID');
      rows.push(...page.data);
      const total = Number(page.meta?.total_count);
      if (page.data.length < pageSize || (Number.isInteger(total) && total >= 0 && rows.length >= total)) break;
      if (offset === MAX_PAGED_OFFSET) throw new Error('Resultado excede o limite de paginação de 100.200 registros');
    }
    return rows;
  }
  private assertVersion(row: RawClient, expectedVersion: number): void {
    if (Number(row.version) !== expectedVersion) throw new RepositoryError('Conflito de versão', 409, 'VERSION_CONFLICT');
  }

  private assertDirectusMutationClientAllowed(current: RawClient): void {
    if (current.status !== 'archived' || this.sessionRoleId === FINTRACKS_MANAGEMENT_ROLE_ID) return;
    assertActiveDirectusMutationClient(current);
  }

  private assertDirectusClientProfileMutationAllowed(current: RawClient, patch: UpdateClient): void {
    if (current.status !== 'archived') return;
    const onlyReactivation = current.status === 'archived' && patch.status === 'active' && Object.keys(patch).every((key) => key === 'status');
    if (onlyReactivation || (current.status === 'archived' && this.sessionRoleId === FINTRACKS_MANAGEMENT_ROLE_ID)) return;
    assertActiveDirectusMutationClient(current);
  }

  private clearSessionCache(): void {
    this.sessionUserId = undefined;
    this.sessionRoleId = undefined;
  }

  private async ensureDirectusBrowserSession(): Promise<void> {
    if (!this.directusOnly || typeof window === 'undefined') return;
    const user = await this.currentUser();
    if (!user || !user.role?.id || !FINTRACKS_ROLE_IDS.has(user.role.id)) {
      throw new RepositoryError('A operação exige uma sessão Fintracks confirmada.', 401, 'SESSION_NOT_ESTABLISHED');
    }
  }

  async login(email: string, password: string) {
    this.clearSessionCache();
    try {
      const authBaseUrl = this.provider === 'pocketbase' ? this.fileGatewayBaseUrl : this.baseUrl;
      await this.request(authBaseUrl, '/auth/login', { method: 'POST', body: JSON.stringify({ email, password, mode: 'cookie' }) });
      const user = await this.currentUser();
      if (!user) throw new RepositoryError('O login foi aceito, mas o navegador não confirmou a sessão.', 401, 'SESSION_NOT_ESTABLISHED');
      return user;
    } catch (error) {
      this.clearSessionCache();
      throw error;
    }
  }

  async logout() {
    try {
      const authBaseUrl = this.provider === 'pocketbase' ? this.fileGatewayBaseUrl : this.baseUrl;
      await this.request(authBaseUrl, '/auth/logout', { method: 'POST' });
    } finally {
      this.clearSessionCache();
    }
  }

  private async readCurrentUser(): Promise<SessionUser> {
    const authBaseUrl = this.provider === 'pocketbase' ? this.fileGatewayBaseUrl : this.baseUrl;
    const sessionPath = this.provider === 'pocketbase' ? '/auth/session' : '/users/me?fields=id,email,first_name,last_name,role.id,role.name';
    const { data } = await this.request<Envelope<Record<string, unknown>>>(authBaseUrl, sessionPath);
    if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.id !== 'string' || !SESSION_USER_ID.test(data.id) || typeof data.email !== 'string' || !SESSION_USER_EMAIL.test(data.email)) {
      throw new RepositoryError('A autoridade de sessão retornou uma identidade inválida.', 502, 'SESSION_RESPONSE_INVALID');
    }
    const role = data.role as Record<string, unknown> | undefined;
    if (role !== undefined && (role === null || typeof role !== 'object' || Array.isArray(role) || typeof role.id !== 'string' || typeof role.name !== 'string')) {
      throw new RepositoryError('A autoridade de sessão retornou uma role inválida.', 502, 'SESSION_RESPONSE_INVALID');
    }
    if (!role || !FINTRACKS_ROLE_IDS.has(String(role.id))) throw new RepositoryError('A sessão não possui uma role Fintracks autorizada.', 403, 'ROLE_FORBIDDEN');
    const firstName = this.provider === 'pocketbase' ? data.firstName : data.first_name;
    const lastName = this.provider === 'pocketbase' ? data.lastName : data.last_name;
    const user: SessionUser = {
      id: data.id,
      email: data.email,
      ...(firstName ? { firstName: String(firstName) } : {}),
      ...(lastName ? { lastName: String(lastName) } : {}),
      ...(role ? { role: { id: String(role.id), name: String(role.name) } } : {}),
    };
    this.sessionUserId = user.id;
    this.sessionRoleId = user.role?.id;
    return user;
  }

  async currentUser() {
    try {
      return await this.readCurrentUser();
    } catch (error) {
      this.clearSessionCache();
      if (!(error instanceof RepositoryError) || error.status !== 401) throw error;
      if (this.provider === 'pocketbase') return null;
      try {
        await this.directus('/auth/refresh', { method: 'POST', body: JSON.stringify({}) });
        return await this.readCurrentUser();
      } catch (refreshError) {
        this.clearSessionCache();
        if (refreshError instanceof RepositoryError && (refreshError.status === 401 || (refreshError.status === 400 && refreshError.code === 'DIRECTUS_REQUEST_FAILED'))) return null;
        throw refreshError;
      }
    }
  }

  async listClients() {
    const data = await this.listClientPages<RawClient>(CLIENT_LIST_FIELDS, '&filter[status][_neq]=archived&sort=-date_updated');
    return data.map(mapClient);
  }

  async listArchivedClients() {
    const data = await this.listClientPages<RawClient>(CLIENT_LIST_FIELDS, '&filter[status][_eq]=archived&sort=-date_updated');
    return data.map(mapClient);
  }

  async getClient(id: string) {
    try {
      return mapClient(await this.rawClient(id));
    } catch (error) {
      if (error instanceof RepositoryError && error.status === 404) return null;
      throw error;
    }
  }

  async getCapturedFinanceData(clientId: string): Promise<CapturedFinanceData | null> {
    const row = await this.rawClient(clientId);
    return parseCapturedFinanceData(row.captured_finance, clientId);
  }

  async createClient(input: CreateClient) {
    const validated = assertInput(createClientSchema, input, 'Dados cadastrais inválidos. Revise nome, datas e contatos.');
    await this.ensureDirectusBrowserSession();
    if (this.directusOnly && this.sessionRoleId !== FINTRACKS_CONSULTANT_ROLE_ID && this.sessionRoleId !== FINTRACKS_MANAGEMENT_ROLE_ID) {
      throw new RepositoryError('A sessão não possui uma role Fintracks autorizada para criar clientes.', 403, 'ROLE_FORBIDDEN');
    }
    if (this.edgeBaseUrl && (!validated.id || !validated.operationAt)) {
      throw new RepositoryError('O cadastro exige ID e timestamp de comando estáveis para permitir reconciliação segura.', 422, 'CLIENT_OPERATION_ID_REQUIRED');
    }
    const id = validated.id ?? crypto.randomUUID();
    const operationAt = validated.operationAt ?? new Date().toISOString();
    const scenarioId = await deterministicUuid({ operation: 'client.create.scenario', clientId: id });
    const planId = await deterministicUuid({ operation: 'client.create.plan', clientId: id });
    const scenario = baseScenario(validated, scenarioId, planId, operationAt);
    const contacts = [
      validated.email ? { id: await deterministicUuid({ operation: 'client.create.contact', clientId: id, kind: 'email' }), client_id: id, kind: 'email' as const, value: validated.email, is_primary: true } : null,
      validated.phone ? { id: await deterministicUuid({ operation: 'client.create.contact', clientId: id, kind: 'mobile' }), client_id: id, kind: 'mobile' as const, value: validated.phone, is_primary: true } : null,
    ].filter((contact): contact is NonNullable<typeof contact> => contact !== null);
    const payload = {
      id,
      status: 'draft',
      full_name: validated.fullName.trim(),
	    short_name: validated.shortName,
	    birth_date: validated.birthDate,
	    stated_age: validated.statedAge,
	    address: {},
	    profession: validated.profession,
	    journey_mode: 'simplified',
	    version: 1,
      data_model_version: DATA_MODEL_VERSION,
      planning_data: { version: DOCUMENT_VERSION, scenarios: [scenario], goals: [], financials: [], guaranteeExposures: [], protection: null, exclusions: [], r5: { version: DOCUMENT_VERSION, pensionSimulations: [], financingSimulations: [], financingComparisons: [] } },
      calculation_history: { version: DOCUMENT_VERSION, snapshots: [], idempotency: [] },
      reports_data: { version: DOCUMENT_VERSION, reports: [] },
      follow_up_data: { version: DOCUMENT_VERSION, items: [], pipeline: { clientId: id, stage: 'new', version: 1, history: [] }, contributions: [], contributionOperations: [] },
      file_links: { version: DOCUMENT_VERSION, files: [] },
      audit_log: { version: DOCUMENT_VERSION, entries: [this.makeAuditEntry('client.created', id, undefined, undefined, { id: await deterministicUuid({ operation: 'client.create.audit', clientId: id }), at: operationAt })] },
      // O journal é um campo interno: no navegador, o proxy same-origin o
      // injeta no create antes do forwarding. Nunca o aceite no envelope
      // público, pois isso permitiria ao cliente comandar estado operacional.
      ...((this.edgeBaseUrl || this.directusOnly) ? {} : { operation_journal: { version: DOCUMENT_VERSION, pending: [], completedKeys: [] } }),
      ...(contacts.length > 0 ? { contacts: { create: contacts } } : {}),
    };
    let data: RawClient;
    try {
      const response = await this.directus<Envelope<RawClient>>(this.clientPath(), {
        method: 'POST',
        headers: { 'Idempotency-Key': `client-create:${id}` },
        body: JSON.stringify(payload),
      }) as Partial<Envelope<RawClient>>;
      const responseData = response.data as unknown;
      if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData) || ((this.edgeBaseUrl || this.directusOnly) && String((responseData as Record<string, unknown>).id ?? '') !== id)) {
        data = await this.rawClient(id);
      } else {
        data = responseData as RawClient;
      }
    } catch (error) {
      const canRecoverAmbiguousWrite = !(error instanceof RepositoryError)
        || ['UPSTREAM_RESPONSE_INVALID', 'DIRECTUS_RESPONSE_DEADLINE', 'DIRECTUS_REQUEST_FAILED', 'REQUEST_FAILED', 'UPSTREAM_UNAVAILABLE', 'DIRECTUS_UNAVAILABLE', 'CREATE_OUTCOME_UNKNOWN', 'OUTCOME_UNKNOWN'].includes(error.code);
      if (!canRecoverAmbiguousWrite) throw error;
      try {
        data = await this.rawClient(id);
      } catch {
        throw error;
      }
    }
    const persisted = this.edgeBaseUrl || this.directusOnly ? await this.rawClient(id) : data;
    assertClientMutationContent({ data: persisted }, id, 1, payload);
    const created = mapClient(persisted);
    const expectedFields = {
      status: 'draft' as const, fullName: validated.fullName.trim(), shortName: validated.shortName,
      birthDate: validated.birthDate, statedAge: validated.statedAge, profession: validated.profession,
      email: validated.email, phone: validated.phone,
    };
    for (const field of Object.keys(expectedFields) as Array<keyof typeof expectedFields>) {
      if (!sameDerivedValue(created[field], expectedFields[field])) {
        throw new RepositoryError(`A criação persistiu conteúdo divergente no campo ${field}.`, 502, 'CLIENT_RESPONSE_CONTENT_INVALID');
      }
    }
    if (this.edgeBaseUrl) {
      const provisioning = await this.getClientProvisioning(id);
      if (provisioning.state !== 'provisioned') {
        const code = provisioning.state === 'outcome_unknown' ? 'CREATE_OUTCOME_UNKNOWN' : provisioning.state === 'dead_letter' ? 'PROVISIONING_DEAD_LETTER' : 'PENDING_PROVISIONING';
        throw new RepositoryError('O cliente foi persistido, mas ainda não está disponível operacionalmente; reconcilie pelo mesmo identificador.', 409, code, provisioning.requestId);
      }
    }
    return created;
  }

  async getClientProvisioning(clientId: string): Promise<ClientProvisioningStatus> {
    if (this.provider === 'pocketbase' && !this.edgeBaseUrl) return pocketBaseProvisioningStatus(clientId);
    if (!this.edgeBaseUrl) throw new RepositoryError('Status de provisioning exige o gateway Edge.', 503, 'EDGE_GATEWAY_REQUIRED');
    const response = await this.request<Envelope<unknown>>(this.edgeBaseUrl, `/client-provisioning/${encodeURIComponent(clientId)}`);
    return normalizeClientProvisioning(response.data, clientId);
  }

  async reconcileClientProvisioning(clientId: string): Promise<ClientProvisioningStatus> {
    if (this.provider === 'pocketbase' && !this.edgeBaseUrl) return pocketBaseProvisioningStatus(clientId);
    if (!this.edgeBaseUrl) throw new RepositoryError('Reconciliação de provisioning exige o gateway Edge.', 503, 'EDGE_GATEWAY_REQUIRED');
    const response = await this.request<Envelope<unknown>>(this.edgeBaseUrl, `/client-provisioning/${encodeURIComponent(clientId)}/reconcile`, { method: 'POST', body: JSON.stringify({}) });
    return normalizeClientProvisioning(response.data, clientId);
  }

  async updateClient(id: string, patch: UpdateClient, expectedVersion: number) {
    const validatedPatch = assertInput(updateClientSchema, patch, 'Atualização cadastral inválida. Revise nome, data e contatos.');
    const current = await this.rawClient(id);
    if (this.directusOnly && !this.sessionUserId && !this.syntheticSession) {
      const user = await this.currentUser();
      if (!user) throw new RepositoryError('A mutação exige uma sessão autenticada para autoria.', 401, 'SESSION_EXPIRED');
    }
    if (this.directusOnly) this.assertDirectusClientProfileMutationAllowed(current, validatedPatch);
    if ((current.contacts ?? []).some((contact) => contact.client_id !== undefined && contact.client_id !== id)) {
      throw new RepositoryError('O cliente retornou um contato de outro proprietário.', 502, 'CONTACT_OWNERSHIP_INVALID');
    }
    this.assertVersion(current, expectedVersion);
    const requestedFields: Record<string, unknown> = {};
    if (validatedPatch.status !== undefined) requestedFields.status = validatedPatch.status;
    if (validatedPatch.fullName !== undefined) requestedFields.full_name = validatedPatch.fullName.trim();
    if (validatedPatch.shortName !== undefined) requestedFields.short_name = validatedPatch.shortName;
    if (validatedPatch.birthDate !== undefined) requestedFields.birth_date = validatedPatch.birthDate;
    if (validatedPatch.statedAge !== undefined) requestedFields.stated_age = validatedPatch.statedAge;
    if (validatedPatch.profession !== undefined) requestedFields.profession = validatedPatch.profession?.trim() || null;
    const currentFields: Record<string, unknown> = {
      status: current.status,
      full_name: current.full_name,
      short_name: current.short_name ?? null,
      birth_date: current.birth_date ?? null,
      stated_age: current.stated_age ?? null,
      profession: current.profession ?? null,
    };
    const fields = Object.fromEntries(Object.entries(requestedFields).filter(([field, value]) => !sameDerivedValue(currentFields[field], value)));
    const contactOps: { create: Array<Record<string, unknown>>; update: Array<Record<string, unknown>> } = { create: [], update: [] };
	    const addContactOperation = (kind: 'email' | 'mobile', value: string | null | undefined) => {
	      if (value === undefined) return;
	      const existing = (current.contacts ?? []).find((contact) => contact.is_primary && (kind === 'email'
	        ? contact.kind === 'email'
	        : contact.kind === 'mobile' || contact.kind === 'phone'));
	      if (existing?.id && value === null) contactOps.update.push({ id: existing.id, kind, is_primary: false });
	      else if (existing?.id && value !== null) {
	        const sameKind = kind === 'mobile' ? existing.kind === 'mobile' || existing.kind === 'phone' : existing.kind === kind;
	        if (existing.value === value && existing.is_primary === true && sameKind) return;
	        contactOps.update.push({ id: existing.id, kind, value, is_primary: true });
	      }
	      else if (value !== null) contactOps.create.push({ id: crypto.randomUUID(), client_id: id, kind, value, is_primary: true });
	    };
    addContactOperation('email', validatedPatch.email);
    addContactOperation('mobile', validatedPatch.phone);
    const changed = [
      ...Object.keys(fields),
      ...[...contactOps.create, ...contactOps.update].flatMap((item) => item.kind === 'email' ? ['email'] : ['phone']),
    ].sort();
    if (changed.length === 0) throw new Error('Patch vazio');
    const log = writableAuditLog(current.audit_log);
    const mutationBody = {
      ...fields,
      version: expectedVersion + 1,
      data_model_version: DATA_MODEL_VERSION,
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry(changed.length === 1 && changed[0] === 'status' ? 'client.status_changed' : 'client.updated', id, changed)] },
      ...(contactOps.create.length > 0 || contactOps.update.length > 0 ? { contacts: contactOps } : {}),
    };
    const response = this.provider === 'pocketbase'
      ? await this.request<Envelope<RawClient>>(this.documentGatewayBaseUrl(), `/client-document/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(mutationBody) })
      : this.edgeBaseUrl
        ? undefined
        : await this.directus<Envelope<RawClient>>(this.clientMutationPath(id, expectedVersion), {
        method: 'PATCH',
        body: JSON.stringify(mutationBody),
      });
    if (response && Array.isArray(response.data) && response.data.length === 0) {
      throw new RepositoryError('A versão do cliente mudou antes da gravação.', 409, 'VERSION_CONFLICT');
    }
    const expectedFields = {
      ...(Object.prototype.hasOwnProperty.call(fields, 'status') ? { status: validatedPatch.status } : {}),
      ...(Object.prototype.hasOwnProperty.call(fields, 'full_name') ? { fullName: String(fields.full_name) } : {}),
      ...(Object.prototype.hasOwnProperty.call(fields, 'short_name') ? { shortName: fields.short_name as string | null } : {}),
      ...(Object.prototype.hasOwnProperty.call(fields, 'birth_date') ? { birthDate: fields.birth_date as string | null } : {}),
      ...(Object.prototype.hasOwnProperty.call(fields, 'stated_age') ? { statedAge: fields.stated_age as number | null } : {}),
      ...(Object.prototype.hasOwnProperty.call(fields, 'profession') ? { profession: fields.profession as string | null } : {}),
      ...(changed.includes('email') ? { email: validatedPatch.email } : {}),
      ...(changed.includes('phone') ? { phone: validatedPatch.phone } : {}),
    };
    if (this.edgeBaseUrl) {
      const persisted = await this.patchClientDocument(id, expectedVersion + 1, mutationBody);
      if (!persisted) throw new RepositoryError('A mutação persistida não retornou o documento do cliente.', 502, 'CLIENT_RESPONSE_INVALID');
      const mapped = mapClient(preserveCapturedFinanceReadback(persisted, current));
      for (const field of Object.keys(expectedFields) as Array<keyof typeof expectedFields>) {
        if (!sameDerivedValue(mapped[field], expectedFields[field])) throw new RepositoryError(`A mutação persistida diverge no campo cadastral ${String(field)}.`, 502, 'CLIENT_RESPONSE_CONTENT_INVALID');
      }
      return mapped;
    }
    assertClientMutationFields(response!, id, expectedVersion + 1, expectedFields);
    const persistedData = Array.isArray(response!.data) ? response!.data[0] : response!.data;
    if (!persistedData || typeof persistedData !== 'object' || Array.isArray(persistedData)) throw new RepositoryError('Cliente persistido inválido.', 422, 'VALIDATION_ERROR');
    return mapClient(preserveCapturedFinanceReadback(persistedData as RawClient, current));
  }

  async listFamilyMembers(clientId: string) {
    const fields = 'id,client_id,full_name,relationship,birth_date,financially_dependent,succession_dependent,financial_dependency_end,protection_needed,version,date_created,date_updated';
    const familyGatewayBaseUrl = this.edgeBaseUrl || (this.directusOnly ? this.fileGatewayBaseUrl : '');
    if (!familyGatewayBaseUrl) await this.ensureDirectusBrowserSession();
    if (familyGatewayBaseUrl) {
      const response = await this.request<Envelope<RawFamilyMember[]>>(familyGatewayBaseUrl, `/family/${encodeURIComponent(clientId)}`);
      if (!Array.isArray(response.data) || response.data.some((item) => String(item.client_id) !== clientId)) throw new RepositoryError('Resposta de familiares não corresponde ao cliente.', 502, 'FAMILY_RESPONSE_INVALID');
      const members = response.data.map(normalizeFamilyMember);
      if (new Set(members.map((member) => member.id)).size !== members.length) throw new RepositoryError('Resposta de familiares contém ID duplicado.', 502, 'FAMILY_RESPONSE_INVALID');
      return members;
    }
    const data: RawFamilyMember[] = [];
    for (let offset = 0; offset <= MAX_PAGED_OFFSET; offset += 200) {
      const page = await this.directus<Envelope<RawFamilyMember[]>>(`/items/fintracks_family_members?fields=${encodeURIComponent(fields)}&filter[client_id][_eq]=${encodeURIComponent(clientId)}&sort=id&limit=200&offset=${offset}&meta=total_count`);
      if (!Array.isArray(page.data)) throw new RepositoryError('Resposta de familiares inválida.', 502, 'FAMILY_RESPONSE_INVALID');
      if (page.data.some((item) => String(item.client_id ?? '') !== clientId)) throw new RepositoryError('Familiar pertence a outro cliente.', 502, 'FAMILY_OWNERSHIP_INVALID');
      data.push(...page.data);
      const total = Number(page.meta?.total_count);
      if (page.data.length < 200 || (Number.isInteger(total) && total >= 0 && data.length >= total)) break;
      if (offset === MAX_PAGED_OFFSET) throw new Error('Resultado excede o limite de paginação de 100.200 registros');
    }
    const members = data.map(normalizeFamilyMember);
    if (new Set(members.map((member) => member.id)).size !== members.length) throw new RepositoryError('Resposta de familiares contém ID duplicado.', 502, 'FAMILY_RESPONSE_INVALID');
    return members;
  }

  async createFamilyMember(input: CreateFamilyMemberInput) {
    const validated = assertInput(createFamilyMemberSchema, input, 'Dados familiares inválidos. Revise cliente, datas e dependências.');
    const { clientId, ...body } = validated;
    const operationKey = `family.create:${clientId}:${await sha256(body)}`;
    const familyGatewayBaseUrl = this.edgeBaseUrl || (this.directusOnly ? this.fileGatewayBaseUrl : '');
    if (familyGatewayBaseUrl) {
      const { data } = await this.request<Envelope<RawFamilyMember>>(familyGatewayBaseUrl, `/family/${encodeURIComponent(clientId)}`, { method: 'POST', headers: { 'Idempotency-Key': operationKey }, body: JSON.stringify(body) });
      const normalized = normalizeFamilyMember(data);
      if (normalized.clientId !== validated.clientId || normalized.version !== 1) throw new RepositoryError('Resposta de criação de familiar não corresponde ao cliente.', 502, 'FAMILY_RESPONSE_MISMATCH');
      assertFamilyFields(normalized, validated, 'criação de familiar');
      return normalized;
    }
    if (!this.edgeBaseUrl) {
      if (this.directusOnly) this.assertDirectusMutationClientAllowed(await this.rawClient(clientId));
      const id = this.directusOnly
        ? await deterministicUuid(`family.create:${clientId}:${operationKey}`)
        : crypto.randomUUID();
      const payload = {
        id, client_id: clientId, full_name: body.fullName, relationship: body.relationship,
        birth_date: body.birthDate ?? null, financially_dependent: body.financiallyDependent,
        succession_dependent: body.successionDependent, financial_dependency_end: body.financialDependencyEnd ?? null,
        protection_needed: body.protectionNeeded, version: 1,
      };
      const readPersisted = () => this.directus<Envelope<RawFamilyMember>>(`/items/fintracks_family_members/${encodeURIComponent(id)}?fields=id,client_id,full_name,relationship,birth_date,financially_dependent,succession_dependent,financial_dependency_end,protection_needed,version,date_created,date_updated`);
      const confirm = (response: Envelope<RawFamilyMember>) => {
        if (String(response.data.id ?? '') !== id || String(response.data.client_id ?? '') !== clientId) throw new RepositoryError('Resposta de criação de familiar não comprova o ID e cliente persistidos.', 502, 'FAMILY_RESPONSE_MISMATCH');
        const normalized = normalizeFamilyMember(response.data);
        if (normalized.clientId !== validated.clientId || normalized.version !== 1) throw new RepositoryError('Resposta de criação de familiar não corresponde ao cliente.', 502, 'FAMILY_RESPONSE_MISMATCH');
        assertFamilyFields(normalized, validated, 'criação de familiar');
        return normalized;
      };
      try {
        let response = await this.directus<Envelope<RawFamilyMember>>('/items/fintracks_family_members', { method: 'POST', headers: { 'Idempotency-Key': operationKey }, body: JSON.stringify(payload) });
        if (this.directusOnly) response = await readPersisted();
        return confirm(response);
      } catch (error) {
        if (!this.directusOnly) throw error;
        try {
          return confirm(await readPersisted());
        } catch (readError) {
          if (readError instanceof RepositoryError && readError.status === 404) throw error;
          throw new RepositoryError('Não foi possível confirmar a criação do familiar; reconcilie antes de repetir.', 503, 'FAMILY_OUTCOME_UNKNOWN', error instanceof RepositoryError ? error.requestId : undefined);
        }
      }
    }
    throw new RepositoryError('Gateway familiar não configurado.', 503, 'FAMILY_GATEWAY_REQUIRED');
  }

  async updateFamilyMember(clientId: string, memberId: string, patch: UpdateFamilyMemberInput, expectedVersion: number) {
    const validatedPatch = assertInput(updateFamilyMemberSchema, patch, 'Atualização familiar inválida. Revise campos e dependências.');
    const familyGatewayBaseUrl = this.edgeBaseUrl || (this.directusOnly ? this.fileGatewayBaseUrl : '');
    if (familyGatewayBaseUrl) {
      const { data } = await this.request<Envelope<RawFamilyMember>>(familyGatewayBaseUrl, `/family/${encodeURIComponent(clientId)}/${encodeURIComponent(memberId)}`, { method: 'PATCH', headers: { 'Idempotency-Key': `family.update:${clientId}:${memberId}:${expectedVersion}:${await sha256(validatedPatch)}` }, body: JSON.stringify({ ...validatedPatch, expectedVersion }) });
      const normalized = normalizeFamilyMember(data);
      if (normalized.clientId !== clientId || normalized.id !== memberId || normalized.version !== expectedVersion + 1) throw new RepositoryError('Resposta de atualização de familiar não corresponde à versão esperada.', 502, 'FAMILY_RESPONSE_MISMATCH');
      assertFamilyFields(normalized, patch, 'atualização de familiar');
      return normalized;
    }
    if (!this.edgeBaseUrl) {
      if (this.directusOnly) this.assertDirectusMutationClientAllowed(await this.rawClient(clientId));
      const current = (await this.listFamilyMembers(clientId)).find((item) => item.id === memberId);
      if (!current) throw new RepositoryError('Familiar não encontrado para este cliente.', 404, 'FAMILY_NOT_FOUND');
      if (current.version !== expectedVersion) throw new RepositoryError('O familiar foi alterado em outra sessão.', 409, 'VERSION_CONFLICT');
      const fields: Record<string, unknown> = { version: expectedVersion + 1 };
      if (validatedPatch.fullName !== undefined) fields.full_name = validatedPatch.fullName;
      if (validatedPatch.relationship !== undefined) fields.relationship = validatedPatch.relationship;
      if (validatedPatch.birthDate !== undefined) fields.birth_date = validatedPatch.birthDate;
      if (validatedPatch.financiallyDependent !== undefined) fields.financially_dependent = validatedPatch.financiallyDependent;
      if (validatedPatch.successionDependent !== undefined) fields.succession_dependent = validatedPatch.successionDependent;
      if (validatedPatch.financialDependencyEnd !== undefined) fields.financial_dependency_end = validatedPatch.financialDependencyEnd;
      if (validatedPatch.protectionNeeded !== undefined) fields.protection_needed = validatedPatch.protectionNeeded;
      const operationKey = `family.update:${clientId}:${memberId}:${expectedVersion}:${await sha256(validatedPatch)}`;
      const readPersisted = () => this.directus<Envelope<RawFamilyMember>>(`/items/fintracks_family_members/${encodeURIComponent(memberId)}?fields=${encodeURIComponent(FAMILY_FIELDS)}`);
      const confirm = (response: Envelope<RawFamilyMember>) => {
        if (String(response.data.id ?? '') !== memberId || String(response.data.client_id ?? '') !== clientId) throw new RepositoryError('Resposta de atualização de familiar não comprova o ID e cliente persistidos.', 502, 'FAMILY_RESPONSE_MISMATCH');
        const normalized = normalizeFamilyMember(response.data);
        if (normalized.clientId !== clientId || normalized.id !== memberId || normalized.version !== expectedVersion + 1) throw new RepositoryError('Resposta de atualização de familiar não corresponde à versão esperada.', 502, 'FAMILY_RESPONSE_MISMATCH');
        assertFamilyFields(normalized, patch, 'atualização de familiar');
        return normalized;
      };
      try {
        return confirm(await this.directus<Envelope<RawFamilyMember>>(`/items/fintracks_family_members/${encodeURIComponent(memberId)}?filter[id][_eq]=${encodeURIComponent(memberId)}&filter[client_id][_eq]=${encodeURIComponent(clientId)}&filter[version][_eq]=${encodeURIComponent(String(expectedVersion))}&limit=1`, { method: 'PATCH', headers: { 'Idempotency-Key': operationKey }, body: JSON.stringify(fields) }));
      } catch (error) {
        if (!(error instanceof RepositoryError) || error.code !== 'OUTCOME_UNKNOWN') throw error;
        try {
          return confirm(await readPersisted());
        } catch (readError) {
          if (readError instanceof RepositoryError && readError.status === 404) throw error;
          throw new RepositoryError('Não foi possível confirmar a atualização do familiar; reconcilie antes de repetir.', 503, 'FAMILY_OUTCOME_UNKNOWN', error.requestId);
        }
      }
    }
    throw new RepositoryError('Gateway familiar não configurado.', 503, 'FAMILY_GATEWAY_REQUIRED');
  }

  async listClientFiles(clientId: string) {
    return fileLinksData((await this.rawClient(clientId)).file_links, clientId).files.filter((file) => file.status === 'active');
  }

  async listArchivedClientFiles(clientId: string) {
    return fileLinksData((await this.rawClient(clientId)).file_links, clientId).files.filter((file) => file.status === 'archived');
  }

  private async persistClientFileLink(input: CreateClientFileLinkInput, linkId: string = crypto.randomUUID()) {
    const validated = assertInput(createClientFileLinkSchema, input, 'Vínculo de arquivo inválido.');
    if (this.directusOnly && !validated.contentHash) throw new RepositoryError('Vínculo Directus-only exige hash SHA-256 do conteúdo.', 422, 'FILE_HASH_REQUIRED');
    if (!DIRECTUS_FILE_REF.test(validated.storageRef)) throw new RepositoryError('Fallback sintético exige referência Directus com UUID.', 422, 'FILE_STORAGE_REF_INVALID');
    if (!this.filesFolderId) throw new RepositoryError('Fallback sintético exige a pasta privada Fintracks canônica.', 422, 'FILE_STORAGE_FOLDER_REQUIRED');
    const storageId = DIRECTUS_FILE_REF.exec(validated.storageRef)?.[1];
    const physical = storageId ? await this.directus<Envelope<RawDirectusFile>>(`/files/${encodeURIComponent(storageId)}?fields=id,folder,type,filesize,description`) : null;
    let description: Record<string, unknown> | null = null;
    try { description = physical?.data.description ? JSON.parse(physical.data.description) as Record<string, unknown> : null; } catch { description = null; }
    if (!physical?.data || physical.data.id !== storageId || physical.data.folder !== this.filesFolderId || physical.data.type !== validated.mimeType || Number(physical.data.filesize) !== validated.sizeBytes || description?.namespace !== 'fintracks' || description.clientId !== validated.clientId || description.fileLinkId !== linkId) {
      throw new RepositoryError('Arquivo físico não comprova a pasta, o cliente, o vínculo ou o tipo solicitado.', 422, 'FILE_STORAGE_MISMATCH');
    }
    const current = await this.rawClient(validated.clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const document = fileLinksData(current.file_links, validated.clientId);
    const file: ClientFileLink = {
      id: linkId, clientId: validated.clientId, name: validated.name.trim(), category: validated.category.trim(),
      mimeType: validated.mimeType, sizeBytes: validated.sizeBytes, ...(validated.contentHash ? { contentHash: validated.contentHash } : {}), storageRef: validated.storageRef,
      status: 'active', uploadedAt: new Date().toISOString(), uploadedBy: validated.uploadedBy ?? this.sessionUserId ?? null, version: 1,
    };
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(validated.clientId, Number(current.version) + 1, { version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION, file_links: { version: document.version + 1, files: [file, ...document.files] }, audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('file.linked', file.id, ['file_links'])] } });
    return file;
  }

  async createClientFile(input: CreateClientFileLinkInput) {
    // Caminho legado de referência: não envia bytes nem cria receipt de upload.
    // Uploads novos devem usar o gateway Edge ou uploadClientFile.
    if (this.directusOnly) throw new RepositoryError('createClientFile é somente compatibilidade sintética; use o gateway privado de arquivos.', 503, 'FILE_GATEWAY_REQUIRED');
    if (this.edgeBaseUrl || (!this.allowDirectusFileFallback && !this.directusOnly)) throw new Error('Use uploadClientFile para materializar bytes no storage privado.');
    return this.persistClientFileLink(input);
  }

  async uploadClientFile(input: UploadClientFileInput) {
    const { file, idempotencyKey: requestedIdempotencyKey, ...metadata } = input;
    if (!file || typeof file.size !== 'number' || !Number.isInteger(file.size) || file.size <= 0 || typeof file.type !== 'string' || typeof file.arrayBuffer !== 'function') {
      throw new RepositoryError('O upload deve conter um arquivo não vazio válido.', 422, 'FILE_UPLOAD_INPUT_INVALID');
    }
    let validated: CreateClientFileLinkInput;
    try {
      validated = assertInput(createClientFileLinkSchema, { ...metadata, storageRef: 'private://upload-input' }, 'Metadados de upload inválidos.');
    } catch (error) {
      throw new RepositoryError(error instanceof Error ? error.message : 'Metadados de upload inválidos.', 422, 'FILE_UPLOAD_INPUT_INVALID');
    }
    const actualMime = file.type || 'application/octet-stream';
    if (validated.sizeBytes !== file.size || validated.mimeType !== actualMime) {
      throw new RepositoryError('O MIME e o tamanho informados devem coincidir com o arquivo selecionado.', 422, 'FILE_UPLOAD_INPUT_INVALID');
    }
    if (requestedIdempotencyKey !== undefined && !/^[A-Za-z0-9._:-]{8,200}$/.test(requestedIdempotencyKey.trim())) {
      throw new RepositoryError('A chave de idempotência do upload é inválida.', 422, 'FILE_UPLOAD_INPUT_INVALID');
    }
    const contentBytes = await file.arrayBuffer();
    const contentDigest = await crypto.subtle.digest('SHA-256', contentBytes);
    const contentHash = Array.from(new Uint8Array(contentDigest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const operationKey = requestedIdempotencyKey !== undefined
      ? canonicalIdempotencyKey(requestedIdempotencyKey)
      : `file-upload-${await sha256({ clientId: input.clientId, name: input.name.trim(), category: input.category.trim(), mimeType: actualMime, sizeBytes: file.size, contentHash })}`;
    try { assertClientFileUploadPolicy({ mimeType: actualMime, sizeBytes: file.size }); }
    catch (error) { throw new RepositoryError(error instanceof Error ? error.message : 'Arquivo não atende à política de upload.', 422, 'FILE_UPLOAD_INPUT_INVALID'); }
    if (!this.filesFolderId) throw new Error('Storage privado Fintracks não configurado: informe VITE_DIRECTUS_FILES_FOLDER_ID.');
    if (this.fileGatewayBaseUrl) {
      const form = new FormData();
      form.append('file', input.file, input.name.trim());
      form.append('name', input.name.trim());
      form.append('category', input.category.trim());
      const response = await this.request<Envelope<unknown>>(this.fileGatewayBaseUrl, `/private-files/${encodeURIComponent(input.clientId)}`, { method: 'POST', headers: { 'Idempotency-Key': operationKey }, body: form });
      const link = assertInput(clientFileLinkSchema, response.data, 'Vínculo de arquivo retornado pelo Worker inválido.');
      if (link.clientId !== input.clientId || link.name !== input.name.trim() || link.category !== input.category.trim() || link.mimeType !== input.mimeType || link.sizeBytes !== input.sizeBytes || link.contentHash !== contentHash || link.status !== 'active') {
        throw new RepositoryError('Vínculo de arquivo retornado não corresponde ao upload solicitado.', 502, 'FILE_RESPONSE_MISMATCH');
      }
      return link;
    }
    const fileId = await deterministicUuid({ operation: 'file.upload', clientId: input.clientId, operationKey });
    if (!this.allowDirectusFileFallback && !this.directusOnly) throw new Error('Use o gateway Edge ou habilite o modo Directus-only para validar o upload privado.');
    if (this.directusOnly) {
      const current = await this.rawClient(input.clientId);
      const existing = fileLinksData(current.file_links, input.clientId).files.find((item) => item.id === fileId);
      if (existing) {
        const sameUpload = existing.clientId === input.clientId && existing.name === input.name.trim() && existing.category === input.category.trim() && existing.mimeType === actualMime && existing.sizeBytes === file.size && existing.contentHash === contentHash;
        if (sameUpload) return existing;
        throw new RepositoryError('A chave de idempotência do upload já está vinculada a outro conteúdo.', 409, 'FILE_IDEMPOTENCY_CONFLICT');
      }
      this.assertDirectusMutationClientAllowed(current);
      this.assertPrivateFileTransport();
    }
    const safeName = input.name.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').slice(0, 160) || 'arquivo';
    const form = new FormData();
    form.append('title', `Fintracks/${input.clientId}/${input.name.trim()}`);
    form.append('filename_download', input.name.trim());
    form.append('description', JSON.stringify({ namespace: 'fintracks', clientId: input.clientId, fileLinkId: fileId }));
    if (this.filesFolderId) form.append('folder', this.filesFolderId);
    // O Directus vincula propriedades ao próximo arquivo; metadata deve vir antes de `file`.
    form.append('file', input.file, `${fileId}-${safeName}`);
    let uploadedId: string | undefined;
    try {
      const { data } = await this.directus<Envelope<RawDirectusFile>>('/files', { method: 'POST', body: form });
      if (typeof data.id === 'string' && DIRECTUS_FILE_REF.test(`directus://files/${data.id}`)) uploadedId = data.id;
      const expectedType = input.mimeType || 'application/octet-stream';
      const expectedSize = input.sizeBytes || input.file.size;
      let description: Record<string, unknown> | null = null;
      try {
        const parsed = JSON.parse(String(data.description ?? '')) as unknown;
        description = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
      } catch { description = null; }
      if (!DIRECTUS_FILE_REF.test(`directus://files/${data.id}`) || data.folder !== this.filesFolderId || data.type !== expectedType || Number(data.filesize) !== expectedSize || description?.namespace !== 'fintracks' || description.clientId !== input.clientId || description.fileLinkId !== fileId) {
        throw new RepositoryError('Metadata do arquivo retornada pelo Directus não corresponde ao contrato privado.', 502, 'FILE_METADATA_INVALID');
      }
      return await this.persistClientFileLink({
        clientId: input.clientId,
        name: input.name.trim(),
        category: input.category.trim(),
        mimeType: data.type ?? input.mimeType ?? 'application/octet-stream',
        sizeBytes: Number(data.filesize ?? input.sizeBytes ?? input.file.size),
        contentHash,
        storageRef: `directus://files/${data.id}`,
        ...(input.uploadedBy === undefined ? {} : { uploadedBy: input.uploadedBy }),
      }, fileId);
    } catch (error) {
      if (uploadedId) {
        try { await this.directus(`/files/${encodeURIComponent(uploadedId)}`, { method: 'DELETE' }); } catch { /* preserve original upload/link error */ }
      }
      throw error;
    }
  }

  async downloadClientFile(clientId: string, fileId: string) {
    const document = fileLinksData((await this.rawClient(clientId)).file_links, clientId);
    const file = document.files.find((item) => item.id === fileId && item.status === 'active');
    if (!file) throw new Error('Arquivo não encontrado');
    const match = DIRECTUS_FILE_REF.exec(file.storageRef);
    if (!match?.[1]) throw new Error('Arquivo sem conteúdo binário disponível');
    if (this.fileGatewayBaseUrl) return this.blobRequest(this.fileGatewayBaseUrl, `/private-files/${encodeURIComponent(clientId)}/${encodeURIComponent(fileId)}`);
    this.assertPrivateFileTransport();
    if (!this.allowDirectusFileFallback && !this.directusOnly) throw new Error('Use o gateway Edge ou habilite o modo Directus-only para validar o download privado.');
    const blob = await this.blobRequest(this.baseUrl, `/assets/${encodeURIComponent(match[1])}`);
    if (!file.contentHash || !/^[a-f0-9]{64}$/.test(file.contentHash)) throw new RepositoryError('O vínculo do arquivo não possui uma impressão de integridade verificável.', 502, 'FILE_CONTENT_HASH_MISSING');
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    const actualHash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    if (actualHash !== file.contentHash) throw new RepositoryError('O conteúdo recebido não corresponde à impressão de integridade persistida.', 502, 'FILE_CONTENT_HASH_MISMATCH');
    return blob;
  }

  async archiveClientFile(clientId: string, fileId: string, expectedVersion: number) {
    if (this.fileGatewayBaseUrl) {
      const response = await this.request<Envelope<unknown>>(this.fileGatewayBaseUrl, `/private-files/${encodeURIComponent(clientId)}/${encodeURIComponent(fileId)}/archive`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
      const file = assertInput(clientFileLinkSchema, response.data, 'Arquivo arquivado retornado pelo Worker inválido.');
      const validVersion = response.meta?.noOp === true ? (file.version === expectedVersion || file.version === expectedVersion + 1) : file.version === expectedVersion + 1;
      if (file.clientId !== clientId || file.id !== fileId || file.status !== 'archived' || !validVersion) throw new Error('Resposta de arquivamento de arquivo inválida');
      return file;
    }
    const current = await this.rawClient(clientId);
    const document = fileLinksData(current.file_links, clientId);
    const index = document.files.findIndex((file) => file.id === fileId);
    if (index < 0) throw new Error('Arquivo não encontrado');
    const file = document.files[index]!;
    if (file.version !== expectedVersion) throw new Error('Conflito de versão');
    if (file.status === 'archived') return file;
    this.assertPrivateFileTransport();
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const archived = { ...file, status: 'archived' as const, version: expectedVersion + 1 };
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(clientId, Number(current.version) + 1, { version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION, file_links: { version: document.version + 1, files: document.files.map((item, itemIndex) => itemIndex === index ? archived : item) }, audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('file.archived', fileId, ['file_links'])] } });
    return archived;
  }

  async restoreClientFile(clientId: string, fileId: string, expectedVersion: number) {
    if (this.fileGatewayBaseUrl) {
      const response = await this.request<Envelope<unknown>>(this.fileGatewayBaseUrl, `/private-files/${encodeURIComponent(clientId)}/${encodeURIComponent(fileId)}/restore`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
      const file = assertInput(clientFileLinkSchema, response.data, 'Arquivo restaurado retornado pelo Worker inválido.');
      const validVersion = response.meta?.noOp === true ? (file.version === expectedVersion || file.version === expectedVersion + 1) : file.version === expectedVersion + 1;
      if (file.clientId !== clientId || file.id !== fileId || file.status !== 'active' || !validVersion) throw new Error('Resposta de restauração de arquivo inválida');
      return file;
    }
    const current = await this.rawClient(clientId);
    const document = fileLinksData(current.file_links, clientId);
    const index = document.files.findIndex((file) => file.id === fileId);
    if (index < 0) throw new Error('Arquivo não encontrado');
    const file = document.files[index]!;
    if (file.version !== expectedVersion) throw new Error('Conflito de versão');
    if (file.status === 'active') return file;
    this.assertPrivateFileTransport();
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const restored = { ...file, status: 'active' as const, version: expectedVersion + 1 };
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(clientId, Number(current.version) + 1, { version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION, file_links: { version: document.version + 1, files: document.files.map((item, itemIndex) => itemIndex === index ? restored : item) }, audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('file.restored', fileId, ['file_links'])] } });
    return restored;
  }

  async reconcileOperations(clientId: string): Promise<OperationReconcileResult> {
    if (!this.edgeBaseUrl) {
      // O modo Directus-only não mantém outbox remoto: aportes confirmados ficam
      // no mesmo documento e são validados na leitura. Portanto não há reparo
      // implícito a declarar; uma operação ambígua deve ser relida pela chave.
      const document = await verifiedFollowUpData((await this.rawClient(clientId)).follow_up_data, clientId);
      const confirmed = document.contributionOperations.filter((operation) => document.contributions.some((item) => item.id === operation.contributionId && item.clientId === clientId));
      return operationReconcileResultSchema.parse({ clientId, repaired: 0, pending: document.contributionOperations.length - confirmed.length });
    }
    const response = await this.request<Envelope<unknown>>(this.edgeBaseUrl, `/operations/reconcile/${encodeURIComponent(clientId)}`, { method: 'POST', body: JSON.stringify({}) });
    const result = assertInput(operationReconcileResultSchema, response.data, 'Resultado de reconciliação inválido.');
    if (result.clientId !== clientId) throw new Error('Reconciliação retornou cliente divergente');
    return result;
  }

  async createScenario(clientId: string, input: CreateScenarioInput) {
    const validatedInput = assertInput(createScenarioSchema, input, 'Dados do cenário inválidos.');
    const current = await this.rawClient(clientId);
    const planning = planningData(current.planning_data, clientId);
    const source = validatedInput.sourceScenarioId
      ? planning.scenarios.find((item) => item.id === validatedInput.sourceScenarioId)
      : planning.scenarios.find((item) => item.isPrimary) ?? planning.scenarios[0];
    if (!source) throw new Error('Não há cenário-base para duplicar');
    const name = validatedInput.name.trim();
    const scenarioId = await deterministicUuid({ operation: 'scenario.create', clientId, sourceScenarioId: source.id, input: { ...validatedInput, name } });
    const existingScenario = planning.scenarios.find((item) => item.id === scenarioId);
    if (existingScenario) return existingScenario;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const scenario: PlanScenario = {
      ...source,
      id: scenarioId,
      // A duplicação cria outra versão do mesmo plano do cliente; um planId
      // novo transformaria o cenário em uma entidade órfã no envelope.
      planId: source.planId,
      name,
      isPrimary: validatedInput.makePrimary === true,
      visibleInComparison: true,
      version: 1,
      assumptionsVersion: 1,
      assumptions: { ...source.assumptions },
    };
    const scenarios = validatedInput.makePrimary
      ? [...planning.scenarios.map((item) => item.isPrimary ? { ...item, isPrimary: false, version: item.version + 1 } : item), scenario]
      : [...planning.scenarios, scenario];
    const log = writableAuditLog(current.audit_log);
    const persisted = await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1,
      data_model_version: DATA_MODEL_VERSION,
      planning_data: { ...planning, version: planning.version + 1, scenarios, goals: planning.goals, financials: planning.financials, protection: planning.protection, r5: planning.r5 },
      audit_log: {
        version: log.version + 1,
        entries: [...log.entries, this.makeAuditEntry('scenario.created', scenario.id, ['planning_data'])],
      },
    });
    if (!persisted) return scenario;
    const persistedPlanning = planningData(persisted.planning_data, clientId);
    const persistedScenario = persistedPlanning.scenarios.find((item) => item.id === scenario.id);
    if (!persistedScenario || !sameDerivedValue(persistedScenario, scenario)) throw new RepositoryError('A criação do cenário não confirmou o conteúdo persistido.', 502, 'SCENARIO_RESPONSE_CONTENT_INVALID');
    return persistedScenario;
  }

  async listScenarios(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).scenarios;
  }

  async getScenario(clientId: string, scenarioId: string) {
    return (await this.listScenarios(clientId)).find((item) => item.id === scenarioId) ?? null;
  }

  async saveScenario(clientId: string, scenario: PlanScenario) {
    const validatedScenario = assertInput(planScenarioSchema, scenario, 'Cenário inválido.');
    const assumptions = scenarioAssumptionsSchema.parse(validatedScenario.assumptions);
    const current = await this.rawClient(clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const planning = planningData(current.planning_data, clientId);
    const index = planning.scenarios.findIndex((item) => item.id === validatedScenario.id);
    if (index < 0) throw new Error('Cenário não encontrado');
    if (planning.scenarios[index]?.planId !== validatedScenario.planId) throw new RepositoryError('O cenário não pertence ao plano do cliente.', 422, 'SCENARIO_PLAN_OWNERSHIP_INVALID');
    if (planning.scenarios[index]?.version !== validatedScenario.version) throw new Error('Conflito de versão');
    const wasPrimary = planning.scenarios[index]?.isPrimary === true;
    const saved = {
      ...validatedScenario,
      assumptions,
      isPrimary: validatedScenario.isPrimary || wasPrimary,
      version: validatedScenario.version + 1,
      assumptionsVersion: validatedScenario.assumptionsVersion + 1,
    };
    const scenarios = planning.scenarios.map((item, itemIndex) => {
      if (itemIndex === index) return saved;
      return saved.isPrimary && item.isPrimary ? { ...item, isPrimary: false, version: item.version + 1 } : item;
    });
    const log = writableAuditLog(current.audit_log);
    const clientVersion = Number(current.version);
    await this.patchClientDocument(clientId, clientVersion + 1, {
      version: clientVersion + 1,
      data_model_version: DATA_MODEL_VERSION,
      planning_data: { ...planning, version: planning.version + 1, scenarios, goals: planning.goals, financials: planning.financials, protection: planning.protection, r5: planning.r5 },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('scenario.updated', scenario.id, ['planning_data'])] },
    });
    return saved;
  }

  async listExclusionSelections(clientId: string) {
    const planning = planningData((await this.rawClient(clientId)).planning_data, clientId);
    await this.assertFamilyExclusionTargets(planning, clientId);
    this.assertGuaranteeExclusionTargets(planning, clientId);
    return planning.exclusions;
  }

  async saveExclusionSelection(clientId: string, input: SavePlanningExclusionInput, expectedVersion: number) {
    const validated = assertInput(savePlanningExclusionSchema, input, 'Desconsideração inválida.');
    if (!Number.isInteger(expectedVersion) || expectedVersion < 0) throw new RepositoryError('Versão esperada da desconsideração inválida.', 422, 'VERSION_INVALID');
    const current = await this.rawClient(clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const planning = planningData(current.planning_data, clientId);
    this.assertGuaranteeExclusionTargets(planning, clientId);
    const scenarioId = validated.scenarioId ?? null;
    if (scenarioId !== null && !planning.scenarios.some((scenario) => scenario.id === scenarioId)) throw new RepositoryError('O cenário da desconsideração não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
    if (validated.targetType === 'family_member' && validated.targetIds.length > 0) {
      const familyIds = new Set((await this.listFamilyMembers(clientId)).map((member) => member.id));
      if (validated.targetIds.some((id) => !familyIds.has(id))) throw new RepositoryError('A desconsideração referencia familiar de outro cliente ou inexistente.', 422, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
    }
    if (validated.targetType === 'expense' && validated.targetIds.some((id) => !planning.financials.some((entry) => entry.id === id && entry.kind === 'expense'))) {
      throw new RepositoryError('A desconsideração referencia despesa de outro cliente ou inexistente.', 422, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
    }
    if (validated.targetType === 'guarantee' && validated.targetIds.some((id) => !planning.guaranteeExposures.some((exposure) => exposure.id === id && exposure.clientId === clientId))) {
      throw new RepositoryError('A desconsideração referencia exposição de garantia de outro cliente ou inexistente.', 422, 'EXCLUSION_TARGET_OWNERSHIP_INVALID');
    }
    const existingIndex = planning.exclusions.findIndex((item) => item.scope === validated.scope && item.scenarioId === scenarioId && item.targetType === validated.targetType);
    const existing = existingIndex >= 0 ? planning.exclusions[existingIndex] : undefined;
    const currentVersion = existing?.version ?? 0;
    if (currentVersion !== expectedVersion) throw new RepositoryError('A desconsideração foi alterada em outra sessão.', 409, 'VERSION_CONFLICT');
    const id = existing?.id ?? await deterministicUuid({ operation: 'exclusion.selection', clientId, scope: validated.scope, scenarioId, targetType: validated.targetType });
    const saved = assertInput(planningExclusionSchema, {
      id, clientId, scenarioId, scope: validated.scope, targetType: validated.targetType, state: validated.state,
      targetIds: validated.targetIds, pendingLegacyIds: validated.pendingLegacyIds, version: currentVersion + 1,
      updatedAt: new Date().toISOString(),
    }, 'Desconsideração inválida.');
    const exclusions = existingIndex >= 0 ? planning.exclusions.map((item, index) => index === existingIndex ? saved : item) : [saved, ...planning.exclusions];
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      planning_data: { ...planning, version: planning.version + 1, exclusions },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('planning.exclusion.updated', saved.id, ['planning_data'])] },
    });
    return saved;
  }

  async listGoals(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).goals.filter((goal) => goal.status !== 'archived');
  }

  async listArchivedGoals(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).goals.filter((goal) => goal.status === 'archived');
  }

  async createGoal(clientId: string, input: CreateGoalInput) {
    const validated = assertInput(createGoalSchema, input, 'Dados da meta inválidos. Revise valores e campos obrigatórios.');
    const current = await this.rawClient(clientId);
    const planning = planningData(current.planning_data, clientId);
    if (validated.scenarioId && !planning.scenarios.some((scenario) => scenario.id === validated.scenarioId)) throw new RepositoryError('O cenário da meta não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
    const goalId = await deterministicUuid({ operation: 'goal.create', clientId, input: validated });
    const existingGoal = planning.goals.find((item) => item.id === goalId);
    if (existingGoal) return existingGoal;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const now = new Date().toISOString();
    const goal: ClientGoal = {
      id: goalId, clientId, scenarioId: validated.scenarioId ?? null,
      title: validated.title.trim(), category: validated.category.trim(), priority: validated.priority,
      targetDate: validated.targetDate, targetValue: moneyFromNumber(validated.targetValue), reservedValue: moneyFromNumber(validated.reservedValue),
      periodicity: validated.periodicity, status: 'active', version: 1, createdAt: now, updatedAt: now,
    };
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      planning_data: { ...planning, version: planning.version + 1, scenarios: planning.scenarios, goals: [...planning.goals, goal], financials: planning.financials, protection: planning.protection, r5: planning.r5 },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('goal.created', goal.id, ['planning_data'])] },
    });
    return goal;
  }

  async updateGoal(clientId: string, goalId: string, patch: UpdateGoalInput, expectedVersion: number) {
    const validated = assertInput(createGoalSchema.partial().extend({ status: goalStatusSchema.optional() }), patch, 'Atualização da meta inválida. Revise valores e campos obrigatórios.');
    const current = await this.rawClient(clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const planning = planningData(current.planning_data, clientId);
    const index = planning.goals.findIndex((goal) => goal.id === goalId);
    if (index < 0) throw new Error('Meta não encontrada');
    const currentGoal = planning.goals[index]!;
    if (currentGoal.version !== expectedVersion) throw new Error('Conflito de versão');
    if (validated.scenarioId && !planning.scenarios.some((scenario) => scenario.id === validated.scenarioId)) throw new RepositoryError('O cenário da meta não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
    const saved: ClientGoal = {
      ...currentGoal,
      ...(validated.scenarioId !== undefined ? { scenarioId: validated.scenarioId } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.category !== undefined ? { category: validated.category.trim() } : {}),
      ...(validated.priority !== undefined ? { priority: validated.priority } : {}),
      ...(validated.targetDate !== undefined ? { targetDate: validated.targetDate } : {}),
      ...(validated.targetValue !== undefined ? { targetValue: moneyFromNumber(validated.targetValue) } : {}),
      ...(validated.reservedValue !== undefined ? { reservedValue: moneyFromNumber(validated.reservedValue) } : {}),
      ...(validated.periodicity !== undefined ? { periodicity: validated.periodicity } : {}),
      ...(validated.status !== undefined ? { status: validated.status } : {}),
      version: expectedVersion + 1,
      updatedAt: new Date().toISOString(),
    };
    const goals = planning.goals.map((goal, goalIndex) => goalIndex === index ? saved : goal);
    const log = writableAuditLog(current.audit_log);
    const action = saved.status === 'archived' ? 'goal.archived' : 'goal.updated';
    await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      planning_data: { ...planning, version: planning.version + 1, scenarios: planning.scenarios, goals, financials: planning.financials, protection: planning.protection, r5: planning.r5 },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry(action, goalId, ['planning_data'])] },
    });
    return saved;
  }

  async listFinancialEntries(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).financials.filter((entry) => entry.active);
  }

  async listGuaranteeExposures(clientId: string, scenarioId?: string | null) {
    const exposures = planningData((await this.rawClient(clientId)).planning_data, clientId).guaranteeExposures;
    if (scenarioId === undefined) return exposures;
    return exposures.filter((exposure) => exposure.scenarioId === null || exposure.scenarioId === scenarioId);
  }

  async listArchivedFinancialEntries(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).financials.filter((entry) => !entry.active);
  }

  async createFinancialEntry(input: CreateFinancialEntryInput) {
    const validated = assertInput(createFinancialEntrySchema, input, 'Dados patrimoniais inválidos. Use valores com no máximo duas casas decimais.');
    const current = await this.rawClient(validated.clientId);
    const planning = planningData(current.planning_data, validated.clientId);
    const financialId = await deterministicUuid({ operation: 'financial.create', clientId: validated.clientId, input: validated });
    const existingFinancial = planning.financials.find((item) => item.id === financialId);
    if (existingFinancial) return existingFinancial;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const now = new Date().toISOString();
    const entry: ClientFinancialEntry = {
      id: financialId, clientId: validated.clientId, kind: validated.kind, category: validated.category.trim(),
      title: validated.title.trim(), institution: validated.institution?.trim() || null,
      currentValue: moneyFromNumber(validated.currentValue), monthlyValue: moneyFromNumber(validated.monthlyValue),
      retirementMonthlyValue: moneyFromNumber(validated.retirementMonthlyValue), liquidity: validated.liquidity,
      periodicity: validated.periodicity, active: true, version: 1, createdAt: now, updatedAt: now,
    };
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(input.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, version: planning.version + 1, scenarios: planning.scenarios, goals: planning.goals, financials: [entry, ...planning.financials], protection: planning.protection, r5: planning.r5 },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('financial.created', entry.id, ['planning_data'])] },
    });
    return entry;
  }

  async updateFinancialEntry(clientId: string, entryId: string, patch: UpdateFinancialEntryInput, expectedVersion: number) {
    const { active, ...financialPatch } = patch;
    const validationPayload: Record<string, unknown> = { ...financialPatch, clientId };
    const validated = assertInput(createFinancialEntrySchema.partial(), validationPayload, 'Atualização patrimonial inválida. Use valores com no máximo duas casas decimais.');
    if (active !== undefined && typeof active !== 'boolean') throw new RepositoryError('Atualização patrimonial inválida: active deve ser booleano.', 422, 'FINANCIAL_ENTRY_INVALID');
    const current = await this.rawClient(clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const planning = planningData(current.planning_data, clientId);
    const index = planning.financials.findIndex((entry) => entry.id === entryId);
    if (index < 0) throw new Error('Registro financeiro não encontrado');
    const currentEntry = planning.financials[index]!;
    if (currentEntry.version !== expectedVersion) throw new Error('Conflito de versão');
    const saved: ClientFinancialEntry = {
      ...currentEntry,
      ...(validated.kind !== undefined ? { kind: validated.kind } : {}),
      ...(validated.category !== undefined ? { category: validated.category.trim() } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.institution !== undefined ? { institution: validated.institution?.trim() || null } : {}),
      ...(validated.currentValue !== undefined ? { currentValue: moneyFromNumber(validated.currentValue) } : {}),
      ...(validated.monthlyValue !== undefined ? { monthlyValue: moneyFromNumber(validated.monthlyValue) } : {}),
      ...(validated.retirementMonthlyValue !== undefined ? { retirementMonthlyValue: moneyFromNumber(validated.retirementMonthlyValue) } : {}),
      ...(validated.liquidity !== undefined ? { liquidity: validated.liquidity } : {}),
      ...(validated.periodicity !== undefined ? { periodicity: validated.periodicity } : {}),
      ...(active !== undefined ? { active } : {}),
      version: expectedVersion + 1, updatedAt: new Date().toISOString(),
    };
    const financials = planning.financials.map((entry, entryIndex) => entryIndex === index ? saved : entry);
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, version: planning.version + 1, scenarios: planning.scenarios, goals: planning.goals, financials, protection: planning.protection, r5: planning.r5 },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('financial.updated', entryId, ['planning_data'])] },
    });
    return saved;
  }

  async getProtectionNeed(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).protection;
  }

  async saveProtectionNeed(input: CreateProtectionNeedInput, expectedVersion: number) {
    const validated = assertInput(createProtectionNeedSchema, input, 'Premissas de proteção inválidas. Revise valores monetários e percentuais.');
    if (!Number.isInteger(expectedVersion) || expectedVersion < 0) throw new RepositoryError('Versão esperada de proteção inválida.', 422, 'VERSION_INVALID');
    const current = await this.rawClient(validated.clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const planning = planningData(current.planning_data, validated.clientId);
    const currentProtectionVersion = planning.protection?.version ?? 0;
    if (currentProtectionVersion !== expectedVersion) throw new RepositoryError('O diagnóstico de proteção foi alterado em outra sessão.', 409, 'VERSION_CONFLICT');
    if (validated.guaranteesScenarioId !== null && !planning.scenarios.some((scenario) => scenario.id === validated.guaranteesScenarioId)) throw new RepositoryError('O cenário das garantias não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
    const allFamilyMembers = await this.listFamilyMembers(validated.clientId);
    await this.assertFamilyExclusionTargets(planning, validated.clientId, allFamilyMembers);
    const guaranteeExposures = await this.listGuaranteeExposures(validated.clientId);
    const availableGuarantees = effectiveGuaranteeExposures(guaranteeExposures, validated.guaranteesScenarioId);
    const effectiveGuarantees = effectiveGuaranteeExposures(guaranteeExposures, validated.guaranteesScenarioId, planning.exclusions);
    if (validated.guaranteesMode === 'canonical') {
      const kinds = new Set(availableGuarantees.map((exposure) => exposure.kind));
      if (availableGuarantees.length !== GUARANTEE_EXPOSURE_KINDS.length || kinds.size !== GUARANTEE_EXPOSURE_KINDS.length || availableGuarantees.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) throw new RepositoryError('As quatro exposições canônicas de garantia não estão verificadas para o cenário.', 409, 'GUARANTEE_BINDING_PENDING');
      await assertGuaranteeBindingReceipts(availableGuarantees);
      if (sumMoneyStrings(effectiveGuarantees.map((exposure) => exposure.value)) !== moneyString(validated.currentGuarantees)) throw new RepositoryError('O valor de garantias não corresponde às exposições canônicas verificadas.', 409, 'GUARANTEE_VALUE_STALE');
    }
    const family = allFamilyMembers.filter((member) => !resolveExcludedTargetIds(planning.exclusions, null, 'family_member').includes(member.id));
    if (validated.dependents !== protectionDependentsFromFamily(family)) throw new RepositoryError('A quantidade de dependentes não corresponde à Família atual.', 409, 'PROTECTION_FAMILY_STALE');
    const familyRevision = family.map((member) => ({ id: member.id, version: member.version })).sort((left, right) => left.id.localeCompare(right.id));
    const savedBase = calculateProtection(validated, planning.protection);
    const saved = { ...savedBase, sourceRevisionFingerprint: await sha256(protectionRevisionPayload({ clientId: validated.clientId, family: familyRevision, guaranteesMode: validated.guaranteesMode, guaranteesScenarioId: validated.guaranteesScenarioId, guaranteeExposures: effectiveGuarantees, exclusions: planning.exclusions })) };
    const log = writableAuditLog(current.audit_log);
    const persisted = await this.patchClientDocument(input.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, version: planning.version + 1, scenarios: planning.scenarios, goals: planning.goals, financials: planning.financials, protection: saved, r5: planning.r5 },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('protection.updated', saved.id, ['planning_data'])] },
    });
    if (this.edgeBaseUrl) {
      if (!persisted) throw new RepositoryError('A mutação de proteção não retornou read-back.', 502, 'PROTECTION_RESPONSE_INVALID');
      const persistedPlanning = planningData(persisted.planning_data, validated.clientId);
      const persistedProtection = persistedPlanning.protection;
      if (!persistedProtection || canonicalJson(persistedProtection) !== canonicalJson(saved)) throw new RepositoryError('O read-back de proteção diverge das premissas persistidas.', 502, 'PROTECTION_RESPONSE_INVALID');
      return persistedProtection;
    }
    return saved;
  }

  async listPrivatePensionSimulations(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).r5.pensionSimulations;
  }

  async createPrivatePensionSimulation(input: CreatePrivatePensionSimulationInput) {
    const validated = assertInput(createPrivatePensionSimulationSchema, input, 'Dados de previdência inválidos.');
    const current = await this.rawClient(validated.clientId);
    const planning = planningData(current.planning_data, validated.clientId);
    const projection = privatePensionProjection(validated);
    const pensionId = await deterministicUuid({ operation: 'pension.create', clientId: validated.clientId, input: validated });
    const existingPension = planning.r5.pensionSimulations.find((item) => item.id === pensionId);
    if (existingPension) return existingPension;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const now = new Date().toISOString();
    const simulation = assertInput(privatePensionSimulationSchema, {
      id: pensionId, clientId: validated.clientId, name: validated.name.trim(), product: validated.product,
      taxRegime: validated.taxRegime, socialSecurity: validated.socialSecurity,
      annualTaxableIncome: moneyFromNumber(validated.annualTaxableIncome), annualDeductibleExpenses: moneyFromNumber(validated.annualDeductibleExpenses),
      currentReserve: moneyFromNumber(validated.currentReserve), monthlyContribution: moneyFromNumber(validated.monthlyContribution),
      annualReturnPct: validated.annualReturnPct, inflationAnnualPct: validated.inflationAnnualPct,
      accumulationYears: validated.accumulationYears, retirementIncomeYears: validated.retirementIncomeYears,
      result: {
        suggestedPgblAnnual: calculatedMoney(projection.suggestedPgblAnnual), taxBenefit: calculatedMoney(projection.taxBenefit),
        grossReserve: calculatedMoney(projection.grossReserve), taxableRescue: calculatedMoney(projection.taxableRescue),
        estimatedTax: calculatedMoney(projection.estimatedTax), netReserve: calculatedMoney(projection.netReserve),
        sustainableMonthlyIncome: calculatedMoney(projection.sustainableMonthlyIncome), taxRatePct: projection.taxRatePct,
      },
      version: 1, createdAt: now, updatedAt: now,
    }, 'Simulação de previdência criada inválida.');
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(input.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, r5: { ...planning.r5, version: planning.r5.version + 1, pensionSimulations: [simulation, ...planning.r5.pensionSimulations] } },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('pension.created', simulation.id, ['planning_data'])] },
    });
    return simulation;
  }

  async listFinancingSimulations(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).r5.financingSimulations;
  }

  async createFinancingSimulation(input: CreateFinancingSimulationInput) {
    const normalizedInput = assertInput(createFinancingSimulationSchema, input, 'Dados de financiamento inválidos.');
    const current = await this.rawClient(normalizedInput.clientId);
    const planning = planningData(current.planning_data, normalizedInput.clientId);
    const financingId = await deterministicUuid({ operation: 'financing.create', clientId: normalizedInput.clientId, input: normalizedInput });
    const existingFinancing = planning.r5.financingSimulations.find((item) => item.id === financingId);
    if (existingFinancing) return existingFinancing;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const rows: FinancingScheduleRow[] = normalizedInput.kind === 'sac'
      ? sacSchedule(normalizedInput.principal, normalizedInput.termMonths, normalizedInput.annualInterestPct, normalizedInput.annualIndexationPct).map((row) => ({
        installment: row.number, payment: calculatedMoney(row.payment), principal: calculatedMoney(row.amortization),
        interest: calculatedMoney(row.interest), balance: calculatedMoney(row.closingBalance),
      }))
      : consortiumScheduleRowsFromInput({
        credit: normalizedInput.principal, termMonths: normalizedInput.termMonths, anticipationFeePct: normalizedInput.anticipationFeePct ?? 0,
        administrationFeePct: normalizedInput.administrationFeePct, reserveFundPct: normalizedInput.reserveFundPct,
        monthlyInsurancePct: normalizedInput.monthlyInsurancePct, installmentReducerPct: normalizedInput.installmentReducerPct,
        contemplationInstallment: normalizedInput.contemplationInstallment, totalBidPct: normalizedInput.totalBidPct,
        embeddedBidPct: normalizedInput.embeddedBidPct, annualAdjustmentPct: normalizedInput.annualAdjustmentPct,
      }).map((row, index) => ({ installment: index + 1, payment: calculatedMoney(row.payment), principal: calculatedMoney(row.principal), interest: calculatedMoney(row.interest), balance: calculatedMoney(row.balance) })) ;
    const now = new Date().toISOString();
    const totalPaid = sumMoneyStrings(rows.map((row) => row.payment));
    const simulation = assertInput(financingSimulationSchema, {
      id: financingId, clientId: normalizedInput.clientId, name: normalizedInput.name.trim(), kind: normalizedInput.kind,
      principal: moneyString(normalizedInput.principal), termMonths: normalizedInput.termMonths, annualInterestPct: normalizedInput.annualInterestPct,
      annualIndexationPct: normalizedInput.annualIndexationPct, administrationFeePct: normalizedInput.administrationFeePct, reserveFundPct: normalizedInput.reserveFundPct,
      anticipationFeePct: normalizedInput.anticipationFeePct, monthlyInsurancePct: normalizedInput.monthlyInsurancePct, installmentReducerPct: normalizedInput.installmentReducerPct,
      contemplationInstallment: normalizedInput.contemplationInstallment, totalBidPct: normalizedInput.totalBidPct, embeddedBidPct: normalizedInput.embeddedBidPct,
      annualAdjustmentPct: normalizedInput.annualAdjustmentPct, firstPayment: rows[0]?.payment ?? '0.00', totalPaid,
      lastPayment: rows.at(-1)?.payment ?? '0.00', schedule: rows, version: 1, createdAt: now, updatedAt: now,
    }, 'Simulação de financiamento criada inválida.');
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(normalizedInput.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, r5: { ...planning.r5, version: planning.r5.version + 1, financingSimulations: [simulation, ...planning.r5.financingSimulations] } },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('financing.created', simulation.id, ['planning_data'])] },
    });
    return simulation;
  }

  async listFinancingComparisons(clientId: string) {
    return planningData((await this.rawClient(clientId)).planning_data, clientId).r5.financingComparisons;
  }

  async createFinancingComparison(input: CreateFinancingComparisonInput) {
    const normalizedInput = assertInput(createFinancingComparisonSchema, input, 'Dados de comparação financeira inválidos.');
    const current = await this.rawClient(normalizedInput.clientId);
    const planning = planningData(current.planning_data, normalizedInput.clientId);
    const suggested = planning.r5.financingSimulations.find((item) => item.id === normalizedInput.suggestedSimulationId);
    const alternative = planning.r5.financingSimulations.find((item) => item.id === normalizedInput.alternativeSimulationId);
    if (!suggested || !alternative) throw new RepositoryError('Simulações financeiras não encontradas.', 404, 'FINANCING_SIMULATION_NOT_FOUND');
    const comparisonId = await deterministicUuid({ operation: 'comparison.create', clientId: normalizedInput.clientId, input: normalizedInput });
    const existingComparison = planning.r5.financingComparisons.find((item) => item.id === comparisonId);
    if (existingComparison) return existingComparison;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const comparisonRows = compareFinancingSchedules(suggested.schedule.map((row) => Number(row.payment)), alternative.schedule.map((row) => Number(row.payment)), normalizedInput.annualOpportunityRatePct);
    const annualRows = annualizeFinancingComparison(comparisonRows);
    const cashflows = [0, ...comparisonRows.map((row) => row.difference.toNumber())];
    const now = new Date().toISOString();
    const comparison: FinancingComparison = {
      id: comparisonId, clientId: normalizedInput.clientId, name: normalizedInput.name.trim(), suggestedSimulationId: suggested.id,
      alternativeSimulationId: alternative.id, annualOpportunityRatePct: normalizedInput.annualOpportunityRatePct,
      suggestedPresentValue: calculatedMoney(presentValueSchedule(suggested.schedule.map((row) => Number(row.payment)), normalizedInput.annualOpportunityRatePct)),
      alternativePresentValue: calculatedMoney(presentValueSchedule(alternative.schedule.map((row) => Number(row.payment)), normalizedInput.annualOpportunityRatePct)),
      tirAnnualPct: periodicInternalRateOfReturnPct(cashflows),
      rows: comparisonRows.map((row) => ({ installment: row.installment, suggestedPayment: calculatedMoney(row.suggestedPayment), alternativePayment: calculatedMoney(row.alternativePayment), difference: calculatedMoney(row.difference), accumulatedDifference: calculatedMoney(row.accumulatedDifference) })),
      annualRows: annualRows.map((row) => ({ year: row.year, suggestedPayment: calculatedMoney(row.suggestedPayment), alternativePayment: calculatedMoney(row.alternativePayment), difference: calculatedMoney(row.difference), accumulatedDifference: calculatedMoney(row.accumulatedDifference) })),
      version: 1, createdAt: now, updatedAt: now,
    };
    financingComparisonSchema.parse(comparison);
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(normalizedInput.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        planning_data: { ...planning, r5: { ...planning.r5, version: planning.r5.version + 1, financingComparisons: [comparison, ...planning.r5.financingComparisons] } },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('comparison.created', comparison.id, ['planning_data'])] },
    });
    return comparison;
  }

  async listFollowUp(clientId?: string) {
    if (this.edgeBaseUrl) {
      const path = clientId ? `/read-model/follow-up?clientId=${encodeURIComponent(clientId)}` : '/read-model/follow-up';
      const response = await this.request<Envelope<unknown>>(this.edgeBaseUrl, path);
      if (!Array.isArray(response.data)) throw new Error('Resposta de acompanhamento inválida.');
      const items = response.data.map((item) => assertInput(clientFollowUpSchema, item, 'Acompanhamento retornado pelo Worker inválido.'));
      return items;
    }
    if (clientId) return followUpData((await this.rawClient(clientId)).follow_up_data, clientId).items;
    const data = await this.listClientPages<Pick<RawClient, 'id' | 'follow_up_data'>>('id,follow_up_data', '&filter[status][_neq]=archived');
    return data.flatMap((row) => followUpData(row.follow_up_data, row.id).items)
      .sort((left, right) => (left.dueAt ?? '9999').localeCompare(right.dueAt ?? '9999'));
  }

  async getClientPipeline(clientId: string) {
    return followUpData((await this.rawClient(clientId)).follow_up_data, clientId).pipeline;
  }

  async updateClientPipeline(clientId: string, stage: ClientPipelineStage, expectedVersion: number) {
    const validatedStage = assertInput(clientPipelineStageSchema, stage, 'Etapa de funil inválida.');
    const current = await this.rawClient(clientId);
    const document = followUpData(current.follow_up_data, clientId);
    if (document.pipeline.version !== expectedVersion) throw new RepositoryError('O funil foi alterado por outra pessoa', 409, 'VERSION_CONFLICT');
    if (document.pipeline.stage === validatedStage) return document.pipeline;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const now = new Date().toISOString();
    const pipeline = assertInput(clientPipelineSchema, {
      clientId, stage: validatedStage, version: document.pipeline.version + 1,
      history: [...document.pipeline.history, { stage: validatedStage, at: now, actor: this.sessionUserId ?? null }],
    }, 'Funil persistido inválido.');
    const log = writableAuditLog(current.audit_log);
    const persisted = await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      follow_up_data: { version: document.version + 1, items: document.items, pipeline },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('pipeline.updated', clientId, ['follow_up_data'])] },
    });
    if (persisted) return followUpData(persisted.follow_up_data, clientId).pipeline;
    return pipeline;
  }

  async createFollowUp(input: CreateFollowUpInput) {
    const validated = assertInput(createFollowUpSchema, input, 'Dados de acompanhamento inválidos. Revise título, tipo e prazo.');
    const current = await this.rawClient(validated.clientId);
    const document = followUpData(current.follow_up_data, validated.clientId);
    const followUpId = await deterministicUuid({ operation: 'followup.create', clientId: validated.clientId, input: validated });
    const existingFollowUp = document.items.find((item) => item.id === followUpId);
    if (existingFollowUp) return existingFollowUp;
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const now = new Date().toISOString();
    const item = assertInput(clientFollowUpSchema, {
      id: followUpId, clientId: validated.clientId, type: validated.type, title: validated.title.trim(),
      description: validated.description.trim(), status: 'open', dueAt: validated.dueAt ?? null,
      assignedTo: validated.assignedTo ?? null, version: 1, createdAt: now, updatedAt: now,
    }, 'Acompanhamento criado inválido.');
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(validated.clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      follow_up_data: { version: document.version + 1, items: [item, ...document.items], pipeline: document.pipeline },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('followup.created', item.id, ['follow_up_data'])] },
    });
    return item;
  }

  async updateFollowUp(clientId: string, itemId: string, patch: UpdateFollowUpInput, expectedVersion: number) {
    const validated = assertInput(updateFollowUpSchema, patch, 'Atualização de acompanhamento inválida.');
    const current = await this.rawClient(clientId);
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    const document = followUpData(current.follow_up_data, clientId);
    const index = document.items.findIndex((item) => item.id === itemId);
    if (index < 0) throw new Error('Ação de acompanhamento não encontrada');
    const currentItem = document.items[index]!;
    if (currentItem.version !== expectedVersion) throw new Error('Conflito de versão');
    const saved: ClientFollowUp = {
      ...currentItem,
      ...(validated.type !== undefined ? { type: validated.type } : {}),
      ...(validated.title !== undefined ? { title: validated.title.trim() } : {}),
      ...(validated.description !== undefined ? { description: validated.description.trim() } : {}),
      ...(validated.dueAt !== undefined ? { dueAt: validated.dueAt } : {}),
      ...(validated.assignedTo !== undefined ? { assignedTo: validated.assignedTo } : {}),
      ...(validated.status !== undefined ? { status: validated.status } : {}),
      version: expectedVersion + 1, updatedAt: new Date().toISOString(),
    };
    const log = writableAuditLog(current.audit_log);
    const action = saved.status === 'done' ? 'followup.completed' : 'followup.updated';
    await this.patchClientDocument(clientId, Number(current.version) + 1, {
      version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
      follow_up_data: { version: document.version + 1, items: document.items.map((item, itemIndex) => itemIndex === index ? saved : item), pipeline: document.pipeline },
      audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry(action, itemId, ['follow_up_data'])] },
    });
    return assertInput(clientFollowUpSchema, saved, 'Acompanhamento atualizado inválido.');
  }

  async listContributions(clientId: string): Promise<ClientContribution[]> {
    if (!this.contributionGatewayBaseUrl) {
      const document = followUpData((await this.rawClient(clientId)).follow_up_data, clientId);
      await verifyContributionOperationHashes(document);
      return [...document.contributions].sort((left, right) => right.dueDate.localeCompare(left.dueDate) || left.id.localeCompare(right.id));
    }
    const response = await this.request<Envelope<unknown[]>>(this.contributionGatewayBaseUrl, `/contributions/${encodeURIComponent(clientId)}`);
    if (!Array.isArray(response.data)) throw new Error('Resposta de aportes inválida');
    return response.data.map((item) => {
      const parsed = assertResponse(clientContributionSchema, item, 'Aporte retornado pelo Worker inválido.');
      if (parsed.clientId !== clientId) throw new Error('Aporte de outro cliente na resposta');
      return parsed;
    });
  }

  async createContribution(input: CreateContributionInput, idempotencyKey: string): Promise<ClientContribution> {
    const key = typeof idempotencyKey === 'string' ? idempotencyKey.trim() : '';
    if (key.length < 8 || key.length > 200 || !/^[\x21-\x7e]+$/.test(key)) throw new RepositoryError('Informe uma chave de operação única de 8 a 200 caracteres imprimíveis', 409, 'IDEMPOTENCY_KEY_REQUIRED');
    const parsed = createContributionSchema.parse(input);
    const normalizedInput = { ...parsed, resource: parsed.resource.trim() };
    if (!this.contributionGatewayBaseUrl) {
      const actorId = contributionActorId(this.sessionUserId);
      const current = await this.rawClient(normalizedInput.clientId);
      const document = followUpData(current.follow_up_data, normalizedInput.clientId);
      await verifyContributionOperationHashes(document);
      const requestHash = await sha256({ kind: 'create', clientId: normalizedInput.clientId, input: normalizedInput });
      const prior = document.contributionOperations.find((operation) => operation.actorId === actorId && operation.key.trim().toLowerCase() === key.toLowerCase());
      if (prior) {
        if (prior.kind !== 'create' || prior.clientId !== normalizedInput.clientId || prior.requestHash !== requestHash) throw new RepositoryError('Idempotency-Key já usada com payload diferente', 409, 'IDEMPOTENCY_KEY_REUSED');
        const existing = document.contributions.find((item) => item.id === prior.contributionId);
        if (!existing) throw new RepositoryError('A operação de aporte não possui recibo persistido verificável.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
        const receipt = prior.receipt ?? (existing.status === 'pending' && existing.version === 1 ? existing : undefined);
        if (!receipt) throw new RepositoryError('O recibo inicial do planejamento não está disponível para replay seguro.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
        assertContributionPlanReceipt(receipt, normalizedInput.clientId, prior.contributionId);
        return assertContributionCreateResponse(receipt, normalizedInput);
      }
      this.assertDirectusMutationClientAllowed(current);
      const planning = planningData(current.planning_data, normalizedInput.clientId);
      if (normalizedInput.goalId && !planning.goals.some((goal) => goal.id === normalizedInput.goalId && goal.status === 'active')) throw new RepositoryError('Meta ativa deste cliente não encontrada', 422, 'GOAL_OWNERSHIP_INVALID');
      const now = new Date().toISOString();
      const contribution = assertInput(clientContributionSchema, {
        id: await deterministicUuid({ operation: 'contribution.create', clientId: normalizedInput.clientId, actorId, key }),
        clientId: normalizedInput.clientId, goalId: normalizedInput.goalId ?? null, resource: normalizedInput.resource,
        dueDate: normalizedInput.dueDate, plannedAmount: moneyFromNumber(normalizedInput.plannedAmount), actualAmount: null,
        status: 'pending', realizedAt: null, note: normalizedInput.note, settlementNote: null, version: 1, createdAt: now, updatedAt: now,
      }, 'Aporte criado no Directus inválido.');
      const operation: ContributionOperation = { actorId, key, kind: 'create', clientId: normalizedInput.clientId, contributionId: contribution.id, requestHash, receipt: contribution };
      const log = writableAuditLog(current.audit_log);
      const persisted = await this.patchClientDocument(normalizedInput.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        follow_up_data: { ...document, version: document.version + 1, contributions: [contribution, ...document.contributions], contributionOperations: [...document.contributionOperations, operation] },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('contribution.planned', contribution.id, ['follow_up_data'])] },
      });
      const committed = persisted ? followUpData(persisted.follow_up_data, normalizedInput.clientId).contributions.find((item) => item.id === contribution.id) : contribution;
      if (!committed) throw new RepositoryError('O Directus não confirmou o aporte persistido.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
      return assertContributionCreateResponse(committed, normalizedInput);
    }
    let responseRequestId: string | undefined;
    const result = await this.request<Envelope<unknown>>(this.contributionGatewayBaseUrl, `/contributions/${encodeURIComponent(parsed.clientId)}`, {
      method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify({ ...parsed, resource: parsed.resource.trim() }),
    }, (requestId) => { responseRequestId = requestId; });
    let item: ClientContribution;
    try { item = assertResponse(clientContributionSchema, result.data, 'Aporte criado retornado pelo Worker inválido.'); }
    catch (error) {
      if (error instanceof RepositoryError) throw new RepositoryError('O Worker respondeu um recibo estruturalmente inválido; mantenha a operação bloqueada e reconcilie pela mesma chave', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN', error.requestId ?? responseRequestId);
      throw error;
    }
      try { return assertContributionCreateResponse(item, normalizedInput); }
    catch (error) {
      if (error instanceof RepositoryError && error.code === 'CONTRIBUTION_RESPONSE_MISMATCH') {
        throw new RepositoryError('O Worker respondeu um recibo divergente; mantenha a operação bloqueada e reconcilie pela mesma chave', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN', error.requestId ?? responseRequestId);
      }
      throw error;
    }
  }

  async reconcileContributionByKey(clientId: string, idempotencyKey: string, kind: 'plan' | 'settle' = 'plan'): Promise<ClientContribution> {
    if (kind !== 'plan' && kind !== 'settle') throw new RepositoryError('A operação de reconciliação é inválida', 409, 'CONTRIBUTION_RECONCILE_INVALID');
    const key = typeof idempotencyKey === 'string' ? idempotencyKey.trim() : '';
    if (key.length < 8 || key.length > 200 || !/^[\x21-\x7e]+$/.test(key)) throw new RepositoryError('Informe a mesma chave da operação ambígua', 409, 'IDEMPOTENCY_KEY_REQUIRED');
    if (!this.contributionGatewayBaseUrl) {
      const actorId = contributionActorId(this.sessionUserId);
      const document = followUpData((await this.rawClient(clientId)).follow_up_data, clientId);
      await verifyContributionOperationHashes(document);
      const expectedKind = kind === 'plan' ? 'create' : 'settle';
      const operation = document.contributionOperations.find((item) => item.actorId === actorId && item.key.trim().toLowerCase() === key.toLowerCase() && item.kind === expectedKind);
      if (!operation) throw new RepositoryError('Não há recibo Directus confirmado para esta chave.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
      const item = document.contributions.find((contribution) => contribution.id === operation.contributionId);
      if (!item) throw new RepositoryError('O recibo Directus não aponta para um aporte persistido.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
      if (kind === 'plan') {
        return assertContributionPlanReceipt(operation.receipt, clientId, item.id);
      }
      assertContributionSettleReceipt(operation.receipt, item, clientId, item.id);
      return operation.receipt;
    }
    let responseRequestId: string | undefined;
    const result = await this.request<Envelope<unknown>>(this.contributionGatewayBaseUrl, `/contributions/${encodeURIComponent(clientId)}/reconcile`, {
      method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify({ kind: 'key', operation: kind }),
    }, (requestId) => { responseRequestId = requestId; });
    let item: ClientContribution;
    try { item = assertResponse(clientContributionSchema, result.data, 'A reconciliação do aporte retornou um registro inválido.'); }
    catch (error) {
      if (error instanceof RepositoryError) throw new RepositoryError('A reconciliação retornou um recibo estruturalmente inválido; mantenha a operação bloqueada.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN', error.requestId ?? responseRequestId);
      throw error;
    }
    if (item.clientId !== clientId) throw new RepositoryError('A reconciliação retornou outro cliente', 502, 'CONTRIBUTION_RESPONSE_INVALID', responseRequestId);
    if ((kind === 'plan' && item.status !== 'pending') || (kind === 'settle' && item.status === 'pending')) throw new RepositoryError('A reconciliação retornou um estado incompatível com a operação', 502, 'CONTRIBUTION_RECONCILE_RESPONSE_MISMATCH', responseRequestId);
    return item;
  }

  async settleContribution(clientId: string, contributionId: string, input: SettleContributionInput, expectedVersion: number, idempotencyKey: string): Promise<ClientContribution> {
    const key = typeof idempotencyKey === 'string' ? idempotencyKey.trim() : '';
    if (key.length < 8 || key.length > 200 || !/^[\x21-\x7e]+$/.test(key)) throw new RepositoryError('Informe uma chave de operação única de 8 a 200 caracteres imprimíveis', 409, 'IDEMPOTENCY_KEY_REQUIRED');
    const parsed = settleContributionSchema.parse(input);
    if (!this.contributionGatewayBaseUrl) {
      const actorId = contributionActorId(this.sessionUserId);
      const current = await this.rawClient(clientId);
      const document = followUpData(current.follow_up_data, clientId);
      await verifyContributionOperationHashes(document);
      const normalizedInput = { ...parsed, note: parsed.note.trim() };
      const requestHash = await sha256({ kind: 'settle', clientId, contributionId, expectedVersion, input: normalizedInput });
      const prior = document.contributionOperations.find((operation) => operation.actorId === actorId && operation.key.trim().toLowerCase() === key.toLowerCase());
      if (prior) {
        if (prior.kind !== 'settle' || prior.clientId !== clientId || prior.contributionId !== contributionId || prior.requestHash !== requestHash) throw new RepositoryError('Idempotency-Key já usada com payload diferente', 409, 'IDEMPOTENCY_KEY_REUSED');
        const existing = document.contributions.find((item) => item.id === contributionId);
        if (!existing) throw new RepositoryError('A operação de settlement não possui recibo persistido verificável.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
        return assertContributionSettleResponse(existing, existing, clientId, contributionId, normalizedInput, expectedVersion);
      }
      this.assertDirectusMutationClientAllowed(current);
      const original = document.contributions.find((item) => item.id === contributionId);
      if (!original) throw new RepositoryError('Aporte não encontrado para este cliente', 404, 'CONTRIBUTION_NOT_FOUND');
      assertContributionCanSettle(original, normalizedInput, expectedVersion);
      const now = new Date().toISOString();
      const settled = assertInput(clientContributionSchema, {
        ...original, status: normalizedInput.status, actualAmount: normalizedInput.status === 'realized' ? moneyFromNumber(normalizedInput.actualAmount!) : null,
        realizedAt: normalizedInput.status === 'realized' ? normalizedInput.realizedAt! : null, settlementNote: normalizedInput.note,
        version: expectedVersion + 1, updatedAt: now,
      }, 'Settlement de aporte no Directus inválido.');
      const operation: ContributionOperation = { actorId, key, kind: 'settle', clientId, contributionId, requestHash, receipt: settled };
      const log = writableAuditLog(current.audit_log);
      const persisted = await this.patchClientDocument(clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        follow_up_data: { ...document, version: document.version + 1, contributions: document.contributions.map((item) => item.id === contributionId ? settled : item), contributionOperations: [...document.contributionOperations, operation] },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry(settled.status === 'realized' ? 'contribution.realized' : 'contribution.not_realized', contributionId, ['follow_up_data'])] },
      });
      const committed = persisted ? followUpData(persisted.follow_up_data, clientId).contributions.find((item) => item.id === contributionId) : settled;
      if (!committed) throw new RepositoryError('O Directus não confirmou o settlement persistido.', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN');
      return assertContributionSettleResponse(committed, original, clientId, contributionId, normalizedInput, expectedVersion);
    }
    const original = (await this.listContributions(clientId)).find((item) => item.id === contributionId);
    if (!original) throw new RepositoryError('Aporte não encontrado para este cliente', 404, 'CONTRIBUTION_NOT_FOUND');
    let responseRequestId: string | undefined;
    const result = await this.request<Envelope<unknown>>(this.contributionGatewayBaseUrl, `/contributions/${encodeURIComponent(clientId)}/${encodeURIComponent(contributionId)}/settle`, {
      method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify({ expectedVersion, input: parsed }),
    }, (requestId) => { responseRequestId = requestId; });
    let item: ClientContribution;
    try { item = assertResponse(clientContributionSchema, result.data, 'Aporte liquidado retornado pelo Worker inválido.'); }
    catch (error) {
      if (error instanceof RepositoryError) throw new RepositoryError('O Worker respondeu um recibo estruturalmente inválido; mantenha a operação bloqueada e reconcilie pela mesma chave', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN', error.requestId ?? responseRequestId);
      throw error;
    }
    try { return assertContributionSettleResponse(item, original, clientId, contributionId, parsed, expectedVersion); }
    catch (error) {
      if (error instanceof RepositoryError && error.code === 'CONTRIBUTION_RESPONSE_MISMATCH') {
        throw new RepositoryError('O Worker respondeu um recibo divergente; mantenha a operação bloqueada e reconcilie pela mesma chave', 409, 'CONTRIBUTION_OUTCOME_UNKNOWN', error.requestId ?? responseRequestId);
      }
      throw error;
    }
  }

  async listReportSnapshots(clientId?: string) {
    if (this.edgeBaseUrl) {
      const path = clientId ? `/read-model/reports?clientId=${encodeURIComponent(clientId)}` : '/read-model/reports';
      const response = await this.request<Envelope<ReportSnapshot[]>>(this.edgeBaseUrl, path);
      if (!Array.isArray(response.data)) throw new RepositoryError('O serviço de relatórios retornou uma coleção inválida.', 502, 'REPORTS_RESPONSE_INVALID');
      const reports = response.data.map((report) => assertInput(reportSnapshotSchema, report, 'Snapshot de relatório retornado inválido.'));
      await verifyReportSnapshots(reports);
      return reports;
    }
    if (clientId) {
      const current = await this.rawClient(clientId);
      const planning = planningData(current.planning_data, clientId);
      const reports = reportsData(current.reports_data, clientId, new Set(planning.scenarios.map((scenario) => scenario.id))).reports;
      await verifyReportSnapshots(reports);
      const familyMembers = await this.listFamilyMembers(clientId);
      await verifyLegacyReportSnapshots(reports, current, clientId, familyMembers);
      return [...reports].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    }
    if (this.directusOnly) {
      const data = await this.listClientPages<Pick<RawClient, 'id' | 'reports_data'>>('id,reports_data');
      const reports = data.flatMap((row) => {
        return reportsData(row.reports_data, row.id).reports;
      });
      await verifyReportSnapshots(reports);
      return [...reports].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    }
    const data = await this.listClientPages<Pick<RawClient, 'id' | 'reports_data' | 'planning_data'>>('id,reports_data,planning_data');
    const reports = (await Promise.all(data.map(async (row) => {
      const current = await this.rawClient(row.id);
      const planning = planningData(current.planning_data, row.id);
      const parsed = reportsData(current.reports_data, row.id, new Set(planning.scenarios.map((scenario) => scenario.id))).reports;
      await verifyReportSnapshots(parsed);
      const familyMembers = await this.listFamilyMembers(row.id);
      await verifyLegacyReportSnapshots(parsed, current, row.id, familyMembers);
      return parsed;
    }))).flat()
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    return reports;
  }

  async listLegacyReportSnapshots(clientId?: string) {
    if (this.edgeBaseUrl) {
      const path = clientId ? `/read-model/reports?clientId=${encodeURIComponent(clientId)}&includeLegacy=true` : '/read-model/reports?includeLegacy=true';
      const response = await this.request<Envelope<ReportSnapshot[]> & { legacyReports?: unknown }>(this.edgeBaseUrl, path);
      if (!Array.isArray(response.data)) throw new RepositoryError('O serviço de relatórios não retornou a coleção canônica esperada.', 502, 'REPORTS_RESPONSE_INVALID');
      if (!Array.isArray(response.legacyReports)) throw new RepositoryError('O serviço de relatórios não retornou o arquivo histórico legado explicitamente.', 502, 'LEGACY_REPORTS_UNAVAILABLE');
      const reports = response.legacyReports.map((report) => assertInput(legacyReportSnapshotSchema, report, 'Snapshot legado de relatório retornado inválido.'));
      if (clientId && reports.some((report) => report.clientId !== clientId)) throw new RepositoryError('O arquivo histórico retornou cliente divergente.', 502, 'REPORTS_RESPONSE_INVALID');
      return reports;
    }
    if (clientId) {
      const current = await this.rawClient(clientId);
      return reportsData(current.reports_data, clientId).legacyReports.sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''));
    }
    const data = await this.listClientPages<Pick<RawClient, 'id' | 'reports_data'>>('id,reports_data');
    return data.flatMap((row) => reportsData(row.reports_data, row.id).legacyReports)
      .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''));
  }

  async createReportSnapshot(input: CreateReportSnapshotInput) {
    const validated = assertInput(createReportSnapshotSchema, input, 'Dados de relatório inválidos. Revise cenário, resumo e seções.');
    if (validated.sourceCalculationId && !validated.sourceRevisionFingerprint) throw new RepositoryError('Relatório-fonte exige fingerprint das revisões financeiras.', 422, 'REPORT_SOURCE_FINGERPRINT_REQUIRED');
    if (this.edgeBaseUrl && !validated.sourceCalculationId) throw new RepositoryError('Relatório publicado exige snapshot de cálculo persistido.', 422, 'REPORT_SOURCE_REQUIRED');
    if (this.edgeBaseUrl && validated.sourceCalculationId) {
      if (!this.sessionUserId) {
        const user = await this.currentUser();
        if (!user) throw new RepositoryError('Relatório publicado exige uma sessão autenticada.', 401, 'SESSION_EXPIRED');
      }
      if (validated.summary.monthlyGoalFunding === undefined || validated.summary.annualGoalFunding === undefined || validated.summary.oneTimeGoalFunding === undefined) {
        throw new RepositoryError('Relatório publicado exige o resumo de funding por periodicidade.', 422, 'REPORT_GOALS_SUMMARY_REQUIRED');
      }
    }
    const current = await this.rawClient(validated.clientId);
    const planning = planningData(current.planning_data, validated.clientId);
    const scenario = planning.scenarios.find((item) => item.id === validated.scenarioId);
    if (!scenario) throw new RepositoryError('O cenário do relatório não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
	    let authoritativeSummary = validated.summary;
	    const reports = reportsData(current.reports_data, validated.clientId, new Set(planning.scenarios.map((scenario) => scenario.id)));
	    await verifyReportSnapshots(reports.reports);
	    const normalizedSections: ReportSection[] | undefined = validated.sections?.map((section, position) => ({ ...section, position }));
	    validateReportGoalSummary(validated, planning);
    const followUp = await verifiedFollowUpData(current.follow_up_data, validated.clientId);
    const authoritativeContributions = this.edgeBaseUrl ? await this.listContributions(validated.clientId) : followUp.contributions;
    const familyMembers = await this.listFamilyMembers(validated.clientId);
    await this.assertFamilyExclusionTargets(planning, validated.clientId, familyMembers);
    const familyRevision = familyMembers.map((member) => ({ id: member.id, version: member.version }));
    const excludedFamilyIds = new Set(resolveExcludedTargetIds(planning.exclusions, null, 'family_member'));
    const protectionFamilyRevision = familyMembers.filter((member) => !excludedFamilyIds.has(member.id)).map((member) => ({ id: member.id, version: member.version }));
    const existing = reports.reports.find((report) => report.sourceCalculationId === validated.sourceCalculationId && report.clientId === validated.clientId && report.scenarioId === validated.scenarioId);
    if (existing) {
      if (validated.sourceCalculationId) await assertReportGuaranteeBindings(planning, validated.scenarioId);
      const sameReport = existing.sourceVersion === scenario.version
        && existing.kind === validated.kind
        && existing.title === validated.title.trim()
        && existing.sourceRevisionFingerprint === validated.sourceRevisionFingerprint
        && canonicalJson(existing.summary) === canonicalJson(validated.summary)
        && canonicalJson(existing.goalFunding ?? []) === canonicalJson(validated.goalFunding ?? [])
        && canonicalJson(existing.details) === canonicalJson(validated.details)
        && canonicalJson(existing.sections ?? []) === canonicalJson(normalizedSections ?? []);
      if (!sameReport) {
        if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
        if (existing.sourceVersion !== scenario.version) throw new RepositoryError('O cenário mudou depois do relatório-fonte; gere um novo relatório.', 409, 'REPORT_SOURCE_STALE');
        throw new RepositoryError('A chave do cálculo já foi usada com conteúdo de relatório diferente.', 409, 'REPORT_IDEMPOTENCY_CONFLICT');
      }
      const expectedFingerprint = await sha256(await reportSourceRevisionPayloadForClient(planning, followUp, validated.scenarioId, authoritativeContributions, familyRevision));
      if (existing.sourceRevisionFingerprint !== expectedFingerprint || validated.sourceRevisionFingerprint !== expectedFingerprint) throw new RepositoryError('As fontes financeiras mudaram ou o fingerprint não corresponde ao relatório existente.', 409, 'REPORT_SOURCE_FINGERPRINT_MISMATCH');
      const expectedDetails = reportDetailsForClient(planning, followUp, validated.scenarioId, authoritativeContributions, familyMembers);
      if (canonicalJson(existing.details) !== canonicalJson(expectedDetails) || canonicalJson(validated.details) !== canonicalJson(expectedDetails)) throw new RepositoryError('Detalhes do relatório não correspondem às fontes persistidas atuais.', 409, 'REPORT_SOURCE_DETAILS_STALE');
      const history = calculationHistory(current.calculation_history, validated.clientId);
      await verifyCalculationSnapshots(history.snapshots);
      await verifyCalculationHistoryIdempotency(history, validated.clientId);
      const source = history.snapshots.find((snapshot) => snapshot.id === validated.sourceCalculationId);
      if (!existing.canonical || !source || source.clientId !== validated.clientId || source.scenarioId !== validated.scenarioId || canonicalJson(source.input) !== canonicalJson(scenario.assumptions)) throw new RepositoryError('O relatório existente não possui replay autoritativo verificável.', 502, 'REPORT_REPLAY_INVALID');
      const expectedSummary = reportSummaryForClient(planning, followUp, validated.scenarioId, source.output, authoritativeContributions);
      for (const [key, value] of Object.entries(existing.summary)) {
        if (value !== undefined && value !== expectedSummary[key as keyof typeof expectedSummary]) throw new RepositoryError('Resumo do relatório existente não corresponde às fontes atuais.', 409, 'REPORT_SUMMARY_MISMATCH');
      }
      await verifyReportSnapshotHash(existing);
      return existing;
    }
    if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
    if (validated.sourceCalculationId) {
      await assertReportGuaranteeBindings(planning, validated.scenarioId);
      if (planning.protection && familyRevision) {
        const protectionGuarantees = effectiveGuaranteeExposures(planning.guaranteeExposures, planning.protection.guaranteesScenarioId, planning.exclusions);
        const currentProtectionFingerprint = await sha256(protectionRevisionPayload({ clientId: validated.clientId, family: protectionFamilyRevision, guaranteesMode: planning.protection.guaranteesMode, guaranteesScenarioId: planning.protection.guaranteesScenarioId, guaranteeExposures: protectionGuarantees, exclusions: planning.exclusions }));
        const legacyProtectionFingerprint = await sha256({ clientId: validated.clientId, family: sortedReportRevisionSources(familyRevision) });
        const acceptsLegacy = planning.exclusions.length === 0;
        if (!planning.protection.sourceRevisionFingerprint || (planning.protection.sourceRevisionFingerprint !== currentProtectionFingerprint && (!acceptsLegacy || planning.protection.sourceRevisionFingerprint !== legacyProtectionFingerprint))) throw new RepositoryError('O diagnóstico de proteção está desatualizado em relação à Família ou às desconsiderações.', 409, 'PROTECTION_FAMILY_STALE');
      }
      const expectedFingerprint = await sha256(await reportSourceRevisionPayloadForClient(planning, followUp, validated.scenarioId, authoritativeContributions, familyRevision));
	      if (validated.sourceRevisionFingerprint !== expectedFingerprint) throw new RepositoryError('As fontes financeiras mudaram ou o fingerprint não foi calculado pela autoridade atual.', 409, 'REPORT_SOURCE_FINGERPRINT_MISMATCH');
      const expectedDetails = reportDetailsForClient(planning, followUp, validated.scenarioId, authoritativeContributions, familyMembers);
	      if (!validated.details || canonicalJson(validated.details) !== canonicalJson(expectedDetails)) throw new RepositoryError('Os detalhes financeiros do relatório não correspondem às fontes persistidas atuais.', 409, 'REPORT_SOURCE_DETAILS_STALE');
	    }
    if (validated.sourceCalculationId) {
      const history = calculationHistory(current.calculation_history, validated.clientId);
      await verifyCalculationSnapshots(history.snapshots);
      await verifyCalculationHistoryIdempotency(history, validated.clientId);
      const source = history.snapshots.find((snapshot) => snapshot.id === validated.sourceCalculationId);
      if (!source || source.clientId !== validated.clientId || source.scenarioId !== validated.scenarioId) throw new RepositoryError('O cálculo-fonte do relatório não pertence ao cliente/cenário.', 422, 'CALCULATION_OWNERSHIP_INVALID');
      if (canonicalJson(source.input) !== canonicalJson(scenario.assumptions)) throw new RepositoryError('O cenário mudou depois do cálculo-fonte; gere um novo cálculo antes de publicar.', 409, 'REPORT_SOURCE_STALE');
      const expectedSummary = reportSummaryForClient(planning, followUp, validated.scenarioId, source.output, authoritativeContributions);
      for (const [key, value] of Object.entries(validated.summary)) {
        if (value !== undefined && value !== expectedSummary[key as keyof typeof expectedSummary]) throw new RepositoryError('Resumo do relatório não corresponde às fontes financeiras atuais.', 422, 'REPORT_SUMMARY_MISMATCH');
      }
      authoritativeSummary = expectedSummary;
    }
    if (!this.sessionUserId) {
      if (this.syntheticSession) this.sessionUserId = SYNTHETIC_ACTOR_ID;
      else {
        const user = await this.currentUser();
        if (!user) throw new RepositoryError('Relatório exige uma sessão autenticada para autoria.', 401, 'SESSION_EXPIRED');
      }
    }
    const createdAt = new Date().toISOString();
    const base = {
      id: crypto.randomUUID(), clientId: validated.clientId, scenarioId: validated.scenarioId,
      kind: validated.kind, title: validated.title.trim(), sourceVersion: scenario.version,
      summary: authoritativeSummary, createdAt, createdBy: this.sessionUserId!, canonical: true,
      sourceCalculationId: validated.sourceCalculationId,
      sourceRevisionFingerprint: validated.sourceRevisionFingerprint,
      ...(validated.goalFunding ? { goalFunding: validated.goalFunding } : {}),
      details: validated.details,
      ...(normalizedSections ? { sections: normalizedSections } : {}),
    } satisfies Omit<ReportSnapshot, 'sha256'>;
    const snapshot = assertInput(reportSnapshotSchema, { ...base, sha256: await sha256(base) }, 'Snapshot de relatório criado inválido.');
    const log = writableAuditLog(current.audit_log);
    await this.patchClientDocument(validated.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        reports_data: { version: reports.version + 1, reports: [...reports.reports, snapshot], legacyReports: reports.legacyReports },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('report.created', snapshot.id, ['reports_data'])] },
    });
    return snapshot;
  }

  async calculateRetirement(request: RetirementCalculationRequest) {
    const validatedRequest = assertInput(retirementCalculationRequestSchema, request, 'Request de cálculo inválido.');
    if (!this.edgeBaseUrl && !this.directusOnly) throw new RepositoryError('Cálculo publicado exige o gateway Edge.', 503, 'EDGE_API_REQUIRED');
    if (!this.edgeBaseUrl) {
      const canonicalKey = canonicalIdempotencyKey(validatedRequest.idempotencyKey);
      const current = await this.rawClient(validatedRequest.clientId);
      const planning = planningData(current.planning_data, validatedRequest.clientId);
      const scenario = planning.scenarios.find((item) => item.id === validatedRequest.scenarioId);
      if (!scenario) throw new RepositoryError('O cenário do cálculo não pertence ao cliente.', 422, 'SCENARIO_OWNERSHIP_INVALID');
      if (scenario.version !== validatedRequest.expectedScenarioVersion) throw new RepositoryError('O cenário foi alterado em outra sessão.', 409, 'VERSION_CONFLICT');
      if (scenario.assumptionsVersion !== validatedRequest.expectedAssumptionsVersion) throw new RepositoryError('As premissas foram alteradas em outra sessão.', 409, 'VERSION_CONFLICT');
      if (canonicalJson(scenario.assumptions) !== canonicalJson(validatedRequest.input)) throw new RepositoryError('As premissas calculadas não correspondem ao cenário persistido.', 409, 'CALCULATION_INPUT_STALE');
      const history = calculationHistory(current.calculation_history, validatedRequest.clientId);
      await verifyCalculationSnapshots(history.snapshots);
      await verifyCalculationHistoryIdempotency(history, validatedRequest.clientId);
      const requestHash = await sha256({ clientId: validatedRequest.clientId, scenarioId: validatedRequest.scenarioId, input: validatedRequest.input });
      const prior = history.idempotency.find((entry) => canonicalIdempotencyKey(entry.key) === canonicalKey);
      const linked = prior ? history.snapshots.find((snapshot) => snapshot.id === prior.snapshotId) : undefined;
      const existing = history.snapshots.find((snapshot) => canonicalIdempotencyKey(snapshot.idempotencyKey ?? '') === canonicalKey);
      if (prior) {
        if (prior.requestHash !== requestHash || !linked || linked.clientId !== validatedRequest.clientId || linked.scenarioId !== validatedRequest.scenarioId || canonicalJson(linked.input) !== canonicalJson(validatedRequest.input)) throw new RepositoryError('Idempotency-Key já usada com request diferente ou recibo ausente.', 409, 'CALCULATION_IDEMPOTENCY_CONFLICT');
        return linked;
      }
      if (existing) {
        if (existing.clientId !== validatedRequest.clientId || existing.scenarioId !== validatedRequest.scenarioId || canonicalJson(existing.input) !== canonicalJson(validatedRequest.input)) throw new RepositoryError('Idempotency-Key já usada com request diferente.', 409, 'CALCULATION_IDEMPOTENCY_CONFLICT');
        return existing;
      }
      if (this.directusOnly) this.assertDirectusMutationClientAllowed(current);
      if (!this.sessionUserId) {
        if (this.syntheticSession) this.sessionUserId = SYNTHETIC_ACTOR_ID;
        else {
          const user = await this.currentUser();
          if (!user) throw new RepositoryError('Cálculo exige uma sessão autenticada.', 401, 'SESSION_EXPIRED');
        }
      }
      const output = serializeRetirementOutput(validatedRequest.input);
      const base = {
        id: await deterministicUuid({ operation: 'calculation.retirement', clientId: validatedRequest.clientId, scenarioId: validatedRequest.scenarioId, key: canonicalKey }),
        clientId: validatedRequest.clientId, scenarioId: validatedRequest.scenarioId,
        calculationRunId: await deterministicUuid({ operation: 'calculation.run', clientId: validatedRequest.clientId, scenarioId: validatedRequest.scenarioId, key: canonicalKey }),
        idempotencyKey: canonicalKey, engineVersion: output.engineVersion, input: validatedRequest.input, output,
        createdAt: new Date().toISOString(), createdBy: this.sessionUserId!, canonical: false,
      } satisfies Omit<CalculationSnapshot, 'sha256'>;
      const snapshot = assertInput(calculationSnapshotSchema, { ...base, sha256: await sha256(calculationSnapshotHashPayload(base)) }, 'Snapshot de cálculo Directus inválido.');
      await verifyCalculationSnapshotDerivation(snapshot);
      const log = writableAuditLog(current.audit_log);
      const persisted = await this.patchClientDocument(validatedRequest.clientId, Number(current.version) + 1, {
        version: Number(current.version) + 1, data_model_version: DATA_MODEL_VERSION,
        calculation_history: { version: history.version + 1, snapshots: [...history.snapshots, snapshot], idempotency: [...history.idempotency, { key: canonicalKey, requestHash, snapshotId: snapshot.id }] },
        audit_log: { version: log.version + 1, entries: [...log.entries, this.makeAuditEntry('calculation.created', snapshot.id, ['calculation_history'])] },
      });
      if (persisted) {
        const persistedHistory = calculationHistory(persisted.calculation_history, validatedRequest.clientId);
        const persistedSnapshot = persistedHistory.snapshots.find((item) => item.id === snapshot.id);
        if (!persistedSnapshot) throw new RepositoryError('O Directus não confirmou o snapshot de cálculo.', 409, 'CALCULATION_NOT_PERSISTED');
        await verifyCalculationSnapshots([persistedSnapshot]);
        return persistedSnapshot;
      }
      return snapshot;
    }
    const result = await this.request<Envelope<CalculationSnapshot> & { persisted?: boolean }>(
      this.edgeBaseUrl,
      '/calculations/retirement',
      { method: 'POST', headers: { 'Idempotency-Key': validatedRequest.idempotencyKey }, body: JSON.stringify(validatedRequest) },
    );
    const upstreamSnapshot = assertInput(calculationSnapshotSchema, result.data, 'Snapshot de cálculo retornado pelo Worker inválido.');
    await verifyCalculationSnapshotHash(upstreamSnapshot);
    if (upstreamSnapshot.clientId !== validatedRequest.clientId || upstreamSnapshot.scenarioId !== validatedRequest.scenarioId || canonicalIdempotencyKey(upstreamSnapshot.idempotencyKey ?? '') !== canonicalIdempotencyKey(validatedRequest.idempotencyKey) || canonicalJson(upstreamSnapshot.input) !== canonicalJson(validatedRequest.input)) throw new RepositoryError('Snapshot de cálculo não corresponde ao request', 502, 'CALCULATION_RESPONSE_MISMATCH');
    if (result.persisted !== true) throw new RepositoryError('O Worker não confirmou persistência autoritativa do cálculo.', 503, 'CALCULATION_NOT_PERSISTED');
    if (upstreamSnapshot.canonical !== true) throw new RepositoryError('O Worker retornou um cálculo não canônico para uma operação publicada.', 503, 'CALCULATION_NOT_CANONICAL');
    await verifyCalculationSnapshotDerivation(upstreamSnapshot);
    return upstreamSnapshot;
  }

  async listCalculationSnapshots(clientId?: string) {
    if (this.edgeBaseUrl) {
      const path = clientId ? `/read-model/calculations?clientId=${encodeURIComponent(clientId)}` : '/read-model/calculations';
      const response = await this.request<Envelope<unknown>>(this.edgeBaseUrl, path);
      if (!Array.isArray(response.data)) throw new RepositoryError('Resposta de cálculos inválida.', 502, 'CALCULATIONS_RESPONSE_INVALID');
      const snapshots = response.data.map((item) => assertInput(calculationSnapshotSchema, item, 'Snapshot de cálculo retornado pelo Worker inválido.'));
      await verifyCalculationSnapshots(snapshots);
      return snapshots;
    }
    if (clientId) {
      const current = await this.rawClient(clientId);
      const history = calculationHistory(current.calculation_history, clientId);
      await verifyCalculationSnapshots(history.snapshots);
      await verifyCalculationHistoryIdempotency(history, clientId);
      return [...history.snapshots].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    }
    const data = await this.listClientPages<Pick<RawClient, 'id' | 'calculation_history'>>('id,calculation_history');
    const snapshots = (await Promise.all(data.map(async (row) => {
      const history = calculationHistory(row.calculation_history, row.id);
      await verifyCalculationSnapshots(history.snapshots);
      await verifyCalculationHistoryIdempotency(history, row.id);
      return history.snapshots;
    }))).flat()
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    return snapshots;
  }
}

export interface PocketBaseRepositoryOptions {
  /** BFF wire alias; it is never a PocketBase domain or collection URL. */
  baseUrl?: string;
  /** Optional absolute application origin for explicit server-side tests. */
  serverOrigin?: string;
  /**
   * Legacy factory alias retained for source compatibility. PocketBase is
   * edge-free; this value is intentionally ignored and must not become a
   * transport base. Authentication uses fileGatewayBaseUrl (/api).
   */
  edgeBaseUrl?: string;
  fileGatewayBaseUrl?: string;
  contributionGatewayBaseUrl?: string;
  /** Logical Fintracks file namespace, not a native PocketBase folder. */
  filesNamespaceId: string;
  directusOnly?: boolean;
  casWrites?: boolean;
  fetchImpl?: typeof fetch;
}

function pocketBaseCoreOptions(options: PocketBaseRepositoryOptions): DirectusRepositoryOptions {
  const origin = pocketBaseOrigin(options.serverOrigin);
  const absoluteOutput = Boolean(options.serverOrigin?.trim());
  const filesNamespaceId = assertPocketBaseNamespace(options.filesNamespaceId);
  if (options.directusOnly === false) throw new Error('O adapter PocketBase exige directusOnly=true para preservar os gateways privados.');
  if (options.edgeBaseUrl !== undefined) pocketBaseEndpoint(options.edgeBaseUrl, POCKETBASE_API_PATH, origin, absoluteOutput);
  const core: DirectusRepositoryOptions = {
    provider: 'pocketbase',
    baseUrl: pocketBaseEndpoint(options.baseUrl, POCKETBASE_WIRE_PATH, origin, absoluteOutput),
    edgeBaseUrl: '',
    fileGatewayBaseUrl: pocketBaseEndpoint(options.fileGatewayBaseUrl, POCKETBASE_API_PATH, origin, absoluteOutput),
    contributionGatewayBaseUrl: pocketBaseEndpoint(options.contributionGatewayBaseUrl, POCKETBASE_API_PATH, origin, absoluteOutput),
    directusOnly: true,
    filesFolderId: filesNamespaceId,
    casWrites: options.casWrites ?? true,
  };
  if (options.fetchImpl !== undefined) core.fetchImpl = options.fetchImpl;
  if (origin !== undefined) core.serverOrigin = origin;
  return core;
}

/**
 * PocketBase provider facade. The domain implementation, validation, money
 * handling, hashes, idempotency and envelopes remain in DirectusRepository;
 * only the bounded same-origin wire bases differ.
 */
export class PocketBaseRepository extends DirectusRepository {
  constructor(options: PocketBaseRepositoryOptions) {
    super(pocketBaseCoreOptions(options));
  }
}
